// bluaIQ — M9: Contracts Module
// Contract management with draw schedules

import { apiFetch } from '../utils/api'
import { useState, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, Plus, DollarSign, Download, ChevronDown, ChevronUp, Send, Upload, Loader2, AlertTriangle, Eye, Pencil, X, CheckCircle, Info } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import SignaturePad from './SignaturePad'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'
import ContactLinker, { type LinkedContact } from './ContactLinker'
import { formatMoneyValue, formatMoney } from '../utils/money'
import { isAnomalousContractAmount } from '../services/financialTrustGuards'
import { deriveProposedSchedule } from '../utils/paymentSchedulePreview'
import { downloadPdfBlobSmart } from '../utils/sheetExport'

// ── TYPES ─────────────────────────────────────────

interface DrawItem {
  pct: number
  condition: string
  // Optional Spanish translation of `condition`. Demo-only: present
  // on DEMO_CONTRACTS rows so the draw schedule renders in Spanish
  // when the user switches to ES. Real user-saved DrawItem rows
  // from /api/projects/:id/contracts will not have this field, and
  // the renderer falls back to `condition` so user-entered text is
  // shown verbatim (never translated by bluaIQ).
  conditionEs?: string
  dueDate?: string
}

interface Contract {
  id: string
  contractorName: string
  trade: string
  // Optional Spanish translation of `trade`. Demo-only — see the
  // companion note on DrawItem.conditionEs. Falls back to `trade`
  // when missing so real user-entered values are never altered.
  tradeEs?: string
  totalAmount: number
  status: string
  drawSchedule?: DrawItem[]
  pdfPath?: string
  scopeOfWork?: string | null
  penalties?: string | null
  signerName?: string
  signedAt?: string
  // Phase 2 — builder/owner countersign.
  builderSignerName?: string | null
  builderSignatureUrl?: string | null
  builderSignedAt?: string | null
  createdAt: string
  // Contacts Linking Foundation — durable contractor contact link + the last
  // address a signing request was sent to (audit-of-record).
  contractorContactId?: string | null
  contractorContact?: LinkedContact | null
  recipientEmail?: string | null
  // Contracts Provenance / Source-Bid Prep — read-only source/provenance of the
  // contract. All optional; manual contracts leave them undefined. Nothing in
  // this UI creates a contract from a bid — these are display-only.
  sourceBidId?: string | null
  sourceType?: 'MANUAL' | 'CONTRACTOR_BID' | 'CONTRACT_REVIEW' | null
  sourceFilename?: string | null
  sourceNotes?: string | null
  sourceBid?: {
    id: string
    contractorName: string
    trade: string
    totalAmount: number
    originalFilename?: string | null
    decisionStatus?: string | null
  } | null
  // Free-text payment terms (server field). Used read-only by the NBA-10/11/12A
  // panels; coerced defensively at the read site so an object never renders.
  paymentTerms?: string | null
  // Real scheduled payments for this contract (NBA-12A counts these as
  // "scheduled" — distinct from proposed draft lines, which are never payments).
  payments?: { id: string; status?: string | null }[]
  // NBA-12A: the single ACTIVE draft payment schedule (isolated, non-binding).
  // Proposed lines are NOT real payments and affect no downstream module.
  paymentScheduleDrafts?: PaymentScheduleDraft[]
}

// NBA-12A — a draft payment schedule (header + proposed lines). Fully isolated
// from real Payment rows: these are non-binding planning rows a human reviews,
// never counted as scheduled/due/paid and affecting no downstream module.
interface PaymentScheduleDraftLine {
  id: string
  milestoneLabel: string
  percent?: number | null
  amount?: number | null
  // PR-F4G: additive exact-money field emitted by the server alongside the
  // legacy `amount`. Optional — older payloads without it keep working.
  amountMoney?: { cents: number; amount: string; currency: string } | null
  currency: string
  dueTrigger?: 'deposit_start' | 'completion' | string | null
  dueDate?: string | null
  notes?: string | null
  status: string
  sortOrder: number
}

// PR-F4G: draft-line amount reader — prefers the server-normalized exact
// `amountMoney` (cents → dollars) and falls back to the legacy number. Returns
// null for invalid/missing, which every consumer renders as "—", NEVER $0.
function draftLineAmount(l: {
  amount?: number | null
  amountMoney?: { cents: number; amount: string; currency: string } | null
}): number | null {
  // Codex polish: trust amountMoney only when cents is a SAFE integer and the
  // currency is the supported USD — anything else falls back to the legacy field.
  if (l.amountMoney && Number.isSafeInteger(l.amountMoney.cents) && l.amountMoney.currency === 'USD') return l.amountMoney.cents / 100
  return typeof l.amount === 'number' && Number.isFinite(l.amount) ? l.amount : null
}

// NOTE (UT-PAGOS-1-R): the internal-pilot markers/gate that let a draft-schedule
// panel show WITHOUT builder signature were removed here too. A payment-schedule
// draft (and its UI) requires a fully executed contract — no pilot escape.

interface PaymentScheduleDraft {
  id: string
  status: string
  previewHash?: string
  createdAt?: string
  lines: PaymentScheduleDraftLine[]
}

// ── NBA-13F: timezone-safe date-only extraction for <input type="date"> ──────
// A persisted draft-line dueDate is returned by the server as a full ISO
// datetime (e.g. "2026-08-15T00:00:00.000Z"); a <input type="date"> needs the
// exact "YYYY-MM-DD" prefix. This pulls that prefix from either an ISO datetime
// OR a bare date-only string and returns '' for null/blank/non-date values. It
// NEVER constructs a Date, so it can never shift the day across a UTC/local
// timezone boundary (the classic "saved 2026-08-15, shows 2026-08-14" bug).
function toDateInputValue(v: unknown): string {
  if (typeof v !== 'string') return ''
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(v.trim())
  return m ? m[1] : ''
}

// ── NBA-4 source notes localization ─────────────────────────────────────────
// NBA-4 stores sourceNotes in English as the canonical form. Records created
// before this fix may have been stored in Spanish. Detect either standard form
// and render the correct localized copy. Arbitrary user-entered notes (not
// matching either standard pattern) are returned as-is — no silent translation.
const NBA4_REVIEW_EN = 'Review required before sending.'
const NBA4_REVIEW_ES = 'Revisión requerida antes de enviar.'
const NBA4_REVIEW_VARIANTS = new Set([NBA4_REVIEW_EN, NBA4_REVIEW_ES])

function localizeSourceNotes(notes: string, isEs: boolean): string {
  const localized = isEs ? NBA4_REVIEW_ES : NBA4_REVIEW_EN
  if (NBA4_REVIEW_VARIANTS.has(notes)) return localized
  for (const r of [NBA4_REVIEW_EN, NBA4_REVIEW_ES]) {
    const suffix = ` — ${r}`
    if (notes.endsWith(suffix)) return `${notes.slice(0, notes.length - suffix.length)} — ${localized}`
  }
  return notes
}

const DECISION_STATUS_LABELS: Record<string, { en: string; es: string }> = {
  SELECTED:     { en: 'Selected',     es: 'Seleccionada' },
  REJECTED:     { en: 'Rejected',     es: 'Rechazada' },
  PENDING:      { en: 'Pending',      es: 'Pendiente' },
  NOT_SELECTED: { en: 'Not selected', es: 'No seleccionada' },
  RECEIVED:     { en: 'Received',     es: 'Recibida' },
}
function localizeDecisionStatus(status: string | null | undefined, isEs: boolean): string {
  if (!status) return ''
  const entry = DECISION_STATUS_LABELS[status]
  return entry ? (isEs ? entry.es : entry.en) : status
}

// Same regex used by ContactsModule for email validation.
function isValidRecipientEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
}

// ── NBA-5: Review Before Send ───────────────────
// Read-only review/checklist layer shown on DRAFT contracts before the builder
// sends for signature. This is GUIDANCE ONLY — no durable checklist state (no
// schema), no send/sign rework, no auto-translation, no downstream writes. It
// reuses signals already present on the contract (provenance, linked contact,
// clause-language coherence) to point the builder at what to review first.
const NBA5_COPY = {
  title: { en: 'Review before sending', es: 'Revisar antes de enviar' },
  createdFromSelectedBid: {
    en: 'This draft was created from a selected bid.',
    es: 'Este borrador fue creado desde una propuesta seleccionada.',
  },
  reviewIntro: {
    en: 'Review source terms, missing information, language, and contact before sending.',
    es: 'Revisa términos fuente, información faltante, idioma y contacto antes de enviar.',
  },
  noDownstream: {
    en: 'This does not create payments, update Budget, or create Calendar/Gantt events.',
    es: 'Esto no crea pagos, no actualiza el presupuesto ni crea eventos en Calendario/Gantt.',
  },
  sendManual: {
    en: 'Send for Signature remains manual.',
    es: 'Enviar para firma sigue siendo manual.',
  },
  // Checklist (informational/read-only — not persisted).
  checklist: {
    sourceBid: { en: 'Source bid reviewed', es: 'Propuesta fuente revisada' },
    contact: { en: 'Contractor contact reviewed', es: 'Contacto del contratista revisado' },
    language: { en: 'Contract language reviewed', es: 'Idioma del contrato revisado' },
    scope: { en: 'Scope and terms reviewed', es: 'Alcance y términos revisados' },
    pdf: { en: 'PDF preview reviewed', es: 'Vista previa del PDF revisada' },
    ready: { en: 'Ready to send manually', es: 'Listo para enviar manualmente' },
  },
  sourceBidHeading: { en: 'Source bid', es: 'Propuesta fuente' },
  selectedBidStatus: { en: 'Selected bid status', es: 'Estado de la propuesta seleccionada' },
  // Contact review.
  contactLinked: { en: 'Contractor contact linked', es: 'Contacto del contratista enlazado' },
  contactNotLinked: {
    en: 'Contractor contact is not linked. Link a contact before sending.',
    es: 'El contacto del contratista no está enlazado. Enlaza un contacto antes de enviar.',
  },
  contactNoEmail: {
    en: 'The linked contact has no email. Add an email before sending.',
    es: 'El contacto enlazado no tiene correo. Agrega un correo antes de enviar.',
  },
  // Language review.
  languageNeedsReview: {
    en: 'Contract language needs review before sending.',
    es: 'El idioma del contrato requiere revisión antes de enviar.',
  },
  languageReady: {
    en: 'Contract language appears ready for review.',
    es: 'El idioma del contrato parece listo para revisión.',
  },
  // Source / missing-info review.
  sourceMayNeedReview: {
    en: 'Some source information may need review before sending.',
    es: 'Parte de la información fuente puede requerir revisión antes de enviar.',
  },
  sourceReviewNeutral: {
    en: 'Review all source terms before sending.',
    es: 'Revisa todos los términos fuente antes de enviar.',
  },
  // Send-for-signature guard copy (does NOT disable the button).
  sendGuard: {
    en: 'Send for Signature is manual and should only be used after review.',
    es: 'Enviar para firma es manual y debe usarse solo después de revisar.',
  },
  // PDF review reminder.
  pdfReminder: {
    en: 'Preview the PDF before sending.',
    es: 'Previsualiza el PDF antes de enviar.',
  },
} as const

type DraftContactState = 'linked_ok' | 'linked_no_email' | 'not_linked'
type DraftLanguageState = 'needs_review' | 'ready'

// Pure, read-only derivation of the review signals for a DRAFT contract. No
// writes. `linkedEmail` is the linked contractor contact's email (or null when
// no contact is linked); `coherence` is the clause-language coherence already
// computed for the send gate. `sourceNotes` carries the NBA-4 review marker
// plus any builder decision notes — when notes beyond the standard marker are
// present we surface the stronger "may need review" copy, otherwise neutral.
function deriveDraftReview(args: {
  hasSource: boolean
  hasLinkedContact: boolean
  linkedEmail: string | null | undefined
  coherence: 'en' | 'es' | 'mixed' | 'empty'
  sourceNotes: string | null | undefined
}): {
  contactState: DraftContactState
  languageState: DraftLanguageState
  sourceInfoNeedsReview: boolean
} {
  const contactState: DraftContactState = !args.hasLinkedContact
    ? 'not_linked'
    : (args.linkedEmail && args.linkedEmail.trim() ? 'linked_ok' : 'linked_no_email')
  const languageState: DraftLanguageState = args.coherence === 'mixed' ? 'needs_review' : 'ready'
  // Signal = the builder attached decision notes beyond the standard review
  // marker (those notes are worth a closer look). With only the bare marker (or
  // no notes) there is no concrete signal → neutral copy.
  let sourceInfoNeedsReview = false
  const notes = (args.sourceNotes || '').trim()
  if (notes && args.hasSource) {
    const isBareMarker = NBA4_REVIEW_VARIANTS.has(notes)
    const endsWithMarker = [...NBA4_REVIEW_VARIANTS].some(m => notes.endsWith(` — ${m}`))
    // Notes that are exactly the marker → no extra signal. Notes that END with
    // the marker (i.e. "<decision notes> — <marker>") → builder added content.
    sourceInfoNeedsReview = !isBareMarker && endsWithMarker
  }
  return { contactState, languageState, sourceInfoNeedsReview }
}

// ── NBA-6: Send for Signature Readiness Guard ───────────────────
// A FE-only confirmation layer shown ONLY after the builder explicitly clicks
// "Send for Signature" on a DRAFT contract. It does NOT auto-open and does NOT
// auto-send: the existing POST /send-for-signature handler still runs only from
// the explicit final-confirm button below. The guard surfaces what the send
// actually does (signature request only — no payments/Budget/Calendar-Gantt/
// Contacts/PCS/QuickBooks/Takeoff) and what to review first (recipient, linked
// vs. manual email, final language, PDF preview). It keeps the existing email
// prefill + manual-override behaviour and the existing no-email / mixed-language
// send blocks. No schema, no migration, no backend change, no durable state.
const NBA6_COPY = {
  title: { en: 'Review before sending for signature', es: 'Revisar antes de enviar para firma' },
  recipientEmail: { en: 'Recipient email', es: 'Correo del destinatario' },
  manualRecipientEmail: { en: 'Manual recipient email', es: 'Correo manual del destinatario' },
  linkedContact: { en: 'Linked contractor contact', es: 'Contacto del contratista enlazado' },
  noLinkedContact: { en: 'No linked contractor contact', es: 'Sin contacto del contratista enlazado' },
  notLinkedWarning: {
    en: 'This email is not linked to a contractor contact.',
    es: 'Este correo no está enlazado a un contacto del contratista.',
  },
  langEn: { en: 'The contract will be sent in English.', es: 'El contrato se enviará en inglés.' },
  langEs: { en: 'The contract will be sent in Spanish.', es: 'El contrato se enviará en español.' },
  pdfReminder: { en: 'Preview the PDF before sending.', es: 'Previsualiza el PDF antes de enviar.' },
  sourceReminder: {
    en: 'Review the source bid and contract terms before sending.',
    es: 'Revisa la propuesta fuente y los términos del contrato antes de enviar.',
  },
  signatureOnly: {
    en: 'This sends the contract for signature only.',
    es: 'Esto solo envía el contrato para firma.',
  },
  noDownstream: {
    en: 'This does not create payments, update Budget, create Calendar/Gantt events, update contacts, or sync QuickBooks.',
    es: 'Esto no crea pagos, no actualiza el presupuesto, no crea eventos en Calendario/Gantt, no actualiza contactos ni sincroniza QuickBooks.',
  },
  finalSend: { en: 'Send contract for signature', es: 'Enviar contrato para firma' },
  confirmAndSend: { en: 'Confirm and send', es: 'Confirmar y enviar' },
  cancel: { en: 'Cancel', es: 'Cancelar' },
  invalidEmail: {
    en: 'Enter a valid recipient email before sending.',
    es: 'Ingresa un correo válido del destinatario antes de enviar.',
  },
} as const

// ── NBA-8: Executed Contract Readiness / Post-Signature Summary ──────────────
// READ-ONLY summary shown ONLY on completed/executed contracts (both parties
// signed → builderSignedAt present). It surfaces, from data already on the
// contract row, the final state (status, signatures, amount, final PDF access)
// and — critically — makes the downstream-controls boundary explicit: this
// signature workflow does NOT schedule payments, update Budget, create
// Calendar/Gantt events, sync QuickBooks, create PCS records, connect Takeoff,
// or enable AI Project Chat actions. It NEVER mutates anything: no fetch to a
// write endpoint, no schema, no migration, no lifecycle/signature change.
//
// Wording discipline (audit finding): downstream modules may already hold
// manual or unrelated data, so the indicators say "not …by this workflow"
// rather than asserting a module is empty. Because completed-contract totals
// can already be read by Dashboard / AIA previews, the panel always warns that
// downstream project controls still require review, and shows a stronger
// amount/source warning when the contract total is unavailable or suspicious.

// ── PR-F3A: Anomalous contract amount read guards ───────────────────────────
// Shown on any contract card where the amount is anomalous (not-finite, ≤ 0,
// ≥ $1M, or > 3× project budget). The amount remains visible; these banners
// explain why it is excluded from financial totals and what to do.
const PRF3A_COPY = {
  excludedFromTotals: {
    en: 'Excluded from financial totals until the source is reviewed.',
    es: 'Excluido de los totales financieros hasta revisar la fuente.',
  },
  unusuallyHigh: {
    en: 'This amount looks unusually high for the current project budget.',
    es: 'Este monto parece inusualmente alto para el presupuesto actual del proyecto.',
  },
  verifyBeforeUse: {
    en: 'Verify contract source before using this amount for AIA billing, payments, or forecasts.',
    es: 'Revise la fuente del contrato antes de usar este monto en AIA Billing, pagos o proyecciones.',
  },
} as const

const NBA8_COPY = {
  title: { en: 'Executed contract summary', es: 'Resumen del contrato ejecutado' },
  coreCompleted: {
    en: 'This contract is completed and signed by both parties.',
    es: 'Este contrato está completado y firmado por ambas partes.',
  },
  coreReview: {
    en: 'Review this contract before creating downstream project controls.',
    es: 'Revisa este contrato antes de crear controles posteriores del proyecto.',
  },
  // Section labels.
  statusLabel: { en: 'Final status', es: 'Estado final' },
  contractorLabel: { en: 'Contractor signature', es: 'Firma del contratista' },
  builderLabel: { en: 'Builder signature', es: 'Firma del builder' },
  amountLabel: { en: 'Final amount', es: 'Monto final' },
  readinessHeading: { en: 'Downstream readiness', es: 'Estado de controles posteriores' },
  // Downstream readiness indicators — "by this workflow" wording (audit-safe:
  // never claims a module is empty, only that THIS workflow did not write it).
  paymentsNot: {
    en: 'Payments have not been scheduled by this workflow.',
    es: 'Los pagos no han sido programados por este flujo.',
  },
  budgetNot: {
    en: 'Budget has not been updated by this workflow.',
    es: 'El presupuesto no ha sido actualizado por este flujo.',
  },
  calendarNot: {
    en: 'Calendar/Gantt events have not been created by this workflow.',
    es: 'No se han creado eventos en Calendario/Gantt por este flujo.',
  },
  quickbooksNot: {
    en: 'QuickBooks has not been synced.',
    es: 'QuickBooks no ha sido sincronizado.',
  },
  pcsNot: {
    en: 'Project Cost Sources have not been created by this workflow.',
    es: 'No se han creado Fuentes de Costo del Proyecto por este flujo.',
  },
  takeoffNot: {
    en: 'Takeoff is not connected by this workflow.',
    es: 'El Takeoff no está conectado por este flujo.',
  },
  aiChatNot: {
    en: 'AI Project Chat actions are not enabled by this workflow.',
    es: 'Las acciones de Chat de Proyecto con IA no están habilitadas por este flujo.',
  },
  // Always-on warning (audit finding: dashboards/AIA may already read totals).
  dashboardReadsWarning: {
    en: 'Some dashboards or previews may read contract totals, but downstream project controls still require review.',
    es: 'Algunos dashboards o vistas previas pueden leer totales del contrato, pero los controles posteriores del proyecto aún requieren revisión.',
  },
  // Stronger warning, shown only when the amount is unavailable or suspicious.
  amountSourceWarning: {
    en: 'Review contract amount and source before using it for Budget, Payments, AIA, or Calendar/Gantt.',
    es: 'Revisa el monto y la fuente del contrato antes de usarlo para Presupuesto, Pagos, AIA o Calendario/Gantt.',
  },
} as const

// ── NBA-9: Payment Schedule Readiness ───────────────────────────────────────
// A READ-ONLY readiness layer shown ONLY on completed/executed contracts (both
// parties signed). It explains that a completed contract is *ready for* payment
// schedule review — WITHOUT creating any payment. It never copies suspicious or
// free-text payment terms into actual payment rows; it informs readiness only.
//   • Gated identically to NBA-8 (`!isDemo && isFullyExecuted`)
//   • Renders existing contract-row data only (paymentTerms text, drawSchedule
//     status, amount, contractor/trade) — no fetch, no mutation, no write
//   • "…by this workflow" / "por este flujo" wording (never "no payments" / "Budget is empty")
//   • Surfaces that the structured schedule is missing and/or that terms are
//     free-text and require review before any payment is scheduled
//   • Stronger amount/source warning reuses the per-contract formatMoney gate
const NBA9_COPY = {
  title: { en: 'Payment schedule readiness', es: 'Preparación del calendario de pagos' },
  coreReady: {
    en: 'This completed contract is ready for payment schedule review.',
    es: 'Este contrato completado está listo para revisión del calendario de pagos.',
  },
  noPaymentsCreated: {
    en: 'No payments have been created by this workflow.',
    es: 'No se han creado pagos por este flujo.',
  },
  reviewBeforeCreating: {
    en: 'Review payment terms and source before creating scheduled payments.',
    es: 'Revisa los términos de pago y la fuente antes de crear pagos programados.',
  },
  downstreamCombined: {
    en: 'Budget, Calendar/Gantt, and QuickBooks have not been updated by this workflow.',
    es: 'Presupuesto, Calendario/Gantt y QuickBooks no han sido actualizados por este flujo.',
  },
  createOnlyAfterReview: {
    en: 'Create payments only after explicit review and confirmation.',
    es: 'Crea pagos solo después de revisión y confirmación explícita.',
  },
  // Readiness field labels.
  contractorLabel: { en: 'Contractor', es: 'Contratista' },
  tradeLabel: { en: 'Trade', es: 'Oficio' },
  amountLabel: { en: 'Final amount', es: 'Monto final' },
  paymentTermsLabel: { en: 'Payment terms', es: 'Términos de pago' },
  scheduleStatusLabel: { en: 'Draw / payment schedule', es: 'Calendario de avances / pagos' },
  // Schedule-status values (display-only; never creates a schedule).
  structuredScheduleMissing: {
    en: 'Structured schedule missing',
    es: 'Calendario estructurado ausente',
  },
  freeTextTermsPresent: {
    en: 'Free-text terms require review',
    es: 'Términos en texto requieren revisión',
  },
  noScheduledPayments: {
    en: 'No scheduled payments created by this workflow',
    es: 'No hay pagos programados creados por este flujo',
  },
  // Warning when payment terms are free-text only (no structured rows).
  freeTextTermsWarning: {
    en: 'Payment terms are text-based and require review before scheduling.',
    es: 'Los términos de pago están en texto y requieren revisión antes de programar pagos.',
  },
  // Stronger warning when the amount is unavailable or suspicious.
  amountSourceWarning: {
    en: 'Review contract amount and source before creating payments.',
    es: 'Revisa el monto y la fuente del contrato antes de crear pagos.',
  },
  // Individual downstream lines (audit-safe "by this workflow" wording).
  budgetNot: {
    en: 'Budget has not been updated by this workflow.',
    es: 'El presupuesto no ha sido actualizado por este flujo.',
  },
  calendarNot: {
    en: 'Calendar/Gantt events have not been created by this workflow.',
    es: 'No se han creado eventos en Calendario/Gantt por este flujo.',
  },
  quickbooksNot: {
    en: 'QuickBooks has not been synced.',
    es: 'QuickBooks no ha sido sincronizado.',
  },
} as const

// NBA-10 — Proposed Payment Schedule Preview.
//
// A READ-ONLY preview of proposed milestone rows derived (conservatively) from the
// completed contract's free-text payment terms. It NEVER creates a payment, never
// infers a date, never touches Budget/Calendar-Gantt/QuickBooks. Rows appear only
// when the parser (src/utils/paymentSchedulePreview.ts) is confident; otherwise the
// panel shows a "review manually" warning. Warnings stay visible even when rows are
// shown, because the parse is a convenience, not authority.
const NBA10_COPY = {
  title: { en: 'Proposed payment schedule preview', es: 'Vista previa del calendario de pagos propuesto' },
  previewOnly: {
    en: 'This is a preview only. No payments have been created.',
    es: 'Esto es solo una vista previa. No se han creado pagos.',
  },
  reviewBeforeRows: {
    en: 'Review payment terms, amounts, and dates before creating payment rows.',
    es: 'Revisa términos de pago, montos y fechas antes de crear pagos.',
  },
  dueDatesRequired: {
    en: 'Due dates are required before scheduling payments.',
    es: 'Las fechas de vencimiento son requeridas antes de programar pagos.',
  },
  downstreamNot: {
    en: 'Budget, Calendar/Gantt, and QuickBooks are not updated by this preview.',
    es: 'Presupuesto, Calendario/Gantt y QuickBooks no se actualizan con esta vista previa.',
  },
  createOnlyAfterConfirm: {
    en: 'Create payment rows only after explicit confirmation.',
    es: 'Crea pagos solo después de confirmación explícita.',
  },
  // Table column labels.
  milestoneLabel: { en: 'Proposed milestone', es: 'Hito propuesto' },
  percentLabel: { en: 'Percent', es: 'Porcentaje' },
  amountLabel: { en: 'Proposed amount', es: 'Monto propuesto' },
  dueTriggerLabel: { en: 'Due trigger', es: 'Condición de vencimiento' },
  dueDateMissing: { en: 'Due date missing', es: 'Fecha de vencimiento faltante' },
  // Warnings.
  freeTextWarning: {
    en: 'Payment terms are free text and require review.',
    es: 'Los términos de pago están en texto libre y requieren revisión.',
  },
  ambiguousWarning: {
    en: 'Payment terms are ambiguous. Review manually before creating payment rows.',
    es: 'Los términos de pago son ambiguos. Revísalos manualmente antes de crear pagos.',
  },
  sumWarning: {
    en: 'Percentages do not sum to 100%. Review before creating payment rows.',
    es: 'Los porcentajes no suman 100%. Revisa antes de crear pagos.',
  },
  amountInvalidWarning: {
    en: 'Cannot preview payment amounts because the contract amount is invalid.',
    es: 'No se pueden previsualizar montos de pago porque el monto del contrato es inválido.',
  },
  amountSourceWarning: {
    en: 'Review contract amount and source before creating payments.',
    es: 'Revisa el monto y la fuente del contrato antes de crear pagos.',
  },
  // Milestone display labels (Deposit/Start/Completion/Final possible; the
  // conservative parser emits Deposit + Completion).
  milestoneDeposit: { en: 'Deposit', es: 'Depósito' },
  milestoneStart: { en: 'Start', es: 'Inicio' },
  milestoneCompletion: { en: 'Completion', es: 'Terminación' },
  milestoneFinal: { en: 'Final payment', es: 'Pago final' },
  // Due-trigger display values.
  triggerDepositStart: { en: 'Deposit / start', es: 'Depósito / inicio' },
  triggerCompletion: { en: 'Completion', es: 'Terminación' },
} as const

// NBA-11 — Payment Row Creation Readiness / Explicit Create Payments Guard.
//
// A READ-ONLY guard shown below NBA-10 on completed/executed contracts. It does
// NOT create payments. It surfaces the blockers that must be resolved before
// real payment rows could ever be created, and exposes ONLY a DISABLED button
// (no endpoint, no fetch, no mutation, no navigation). The panel is ALWAYS
// blocked because the Payment model has no DRAFT/PROPOSED state yet — there is
// no safe way to persist a non-binding (proposed) payment row, so creating a
// PENDING row would risk being read as a real obligation.
const NBA11_COPY = {
  title: { en: 'Create payment rows', es: 'Crear pagos' },
  reviewConfirm: {
    en: 'Review and confirm before creating payments.',
    es: 'Revisa y confirma antes de crear pagos.',
  },
  onlyAfterConfirm: {
    en: 'Payment rows will be created only after explicit confirmation.',
    es: 'Los pagos se crearán solo después de confirmación explícita.',
  },
  dueDatesRequired: {
    en: 'Due dates are required before payments can be scheduled.',
    es: 'Las fechas de vencimiento son requeridas antes de programar pagos.',
  },
  downstreamNot: {
    en: 'Budget, Calendar/Gantt, and QuickBooks will not be updated by this action.',
    es: 'Presupuesto, Calendario/Gantt y QuickBooks no serán actualizados por esta acción.',
  },
  notMarkPaid: {
    en: 'This action will not mark payments as paid.',
    es: 'Esta acción no marcará pagos como pagados.',
  },
  notSyncQb: {
    en: 'This action will not sync QuickBooks.',
    es: 'Esta acción no sincronizará QuickBooks.',
  },
  // Heading for the current-blockers list.
  blockersHeading: { en: 'Current blockers', es: 'Bloqueos actuales' },
  // Blocker copy — each gated on a derived, read-only condition.
  blockerDueDates: {
    en: 'Payment creation is blocked until due dates are added.',
    es: 'La creación de pagos está bloqueada hasta agregar fechas de vencimiento.',
  },
  blockerTerms: {
    en: 'Payment creation is blocked until payment terms are reviewed.',
    es: 'La creación de pagos está bloqueada hasta revisar los términos de pago.',
  },
  blockerSuspicious: {
    en: 'Payment creation is blocked until suspicious amount/source is acknowledged.',
    es: 'La creación de pagos está bloqueada hasta reconocer el monto/fuente sospechoso.',
  },
  blockerNoState: {
    en: 'Payment rows cannot be created safely until a draft/proposed payment state is available.',
    es: 'No se pueden crear pagos de forma segura hasta que exista un estado de pago borrador/propuesto.',
  },
  // Disabled action label.
  buttonUnavailable: { en: 'Create payments — unavailable', es: 'Crear pagos — no disponible' },
} as const

// ── NBA-12A: Draft Payment Schedule Foundation ──────────────────────────────
// Copy for the read-only/create-draft panel shown after the NBA-11 guard on
// fully-executed contracts. A DRAFT/PROPOSED schedule is non-binding planning
// data — it is NOT a real payment, is never due/paid, and affects no downstream
// module (Budget / Calendar-Gantt / AIA / QuickBooks). Button copy never implies
// real payments are created.
const NBA12_COPY = {
  // Panel title (proposed/draft framing — NOT "scheduled payments").
  title: { en: 'Draft payment schedule', es: 'Pagos propuestos' },
  // Primary create action — explicitly a DRAFT, never "create payments".
  createButton: {
    en: 'Create draft payment schedule',
    es: 'Crear borrador de calendario de pagos',
  },
  // Count summary — proposed (from draft tables) vs scheduled (real Payment).
  // {p}/{s} are substituted at render; never conflated.
  countSummary: {
    en: '{p} proposed payments · {s} scheduled payments',
    es: '{p} pagos propuestos · {s} pagos programados',
  },
  // Due-dates guidance (dates are never inferred; user must add them).
  addDueDates: {
    en: 'Add due dates before scheduling them',
    es: 'Agrega fechas de vencimiento para poder programarlos',
  },
  // The hard no-effect statement — drafts touch no downstream module.
  noEffect: {
    en: 'These payments are a draft and do not affect Budget, Calendar/Gantt, AIA, or QuickBooks.',
    es: 'Estos pagos son un borrador y no afectan Presupuesto, Calendario/Gantt, AIA ni QuickBooks.',
  },
  // Review-before-real reminder.
  reviewConfirm: {
    en: 'Review and confirm before scheduling real payments.',
    es: 'Revisa y confirma antes de programar pagos reales.',
  },
  // Proposed rows are never treated as real obligations.
  notDueNotPaid: {
    en: 'Proposed payments are not considered due or paid.',
    es: 'Los pagos propuestos no se consideran vencidos ni pagados.',
  },
  // Acknowledgement labels — required before creating a draft when flagged.
  ackSuspicious: {
    en: 'I have reviewed the contract amount and source.',
    es: 'He revisado el monto y la fuente del contrato.',
  },
  ackTerms: {
    en: 'I understand these rows are proposed from the contract text and need review.',
    es: 'Entiendo que estas filas se proponen a partir del texto del contrato y requieren revisión.',
  },
  // Section label for the proposed rows.
  proposedHeading: { en: 'Proposed payments', es: 'Pagos propuestos' },
  // Status badges for an existing draft.
  badgeDraft: { en: 'Draft', es: 'Borrador' },
  badgeProposed: { en: 'Proposed', es: 'Propuesto' },
  // Milestone labels (localized by dueTrigger).
  milestoneDeposit: { en: 'Deposit', es: 'Depósito' },
  milestoneCompletion: { en: 'Completion', es: 'Terminación' },
  dueTriggerDepositStart: { en: 'Deposit / start', es: 'Depósito / inicio' },
  dueTriggerCompletion: { en: 'Completion', es: 'Terminación' },
  // Per-row due-date placeholder (never inferred).
  dueDateMissing: { en: 'Due date: not set', es: 'Vencimiento: sin definir' },
  // States where creation is not offered.
  cannotDerive: {
    en: 'A draft schedule cannot be proposed from these terms — review the payment terms manually.',
    es: 'No se puede proponer un borrador con estos términos — revisa los términos de pago manualmente.',
  },
  creating: { en: 'Creating draft…', es: 'Creando borrador…' },
  createError: {
    en: 'Could not create the draft payment schedule.',
    es: 'No se pudo crear el borrador del calendario de pagos.',
  },
} as const

// NBA-13A — Draft Payment Schedule Review/Edit + Schedule Payments Readiness Guard.
// The user reviews and edits an EXISTING draft schedule (milestoneLabel, dueDate,
// notes — draft-only fields) and sees a clear readiness guard explaining what is
// still required before real scheduling can ever happen. Nothing here creates a
// real Payment, marks anything due/paid, or touches any downstream module. The
// "Schedule payments" button is permanently disabled — scheduling is not enabled.
const NBA13_COPY = {
  reviewTitle: {
    en: 'Review draft payment schedule',
    es: 'Revisar borrador de calendario de pagos',
  },
  readinessTitle: {
    en: 'Schedule payments readiness',
    es: 'Preparación para programar pagos',
  },
  stillDraft: {
    en: 'These are still draft payments. They are not real scheduled payments yet.',
    es: 'Estos siguen siendo pagos en borrador. Todavía no son pagos programados reales.',
  },
  addDueDates: {
    en: 'Add due dates before payments can be scheduled.',
    es: 'Agrega fechas de vencimiento antes de poder programar pagos.',
  },
  // Shown when at least one draft line is still missing a due date.
  addDueDatesMissing: {
    en: 'Add due dates before reviewing the schedule.',
    es: 'Agrega fechas de vencimiento antes de revisar la programación.',
  },
  // Shown when all draft lines have a due date — preflight is available.
  preflightAvailable: {
    en: 'Preflight review is available. Creating real scheduled payments will come next.',
    es: 'La revisión previa está disponible. La creación de pagos programados reales será el siguiente paso.',
  },
  reviewMilestones: {
    en: 'Review milestones and amounts before scheduling.',
    es: 'Revisa hitos y montos antes de programar.',
  },
  schedulingNotEnabled: {
    en: 'Scheduling real payments is not enabled yet.',
    es: 'La programación de pagos reales todavía no está habilitada.',
  },
  // The hard no-effect statement for the readiness guard.
  noEffect: {
    en: 'This will not create Payment rows, mark payments due or paid, update Budget, create Calendar/Gantt events, sync QuickBooks, create AIA billing, update Contacts, PCS, Takeoff, or AI Chat.',
    es: 'Esto no creará filas de Payment, no marcará pagos como vencidos ni pagados, no actualizará Presupuesto, no creará eventos en Calendario/Gantt, no sincronizará QuickBooks, no creará AIA Billing, no actualizará Contactos, PCS, Takeoff ni AI Chat.',
  },
  scheduleButton: {
    en: 'Schedule payments — not available yet',
    es: 'Programar pagos — no disponible todavía',
  },
  // Editable-field labels.
  milestoneLabelLabel: { en: 'Milestone', es: 'Hito' },
  dueDateLabel: { en: 'Due date', es: 'Fecha de vencimiento' },
  notesLabel: { en: 'Notes', es: 'Notas' },
  notesPlaceholder: { en: 'Optional notes', es: 'Notas opcionales' },
  saveLine: { en: 'Save', es: 'Guardar' },
  savingLine: { en: 'Saving…', es: 'Guardando…' },
  editError: {
    en: 'Could not save the change.',
    es: 'No se pudo guardar el cambio.',
  },
  // Readiness-guard blockers — why real scheduling is not available yet.
  blockersHeading: {
    en: 'Still required before real scheduling',
    es: 'Requisitos pendientes para la programación real',
  },
  blockMissingDueDate: {
    en: 'Every payment row needs a due date.',
    es: 'Cada fila de pago necesita una fecha de vencimiento.',
  },
  blockMilestone: {
    en: 'Every payment row needs a valid milestone.',
    es: 'Cada fila de pago necesita un hito válido.',
  },
  blockAmount: {
    en: 'Every payment row needs a valid amount greater than zero.',
    es: 'Cada fila de pago necesita un monto válido mayor que cero.',
  },
  blockSuspiciousAck: {
    en: 'The contract amount and source must be acknowledged.',
    es: 'Se debe reconocer el monto y la fuente del contrato.',
  },
  blockAmbiguousAck: {
    en: 'The free-text / ambiguous terms must be acknowledged.',
    es: 'Se deben reconocer los términos en texto libre / ambiguos.',
  },
  blockScheduledStatus: {
    en: 'Real payment SCHEDULED status is not implemented yet.',
    es: 'El estado SCHEDULED de pagos reales todavía no está implementado.',
  },
  blockProvenance: {
    en: 'Provenance / source fields for real payments are not implemented yet.',
    es: 'Los campos de procedencia / fuente para pagos reales todavía no están implementados.',
  },
  blockEndpoint: {
    en: 'The real payment scheduling endpoint is not implemented yet.',
    es: 'El endpoint para programar pagos reales todavía no está implementado.',
  },
} as const

// ── NBA-13E: Schedule Payments — Preflight Confirmation copy ─────────────────
// A read-only preflight that validates the reviewed draft and shows a
// confirmation summary BEFORE any (future) real scheduling. The preflight
// CREATES NOTHING — the final scheduling action stays disabled ("coming next").
const NBA13E_COPY = {
  // Enabled action: runs the server-validated preflight (no writes).
  reviewBeforeBtn: {
    en: 'Review before scheduling',
    es: 'Revisar antes de programar',
  },
  reviewingBtn: {
    en: 'Reviewing…',
    es: 'Revisando…',
  },
  // Confirmation summary title.
  confirmTitle: {
    en: 'Review scheduled payments',
    es: 'Revisar pagos programados',
  },
  // PR #370 / NBA-13D accepted banner copy (verbatim).
  banner: {
    en: 'These payments are scheduled for future dates. They are not due or paid yet. QuickBooks, AIA, and Calendar/Gantt will not update automatically.',
    es: 'Estos pagos están programados para fechas futuras. Todavía no están vencidos ni pagados. QuickBooks, AIA y Calendario/Gantt no se actualizarán automáticamente.',
  },
  // No-write reassurance.
  noWrite: {
    en: 'This review does not create payments yet.',
    es: 'Esta revisión todavía no crea pagos.',
  },
  // Preflight result — pass / blocked headings.
  readyHeading: {
    en: 'Preflight passed — everything required is present.',
    es: 'Revisión previa aprobada — está presente todo lo requerido.',
  },
  blockedHeading: {
    en: 'Preflight found items to resolve before scheduling.',
    es: 'La revisión previa encontró elementos por resolver antes de programar.',
  },
  blockersHeading: {
    en: 'Must be resolved first',
    es: 'Debe resolverse primero',
  },
  warningsHeading: {
    en: 'Good to know',
    es: 'Información importante',
  },
  summaryHeading: {
    en: 'Payments to be scheduled (preview)',
    es: 'Pagos por programar (vista previa)',
  },
  preflightError: {
    en: 'Could not run the preflight review.',
    es: 'No se pudo ejecutar la revisión previa.',
  },
  // The final scheduling action stays disabled / future.
  finalComingNext: {
    en: 'Schedule payments — coming next',
    es: 'Programar pagos — siguiente paso',
  },
} as const

// ── CREATE-FLOW-4: gated "Schedule payments" action copy ─────────────────────
// The real create action is offered ONLY when the backend readiness probe says
// this user+project may create (global flag ON *and* scoped-allowlisted) AND the
// read-only preflight passed. Otherwise the disabled "coming next" button stays.
// All copy is code-keyed EN/ES; no raw server strings ever reach the UI.
const CF4_CREATE_COPY = {
  // Enabled primary action (opens the explicit final confirmation).
  scheduleBtn: {
    en: 'Schedule payments',
    es: 'Programar pagos',
  },
  schedulingBtn: {
    en: 'Scheduling…',
    es: 'Programando…',
  },
  // Explicit final confirmation before any real payment is created.
  confirmTitle: {
    en: 'Confirm — create scheduled payments',
    es: 'Confirmar — crear pagos programados',
  },
  confirmBody: {
    en: 'This creates real scheduled payments from the reviewed draft. They are scheduled for future dates — not due or paid yet — and do not update Budget, Calendar/Gantt, or QuickBooks.',
    es: 'Esto crea pagos programados reales a partir del borrador revisado. Están programados para fechas futuras — todavía no vencen ni se pagan — y no actualizan Presupuesto, Calendario/Gantt ni QuickBooks.',
  },
  confirmCta: {
    en: 'Confirm & schedule',
    es: 'Confirmar y programar',
  },
  cancelCta: {
    en: 'Cancel',
    es: 'Cancelar',
  },
  // Success states.
  successTitle: {
    en: 'Payments scheduled',
    es: 'Pagos programados',
  },
  reused: {
    en: 'These payments were already scheduled — nothing was duplicated.',
    es: 'Estos pagos ya estaban programados — no se duplicó nada.',
  },
  // Disabled-reason copy when readiness says the flow exists but is not allowed
  // for this project/user (scoped rollout not extended here yet).
  notAllowedForScope: {
    en: 'Scheduling payments is not enabled for this project yet.',
    es: 'La programación de pagos aún no está habilitada para este proyecto.',
  },
  // Error states — mapped from the response code, never the raw server message.
  errDisabled: {
    en: 'Scheduled payments are not enabled.',
    es: 'Los pagos programados no están habilitados.',
  },
  errScope: {
    en: 'Scheduling payments is not enabled for this project.',
    es: 'La programación de pagos no está habilitada para este proyecto.',
  },
  errBlockers: {
    en: 'Some items still need to be resolved before scheduling. Re-run the review.',
    es: 'Aún hay elementos por resolver antes de programar. Vuelve a ejecutar la revisión.',
  },
  errValidation: {
    en: 'Please review and try again.',
    es: 'Revisa e inténtalo de nuevo.',
  },
  errGeneric: {
    en: 'Could not schedule payments. Please try again.',
    es: 'No se pudieron programar los pagos. Inténtalo de nuevo.',
  },
  // Shown when the flow is allowed but the read-only preflight has not passed yet.
  runReviewFirst: {
    en: 'Run the review above first — the button enables once the preflight passes.',
    es: 'Primero ejecuta la revisión de arriba — el botón se habilita cuando la revisión previa se aprueba.',
  },
} as const

// Map a create-flow error response (code + optional blockers[]) to localized copy.
// A 409-with-blockers body carries no top-level code, so detect the blockers array
// explicitly. Unknown/again codes fall back to the neutral generic message.
const localizeCreateError = (
  body: { code?: unknown; blockers?: unknown },
  isEs: boolean,
): string => {
  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
  const code = typeof body?.code === 'string' ? body.code : ''
  if (Array.isArray(body?.blockers) && body.blockers.length > 0) return L(CF4_CREATE_COPY.errBlockers)
  switch (code) {
    case 'SCHEDULED_PAYMENT_CREATE_DISABLED':
      return L(CF4_CREATE_COPY.errDisabled)
    case 'SCHEDULED_PAYMENT_CREATE_NOT_ALLOWED_FOR_SCOPE':
      return L(CF4_CREATE_COPY.errScope)
    case 'CONFIRM_REQUIRED':
    case 'IDEMPOTENCY_KEY_REQUIRED':
      return L(CF4_CREATE_COPY.errValidation)
    default:
      return L(CF4_CREATE_COPY.errGeneric)
  }
}

// Fresh idempotency key for one create attempt. Reused across retries of the SAME
// confirmation so a transient network retry can never double-create (the backend
// per-line unique guard is the real safety net; this just avoids needless churn).
const newIdempotencyKey = (): string => {
  try {
    const c = (globalThis as any)?.crypto
    if (c && typeof c.randomUUID === 'function') return `cf4-${c.randomUUID()}`
  } catch {
    /* fall through to the non-crypto path below */
  }
  return `cf4-${Date.now()}-${Math.floor(Math.random() * 1e9).toString(36)}`
}

// ── NBA-13E: localized preflight blocker / warning copy ──────────────────────
// The preflight endpoint returns stable, code-keyed blockers/warnings; its
// `message` strings are English and exist for debugging only. The UI maps each
// CODE to user-facing EN/ES copy here so the Spanish UI never renders raw English
// server text. Codes are the durable API contract; copy lives entirely client-side
// and never exposes IDs, technical detail, or any private payload.
const PREFLIGHT_CODE_COPY: Record<string, { en: string; es: string }> = {
  // Precondition / scoping (returned as HTTP errors with a `code`).
  PROJECT_NOT_FOUND: {
    en: 'This project could not be found.',
    es: 'No se encontró este proyecto.',
  },
  CONTRACT_NOT_FOUND: {
    en: 'This contract could not be found.',
    es: 'No se encontró este contrato.',
  },
  CONTRACT_NOT_EXECUTED: {
    en: 'The contract must be fully signed before payments can be scheduled.',
    es: 'El contrato debe estar completamente firmado antes de programar pagos.',
  },
  DRAFT_NOT_FOUND: {
    en: 'The draft payment schedule could not be found.',
    es: 'No se encontró el calendario de pagos en borrador.',
  },
  DRAFT_NOT_ACTIVE: {
    en: 'This draft schedule is not ready for review.',
    es: 'Este calendario en borrador todavía no está listo para revisión.',
  },
  // Draft / line blockers (returned in the `blockers` array).
  DRAFT_NO_LINES: {
    en: 'Add at least one payment line before reviewing.',
    es: 'Agrega al menos una línea de pago antes de revisar.',
  },
  LINE_MISSING_MILESTONE: {
    en: 'Each payment needs a milestone name.',
    es: 'Cada pago necesita un nombre de hito.',
  },
  LINE_AMOUNT_INVALID: {
    en: 'Each payment amount must be greater than zero.',
    es: 'Cada monto de pago debe ser mayor que cero.',
  },
  LINE_CURRENCY_INVALID: {
    en: 'Each payment must use a valid currency.',
    es: 'Cada pago debe usar una moneda válida.',
  },
  LINE_MISSING_DUE_DATE: {
    en: 'Each payment needs a due date.',
    es: 'Cada pago necesita una fecha de vencimiento.',
  },
  LINE_INVALID_DUE_DATE: {
    en: 'One or more due dates are not valid.',
    es: 'Una o más fechas de vencimiento no son válidas.',
  },
  LINE_DUE_DATE_IN_PAST: {
    en: 'Scheduled payment dates cannot be in the past.',
    es: 'Las fechas de pagos programados no pueden estar en el pasado.',
  },
  LINE_ALREADY_SCHEDULED: {
    en: 'One or more draft lines have already been scheduled.',
    es: 'Una o más líneas del borrador ya fueron programadas.',
  },
  SUSPICIOUS_AMOUNT_NOT_ACKNOWLEDGED: {
    en: 'Review and acknowledge the flagged amount before scheduling.',
    es: 'Revisa y confirma el monto marcado antes de programar.',
  },
  AMBIGUOUS_TERMS_NOT_ACKNOWLEDGED: {
    en: 'Review and acknowledge the ambiguous payment terms before scheduling.',
    es: 'Revisa y confirma los términos de pago ambiguos antes de programar.',
  },
  // Warnings (returned in the `warnings` array).
  PREFLIGHT_ONLY: {
    en: 'This is a review only. It does not create payments.',
    es: 'Esto es solo una revisión. Todavía no crea pagos.',
  },
  SCHEDULING_ENDPOINT_NOT_IMPLEMENTED: {
    en: 'Real payment scheduling is coming next.',
    es: 'La programación real de pagos es el siguiente paso.',
  },
  MONEY_PRECISION_UNRESOLVED: {
    en: 'Tax and retainage handling for scheduled payments is still being finalized.',
    es: 'El manejo de impuestos y retención para pagos programados aún se está definiendo.',
  },
}

// Fallback for any unknown / future code — never shows raw server text.
const PREFLIGHT_FALLBACK_COPY = {
  en: 'This item needs review before scheduling.',
  es: 'Este elemento necesita revisión antes de programar.',
}

// Map a preflight CODE to localized user-facing copy (never the raw English
// server message). Unknown codes fall back to the neutral message above.
const localizePreflightCode = (code: string, isEs: boolean): string => {
  const entry = PREFLIGHT_CODE_COPY[code] ?? PREFLIGHT_FALLBACK_COPY
  return isEs ? entry.es : entry.en
}

// ── DEMO DATA ───────────────────────────────────

const DEMO_CONTRACTS: Contract[] = [
  {
    id: 'con-1', contractorName: 'Solid Ground LLC', trade: 'Foundation', tradeEs: 'Cimentación',
    totalAmount: 18500, status: 'COMPLETED', createdAt: '2026-02-10T00:00:00Z',
    drawSchedule: [
      { pct: 33, condition: 'Deposit — Upon signing', conditionEs: 'Anticipo — Al firmar', dueDate: '2026-02-20' },
      { pct: 33, condition: 'Progress — After pour complete', conditionEs: 'Avance — Después del colado', dueDate: '2026-03-01' },
      { pct: 34, condition: 'Final — After inspection passed', conditionEs: 'Final — Tras inspección aprobada', dueDate: '2026-03-05' },
    ],
    pdfPath: '/docs/contract-solid-ground.pdf',
  },
  {
    id: 'con-2', contractorName: 'Mendez Framing LLC', trade: 'Framing', tradeEs: 'Estructura',
    totalAmount: 42000, status: 'ACTIVE', createdAt: '2026-02-15T00:00:00Z',
    drawSchedule: [
      { pct: 25, condition: 'Deposit — Materials ordered', conditionEs: 'Anticipo — Materiales pedidos', dueDate: '2026-02-25' },
      { pct: 25, condition: 'Walls up', conditionEs: 'Muros levantados', dueDate: '2026-03-08' },
      { pct: 25, condition: 'Roof trusses set', conditionEs: 'Armaduras de techo colocadas', dueDate: '2026-03-14' },
      { pct: 25, condition: 'Final — Inspection passed', conditionEs: 'Final — Inspección aprobada', dueDate: '2026-03-20' },
    ],
    pdfPath: '/docs/contract-mendez-framing.pdf',
  },
  {
    id: 'con-3', contractorName: 'Torres Plumbing', trade: 'Plumbing', tradeEs: 'Plomería',
    totalAmount: 14500, status: 'ACTIVE', createdAt: '2026-03-10T00:00:00Z',
    drawSchedule: [
      { pct: 40, condition: 'Rough-in complete', conditionEs: 'Instalación oculta terminada', dueDate: '2026-03-25' },
      { pct: 30, condition: 'Top-out and trim', conditionEs: 'Conexiones y acabados', dueDate: '2026-05-01' },
      { pct: 30, condition: 'Final — Fixtures installed', conditionEs: 'Final — Accesorios instalados', dueDate: '2026-05-15' },
    ],
  },
  {
    id: 'con-4', contractorName: 'Spark Electric DFW', trade: 'Electrical', tradeEs: 'Eléctrico',
    totalAmount: 16800, status: 'DRAFT', createdAt: '2026-03-18T00:00:00Z',
    drawSchedule: [],
  },
]

// ── COMPONENT ─────────────────────────────────────

export default function ContractsModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  const [showForm, setShowForm] = useState(false)
  const [showReview, setShowReview] = useState(false)
  const [editingContract, setEditingContract] = useState<Contract | null>(null)

  const { data: apiContracts, isLoading } = useQuery<Contract[]>({
    queryKey: ['contracts', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/contracts`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })
  const isDemo = isDemoProject(projectId)
  const contracts = Array.isArray(apiContracts) ? apiContracts : (isDemo ? DEMO_CONTRACTS : [])

  const statusColors: Record<string, string> = {
    DRAFT:     'bg-[#F2F0EB] text-[#9E9890]',
    ACTIVE:    'bg-green-50 text-green-600',
    COMPLETED: 'bg-sky-50 text-sky-600',
  }

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      {/* F4 — Responsive UX Parity Audit:
          Title + action buttons stack vertically on mobile (<sm) so
          long Spanish CTAs ("Revisar Contrato del Contratista" +
          "Nuevo Contrato") can't push each other off-screen. The
          inner button row gets `flex-wrap` so the two buttons can
          wrap to two lines at very narrow widths instead of being
          clipped. Desktop (≥sm) keeps the title-left / buttons-right
          single-row layout. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{t('Contracts')}</h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {contracts?.length || 0} {t('contracts')}
          </p>
        </div>
        {/* PR #138B: Review uses the AI contract-review endpoint; New
            Contract writes a real DB row + downstream sign flow. Both
            unsafe against a demo project. */}
        {!isDemo && (
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => { setShowReview(true); setShowForm(false) }}
              className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors">
              <Eye size={14} /> {t("Review Contractor's Contract")}
            </button>
            <button onClick={() => { setEditingContract(null); setShowForm(true); setShowReview(false) }}
              className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full">
              <Plus size={14} /> {t('New Contract')}
            </button>
          </div>
        )}
      </div>

      {/* PR #138B: demo banner. Demo contract list still renders below. */}
      {isDemo && (
        <SavedProjectOnlyNotice
          testId="contracts-actions-demo-notice"
          bodyEn="Contract review (AI), new contracts, and signing actions are available in saved projects. Create or open a real project to manage contracts."
          bodyEs="La revisión de contratos (IA), la creación de nuevos contratos y la firma están disponibles en proyectos guardados. Crea o abre un proyecto real para gestionar contratos."
        />
      )}

      {showForm && (
        <NewContractForm
          projectId={projectId!}
          editingContract={editingContract}
          onClose={() => { setShowForm(false); setEditingContract(null) }}
          onCreated={() => { setShowForm(false); setEditingContract(null); queryClient.invalidateQueries({ queryKey: ['contracts', projectId] }) }}
        />
      )}

      {showReview && (
        <ContractReviewFlow
          projectId={projectId!}
          onClose={() => setShowReview(false)}
          onCreated={() => { setShowReview(false); queryClient.invalidateQueries({ queryKey: ['contracts', projectId] }) }}
        />
      )}

      {/* PR-F4: source-honesty trust note — contracts feed Dashboard Committed + AIA */}
      <div data-testid="contracts-trust-note" className="flex items-start gap-2 text-[10px] font-[600] text-[#9E6B00] bg-[#FEF7E6] border border-[#F5E2B8] rounded-lg px-3 py-2 leading-relaxed">
        <Info size={12} className="flex-shrink-0 mt-0.5" />
        <span>
          {isEs
            ? 'Los montos de los contratos activos alimentan el total Comprometido del Panel y pueden usarse en AIA Billing. Los contratos aún no están conectados a las partidas del presupuesto ni al Gantt.'
            : 'Active contract amounts feed the Dashboard Committed total and may be used in AIA Billing. Contracts are not connected to Budget line items or Gantt schedule yet.'}
        </span>
      </div>

      <div className="space-y-3">
        {contracts?.map(contract => (
          <ContractCard key={contract.id} contract={contract} isEs={isEs} isDemo={isDemo} onEdit={contract.status === 'DRAFT' ? () => {
            setEditingContract(contract); setShowForm(true); setShowReview(false)
          } : undefined} />
        ))}
      </div>

      {(!contracts || contracts.length === 0) && !isLoading && (
        <div className="text-center py-12">
          <FileText size={36} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[12px] font-[700] text-[#9E9890]">{t('No contracts yet')}</p>
        </div>
      )}
    </div>
  )
}

// ── CONTRACT CARD ─────────────────────────────────

function ContractCard({ contract, isEs, isDemo, onEdit }: { contract: Contract; isEs: boolean; isDemo: boolean; onEdit?: () => void }) {
  const { getToken } = useAuth()
  const { t } = useTranslation()
  // Final document language of THIS contract, detected from its stored clause
  // text — drives the signing link + PDF section labels (not the app UI lang).
  const contractLang = detectClauseLanguage((contract as any).scopeOfWork, (contract as any).paymentTerms, (contract as any).penalties)
  // iOS (iPhone/iPad incl. iPadOS-on-desktop-UA) and the in-app webviews that
  // share WebKit do NOT render PDFs inline (<iframe>/<object> go blank) and
  // ignore the `download` attribute for blob: URLs. Detect them so the preview
  // skips the blank viewer (shows a Download-first fallback) and the download
  // routes through a data: URL. Same predicate as DrawingMarkup's export path.
  const isIosLike = typeof navigator !== 'undefined' &&
    (/iP(ad|hone|od)/.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) && (navigator.maxTouchPoints || 0) > 1))
  // Coherence gate: a contract whose clause bodies mix English + Spanish must
  // NOT be sent for signature (it would produce an incoherent document /
  // signing page). We block send and ask the user to make all clauses one
  // language first — no auto-translation, no body rewrite.
  const clauseCoherence = contractClauseCoherence((contract as any).scopeOfWork, (contract as any).paymentTerms, (contract as any).penalties)
  const isMixedLanguage = clauseCoherence === 'mixed'
  const queryClient = useQueryClient()
  const { projectId } = useParams()
  const [expanded, setExpanded] = useState(false)
  const [signEmail, setSignEmail] = useState('')
  const [showSignForm, setShowSignForm] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendStatus, setSendStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [downloading, setDownloading] = useState(false)
  // Blob URL of the PDF shown in the in-app preview modal (null = closed).
  // Preview holds BOTH a blob: object URL (for the desktop inline <object>
  // viewer + Open-in-tab) AND the original Blob (so the smart download can
  // route through a data: URL on iOS, where blob: downloads/preview fail).
  const [preview, setPreview] = useState<{ objectUrl: string; blob: Blob } | null>(null)
  // NBA-6: FE-only, session-scoped flag — true once the builder has previewed OR
  // downloaded this contract's PDF in the current view. Drives the readiness
  // guard's PDF reminder (reminder when false, confirmed-note when true). Never
  // persisted, never a send gate — only a soft warn-but-allow signal.
  const [pdfPreviewedThisSession, setPdfPreviewedThisSession] = useState(false)
  // Phase 2 builder/owner countersign modal state.
  const [showBuilderSignModal, setShowBuilderSignModal] = useState(false)
  const [builderSignerName, setBuilderSignerName] = useState('')
  const [builderSignatureData, setBuilderSignatureData] = useState<string | null>(null)
  const [builderConfirm, setBuilderConfirm] = useState(false)
  const [countersigning, setCountersigning] = useState(false)
  const [countersignError, setCountersignError] = useState<string | null>(null)
  // NBA-12A: draft-payment-schedule creation state. Acks are required before
  // creating a draft when the preview is flagged (suspicious amount / free-text
  // terms). Nothing here creates a real payment or touches any other module.
  const [draftAckSuspicious, setDraftAckSuspicious] = useState(false)
  const [draftAckTerms, setDraftAckTerms] = useState(false)
  const [creatingDraft, setCreatingDraft] = useState(false)
  const [draftError, setDraftError] = useState<string | null>(null)
  // NBA-13A: review/edit state for an EXISTING draft schedule's lines. Local edit
  // buffer (keyed by line id) + per-line save/error. Editing PATCHes ONLY the
  // isolated draft line — it never creates a real payment or touches any module.
  const [lineEdits, setLineEdits] = useState<
    Record<string, { milestoneLabel: string; dueDate: string; notes: string }>
  >({})
  // NBA-13F (fix): live DOM refs for the per-line <input type="date"> controls.
  // A native date input does NOT always fire `change` before the user clicks
  // Save (incomplete-segment commit, keyboard entry, a fast click) — so the
  // controlled `lineEdits[id].dueDate` can still be '' even though the field
  // visibly shows the entered date. Saving off that stale state silently sent
  // `dueDate: null`, which the backend faithfully persisted as "clear the date"
  // (200, no error, blank after reload, preflight reports "missing due date").
  // Reading the input's CURRENT DOM value at save time captures the user's real
  // entry regardless of when the change event commits. UI-only; no new request,
  // no schema, no write to any module.
  const dueDateInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const [savingLineId, setSavingLineId] = useState<string | null>(null)
  const [lineEditError, setLineEditError] = useState<string | null>(null)
  // NBA-13E: schedule-payments preflight confirmation state. Running the preflight
  // calls a READ-ONLY server endpoint that validates the draft and returns a safe
  // confirmation summary — it creates NO payments and writes nothing anywhere.
  const [preflightLoading, setPreflightLoading] = useState(false)
  const [preflightError, setPreflightError] = useState<string | null>(null)
  const [preflightResult, setPreflightResult] = useState<{
    ok: boolean
    ready: boolean
    blockers: { code: string; lineId?: string; message: string }[]
    warnings: { code: string; message: string }[]
    summary: {
      contractId: string
      contractorName: string
      trade: string
      totalAmount: number
      currency: string
      proposedLineCount: number
      scheduledLineCount: number
      earliestDueDate: string | null
      latestDueDate: string | null
    }
    lines: { id: string; milestoneLabel: string; amount: number | null; amountMoney?: { cents: number; amount: string; currency: string } | null; currency: string; dueDate: string | null }[]
    downstream: Record<string, boolean>
  } | null>(null)

  // CREATE-FLOW-4: read-only readiness probe — decides whether to OFFER the gated
  // "Schedule payments" create action for this project. Returns booleans + a code
  // only; no secrets. Skipped for demo contracts. `canCreate` is false by default
  // (flag OFF and/or not scoped-allowlisted), so the disabled button stays the norm.
  const { data: scheduleReadiness } = useQuery<{
    enabled: boolean
    allowedForScope: boolean
    canCreate: boolean
    canCancel: boolean
    reasonCode: string | null
  }>({
    queryKey: ['schedule-readiness', projectId],
    queryFn: async () => {
      const token = await getToken()
      return apiFetch(`/api/projects/${projectId}/scheduled-payments/readiness`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      }).then((r) => (r.ok ? r.json() : { enabled: false, allowedForScope: false, canCreate: false, canCancel: false, reasonCode: null }))
    },
    enabled: !isDemo && !!projectId,
    retry: false,
  })

  // CREATE-FLOW-4 create-action state: an explicit two-step confirmation gate + a
  // reused idempotency key + a submitting flag + the result banner (success/error).
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false)
  const [createSubmitting, setCreateSubmitting] = useState(false)
  const [createIdemKey, setCreateIdemKey] = useState<string | null>(null)
  const [createResult, setCreateResult] = useState<
    | { type: 'success'; count: number; reused: boolean }
    | { type: 'error'; message: string }
    | null
  >(null)

  // Open the explicit final confirmation (mints one idempotency key reused across
  // retries of THIS confirmation). Creates NOTHING until the user confirms.
  const openScheduleConfirm = () => {
    setCreateResult(null)
    setCreateIdemKey(newIdempotencyKey())
    setCreateConfirmOpen(true)
  }

  // CREATE-FLOW-4: call the gated create endpoint AFTER explicit confirmation.
  // POSTs { confirm:true, idempotencyKey }. On 201/200 → success (reused-aware) and
  // refresh contracts + payments. On any error → localized banner (by code, never
  // the raw server string). Never retries automatically; the same key is kept so a
  // manual retry can't double-create.
  const runScheduleCreate = async (draftId: string) => {
    if (createSubmitting) return
    setCreateSubmitting(true)
    setCreateResult(null)
    try {
      const token = await getToken()
      const idempotencyKey = createIdemKey ?? newIdempotencyKey()
      const res = await apiFetch(
        `/api/projects/${projectId}/contracts/${contract.id}/payment-schedule-draft/${draftId}/schedule-payments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ confirm: true, idempotencyKey }),
        },
      )
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        setCreateConfirmOpen(false)
        setCreateResult({
          type: 'success',
          count: typeof data?.scheduledCount === 'number' ? data.scheduledCount : 0,
          reused: data?.reusedExisting === true,
        })
        // Draft lines are now promoted → refresh contracts (draft/scheduled state)
        // and payments (the new SCHEDULED rows show in the Payments module).
        queryClient.invalidateQueries({ queryKey: ['contracts', projectId] })
        queryClient.invalidateQueries({ queryKey: ['payments', projectId] })
        // A prior preflight verdict is now stale (lines are scheduled).
        setPreflightResult(null)
      } else {
        setCreateResult({ type: 'error', message: localizeCreateError(data, isEs) })
      }
    } catch {
      setCreateResult({ type: 'error', message: isEs ? CF4_CREATE_COPY.errGeneric.es : CF4_CREATE_COPY.errGeneric.en })
    } finally {
      setCreateSubmitting(false)
    }
  }

  // Preview the contract PDF in a new tab. Hits the existing
  // POST /api/contracts/:id/pdf route which is owner-scoped,
  // read-only, never sends email, and never mutates contract
  // state. The handler ONLY opens the blob when the response is
  // actually a non-empty `application/pdf` — when the server
  // returns a JSON error (e.g. 500 PDF-generator failure) we
  // parse it and surface the message in the sendStatus banner
  // instead of opening a broken viewer.
  const handlePreview = async () => {
    // Hard finalization gate (defense-in-depth): a mixed EN/ES contract can
    // never produce a coherent single-language PDF, so block the action at the
    // handler — not only via the button's `disabled` attribute — and surface a
    // visible bilingual message instead of a silent no-op. No translation.
    if (isMixedLanguage) {
      setSendStatus({ type: 'error', message: mixedLanguageBlockMessage(isEs) })
      return
    }
    // Show the PDF IN-APP (iframe modal) instead of opening a separate blank
    // tab. Opening a blank popup and then pointing it at the blob URL is
    // unreliable across browsers and the standalone PWA: the popup stays on
    // about:blank when blob navigation is suppressed, leaving the user stranded
    // with no result. The modal is always a visible result (it carries Download
    // + Open-in-tab actions), so the user is never left on about:blank. No
    // translation; clause bodies are never touched.
    setPreviewing(true)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/contracts/${contract.id}/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        // Render PDF section labels in the contract's final language (detected
        // from its stored clause text), not the builder's app UI language.
        body: JSON.stringify({ language: contractLang }),
      })
      const contentType = res.headers.get('content-type') || ''
      if (!res.ok || !contentType.toLowerCase().startsWith('application/pdf')) {
        // Try to read a server-provided error message if the
        // response is JSON; otherwise fall back to a generic
        // bilingual error.
        let detail = ''
        try {
          if (contentType.includes('application/json')) {
            const body = await res.json()
            detail = body?.error ? String(body.error) : ''
          } else {
            const text = await res.text()
            if (text && text.length < 200) detail = text
          }
        } catch {
          /* ignore parse failure — fall back to generic message */
        }
        setSendStatus({
          type: 'error',
          message: detail
            ? `${isEs ? 'No se pudo generar la vista previa' : 'Could not generate preview'}: ${detail}`
            : isEs
              ? 'No se pudo generar la vista previa'
              : 'Could not generate preview',
        })
        return
      }
      const blob = await res.blob()
      if (!blob || blob.size === 0) {
        setSendStatus({
          type: 'error',
          message: isEs ? 'Vista previa vacía' : 'Preview was empty',
        })
        return
      }
      // Open the in-app preview modal showing the actual PDF. Keep the Blob so
      // the modal's Download can route through a data: URL on iOS. Release any
      // previously-open object URL first so we never leak blob URLs.
      if (preview) URL.revokeObjectURL(preview.objectUrl)
      setPreview({ objectUrl: URL.createObjectURL(blob), blob })
      setPdfPreviewedThisSession(true) // NBA-6 soft signal — not a send gate
      setSendStatus(null)
    } catch (err: any) {
      setSendStatus({
        type: 'error',
        message: err?.message || (isEs ? 'Error al cargar vista previa' : 'Preview failed'),
      })
    } finally {
      setPreviewing(false)
    }
  }

  // Close the in-app preview modal and release the blob URL.
  const closePreview = () => {
    setPreview(prev => {
      if (prev) URL.revokeObjectURL(prev.objectUrl)
      return null
    })
  }

  // Reliable download for the modal + the card button. downloadPdfBlobSmart
  // (shared with the plans/markups export) routes through a data: URL on iOS
  // — where blob: + the `download` attribute silently fail — and uses the
  // blob + `download` attribute on desktop. Always shows a visible result.
  const savePdfBlob = (blob: Blob) => {
    const safeName = (contract.contractorName || 'contract').replace(/[^A-Za-z0-9._-]+/g, '-')
    try {
      downloadPdfBlobSmart(blob, `Contrato-${safeName}.pdf`, isIosLike)
      setPdfPreviewedThisSession(true) // NBA-6 soft signal — not a send gate
      setSendStatus({
        type: 'success',
        message: isEs ? 'PDF descargado. Revise sus descargas.' : 'PDF downloaded. Check your downloads.',
      })
    } catch (err: any) {
      setSendStatus({
        type: 'error',
        message: err?.message || (isEs ? 'Error al descargar' : 'Download failed'),
      })
    }
  }

  // Best-effort "open in a new tab" for desktop, where a blob: URL opens the
  // browser's native PDF viewer (the richest inspection path). In-app webviews
  // block blob: navigation — the modal's fallback copy points at Download.
  const openPdfInTab = (objectUrl: string) => {
    try {
      window.open(objectUrl, '_blank', 'noopener,noreferrer')
    } catch {
      setSendStatus({
        type: 'error',
        message: isEs
          ? 'No se pudo abrir en una pestaña. Use Descargar.'
          : 'Could not open in a tab. Use Download.',
      })
    }
  }

  // Download the contract PDF to disk. Hits the same
  // POST /api/contracts/:id/pdf route as handlePreview — owner-scoped,
  // read-only, never mutates contract state. The difference from
  // handlePreview is that this handler always forces a client-side
  // download (a.download) instead of showing the in-app preview.
  // pdfPath is intentionally NOT used: the field has no server-side
  // writer for real contracts — the PDF is always generated on the fly.
  const handleDownload = async () => {
    // Hard finalization gate (defense-in-depth): same rule as handlePreview —
    // a mixed EN/ES contract is blocked at the handler with a visible bilingual
    // message, not just greyed out. No translation.
    if (isMixedLanguage) {
      setSendStatus({ type: 'error', message: mixedLanguageBlockMessage(isEs) })
      return
    }
    setDownloading(true)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/contracts/${contract.id}/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ language: contractLang }),
      })
      const contentType = res.headers.get('content-type') || ''
      if (!res.ok || !contentType.toLowerCase().startsWith('application/pdf')) {
        let detail = ''
        try {
          if (contentType.includes('application/json')) {
            const body = await res.json()
            detail = body?.error ? String(body.error) : ''
          } else {
            const text = await res.text()
            if (text && text.length < 200) detail = text
          }
        } catch { /* ignore parse failure */ }
        setSendStatus({
          type: 'error',
          message: detail
            ? `${isEs ? 'No se pudo descargar el PDF' : 'Could not download PDF'}: ${detail}`
            : isEs
              ? 'No se pudo descargar el PDF'
              : 'Could not download PDF',
        })
        return
      }
      const blob = await res.blob()
      if (!blob || blob.size === 0) {
        setSendStatus({
          type: 'error',
          message: isEs ? 'PDF vacío' : 'PDF was empty',
        })
        return
      }
      // iOS-safe download (data: URL on iOS, blob + `download` on desktop) +
      // a visible success message so a silently-saved file is never a no-op.
      savePdfBlob(blob)
    } catch (err: any) {
      setSendStatus({
        type: 'error',
        message: err?.message || (isEs ? 'Error al descargar' : 'Download failed'),
      })
    } finally {
      setDownloading(false)
    }
  }

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-[#F2F0EB] text-[#9E9890]',
    SENT: 'bg-amber-50 text-amber-600',
    ACTIVE: 'bg-green-50 text-green-600',
    COMPLETED: 'bg-sky-50 text-sky-600',
  }

  // Derived flags. The route sets:
  //   contractor sign   → signedAt + signerName, status='ACTIVE'
  //   builder countersign → builderSignedAt + builderSignerName,
  //                         status='COMPLETED'
  // We mirror those two transitions here so the badge / buttons
  // / helper text branch off the same predicates.
  const isSigned = !!contract.signedAt
  const isFullyExecuted = !!contract.builderSignedAt
  // UT-PAGOS-1-R: the draft-schedule panel shows ONLY for a fully executed
  // (builder-signed) contract — no internal-pilot bypass.
  const canShowDraftSchedule = isFullyExecuted
  // Bilingual status label override. ACTIVE = contractor-signed
  // pending builder countersign. COMPLETED + builderSignedAt =
  // fully executed. Anything else falls back to the bare enum.
  // Localize the base status enum (DRAFT/SENT/ACTIVE/COMPLETED) so the badge is
  // never a raw English token in Spanish mode. The signed/executed states below
  // override this with their richer bilingual phrasing.
  const baseStatusLabel = (s: string): string => {
    const en: Record<string, string> = { DRAFT: 'Draft', SENT: 'Sent', ACTIVE: 'Active', COMPLETED: 'Completed' }
    const es: Record<string, string> = { DRAFT: 'Borrador', SENT: 'Enviado', ACTIVE: 'Activo', COMPLETED: 'Completado' }
    return (isEs ? es[s] : en[s]) || s
  }
  const statusLabel: string = isFullyExecuted
    ? (isEs ? 'Contrato completado' : 'Fully executed')
    : isSigned
      ? (isEs
          ? 'Contratista firmado — falta firma del builder'
          : 'Contractor signed — builder signature required')
      : baseStatusLabel(contract.status)

  // Owner-scoped countersign call. The route enforces:
  //   * contract is owned by req.userId
  //   * contractor already signed
  //   * not already countersigned / COMPLETED
  //   * body carries confirm: true (legal-intent capture)
  const submitCountersign = async () => {
    if (!builderSignerName.trim() || !builderSignatureData || !builderConfirm) return
    setCountersigning(true)
    setCountersignError(null)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/contracts/${contract.id}/countersign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          builderSignerName: builderSignerName.trim(),
          builderSignatureImage: builderSignatureData,
          builderSignedAt: new Date().toISOString(),
          confirm: true,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `Request failed (${res.status})`)
      }
      setShowBuilderSignModal(false)
      setBuilderSignerName('')
      setBuilderSignatureData(null)
      setBuilderConfirm(false)
      queryClient.invalidateQueries({ queryKey: ['contracts', projectId] })
    } catch (err: any) {
      setCountersignError(
        err?.message || (isEs ? 'Error al firmar como builder' : 'Countersign failed'),
      )
    } finally {
      setCountersigning(false)
    }
  }

  // NBA-12A: create a DRAFT payment schedule from this executed contract's
  // NBA-10 preview. Hits the owner-scoped POST route which writes ONLY the
  // isolated draft tables — it never creates a real Payment row, never marks a
  // payment due/paid, and never touches Budget, Calendar/Gantt, AIA, QuickBooks,
  // Contacts, PCS, Takeoff, or AI Chat. Acks are sent only when the preview is
  // flagged. On success we refetch contracts so the new draft renders read-only.
  const createDraftSchedule = async (needAckSuspicious: boolean, needAckTerms: boolean) => {
    setCreatingDraft(true)
    setDraftError(null)
    try {
      const token = await getToken()
      const res = await apiFetch(
        `/api/projects/${projectId}/contracts/${contract.id}/payment-schedule-draft`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            ...(needAckSuspicious ? { suspiciousAmountAcknowledged: true } : {}),
            ...(needAckTerms ? { ambiguousTermsAcknowledged: true } : {}),
          }),
        },
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `Request failed (${res.status})`)
      }
      queryClient.invalidateQueries({ queryKey: ['contracts', projectId] })
    } catch (err: any) {
      setDraftError(err?.message || (isEs ? NBA12_COPY.createError.es : NBA12_COPY.createError.en))
    } finally {
      setCreatingDraft(false)
    }
  }

  // NBA-13A: persist a single draft-line edit. PATCHes ONLY the isolated
  // PaymentScheduleDraftLine (milestoneLabel / dueDate / notes). It NEVER creates a
  // real Payment row, never marks anything due/paid, and never touches Budget,
  // Calendar/Gantt, QuickBooks, AIA, Contacts, PCS, Takeoff, or AI Chat.
  const saveDraftLine = async (
    draftId: string,
    lineId: string,
    patch: { milestoneLabel?: string; dueDate?: string | null; notes?: string | null },
  ) => {
    setSavingLineId(lineId)
    setLineEditError(null)
    try {
      const token = await getToken()
      const res = await apiFetch(
        `/api/projects/${projectId}/contracts/${contract.id}/payment-schedule-draft/${draftId}/lines/${lineId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(patch),
        },
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `Request failed (${res.status})`)
      }
      // NBA-13F: consume the PATCH response — the refreshed, server-confirmed draft
      // (header + lines) — and write its lines straight into the contracts cache so
      // the just-saved dueDate is reflected SYNCHRONOUSLY. Without this, deleting the
      // edit buffer below would reseed the date field from the still-cached line
      // (blank for a freshly-entered date) on the immediate re-render — a blank
      // "flash" the user reads as "the due date cleared". This writes ONLY the
      // client read-model the server just returned; it creates no Payment, promotes
      // no line, and touches no other module.
      const refreshed = (await res.json().catch(() => null)) as
        | { id: string; status?: string; lines?: PaymentScheduleDraftLine[] }
        | null
      if (refreshed && typeof refreshed.id === 'string' && Array.isArray(refreshed.lines)) {
        queryClient.setQueryData<Contract[]>(['contracts', projectId], (old) =>
          Array.isArray(old)
            ? old.map((c) =>
                c.id === contract.id
                  ? {
                      ...c,
                      paymentScheduleDrafts: (c.paymentScheduleDrafts ?? []).map((d) =>
                        d.id === refreshed.id
                          ? { ...d, status: refreshed.status ?? d.status, lines: refreshed.lines! }
                          : d,
                      ),
                    }
                  : c,
              )
            : old,
        )
      }
      // Clear the local edit buffer for this line so the row reseeds from the now
      // server-confirmed cached value (no stale buffer, no blank flash).
      setLineEdits((prev) => {
        const next = { ...prev }
        delete next[lineId]
        return next
      })
      // A draft line just changed, so any previously-run preflight verdict is now
      // stale (e.g. a "due date missing" blocker the user has just resolved). Clear
      // it so the panel never shows an out-of-date result; the user re-runs the
      // READ-ONLY preflight to see fresh server truth. This clears UI state only.
      setPreflightResult(null)
      setPreflightError(null)
      // Eventual server-truth: refetch in the background (also covers reload).
      queryClient.invalidateQueries({ queryKey: ['contracts', projectId] })
    } catch (err: any) {
      setLineEditError(err?.message || (isEs ? NBA13_COPY.editError.es : NBA13_COPY.editError.en))
    } finally {
      setSavingLineId(null)
    }
  }

  // NBA-13E: run the READ-ONLY schedule-payments preflight. POSTs to a validation
  // endpoint that creates NOTHING and returns a safe confirmation summary
  // (ok/ready + blockers + warnings + summary + line previews + downstream
  // boundaries). It NEVER creates a real Payment, never promotes a draft line, and
  // never writes any downstream module — it only surfaces what scheduling WOULD do.
  const runSchedulePreflight = async (draftId: string) => {
    setPreflightLoading(true)
    setPreflightError(null)
    try {
      const token = await getToken()
      const res = await apiFetch(
        `/api/projects/${projectId}/contracts/${contract.id}/payment-schedule-draft/${draftId}/schedule-payments/preflight`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({}),
        },
      )
      if (!res.ok) {
        // Localize the precondition/scoping error by CODE — never surface the raw
        // English server `error` string in the Spanish UI. Unknown/codeless errors
        // fall back to the generic preflight-error copy.
        const errBody = await res.json().catch(() => ({} as { code?: string }))
        const code = typeof errBody?.code === 'string' ? errBody.code : ''
        setPreflightResult(null)
        setPreflightError(
          code
            ? localizePreflightCode(code, isEs)
            : isEs
              ? NBA13E_COPY.preflightError.es
              : NBA13E_COPY.preflightError.en,
        )
        return
      }
      const data = await res.json()
      setPreflightResult(data)
    } catch {
      setPreflightResult(null)
      setPreflightError(isEs ? NBA13E_COPY.preflightError.es : NBA13E_COPY.preflightError.en)
    } finally {
      setPreflightLoading(false)
    }
  }

  const draws = contract.drawSchedule || []

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      {/* Accessible expand/collapse: the summary row is a keyboard-operable
          button (role + tabIndex + Enter/Space) exposing aria-expanded so screen
          readers announce the collapsed/expanded state. It contains no nested
          interactive controls, so role="button" is safe (no broad refactor). */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={isEs ? `${expanded ? 'Contraer' : 'Expandir'} contrato de ${contract.contractorName}` : `${expanded ? 'Collapse' : 'Expand'} contract for ${contract.contractorName}`}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded) } }}
        className="p-4 cursor-pointer hover:bg-[#FAFAF8] transition-colors"
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-[13px] font-[800] text-[#1C1A17]">{contract.contractorName}</h3>
            <span className="text-[12px] font-[700] text-[#9E9890]">{isEs && contract.tradeEs ? contract.tradeEs : contract.trade}</span>
          </div>
          <span className={`text-[9px] font-[800] px-2.5 py-1 rounded-full ${statusColors[contract.status] || ''}`}>
            {statusLabel}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* PR-F4: safe amount — show suspicious note if formatMoney flags the value */}
          {(() => {
            const amt = formatMoney(contract.totalAmount, { isEs })
            return (
              <>
                <span className={`text-[16px] font-[900] ${amt.isValid ? 'text-[#1C1A17]' : 'text-[#9E9890]'}`}>
                  {amt.displayValue}
                </span>
                {amt.isSuspicious && (
                  <span data-testid="contract-amount-review-note" className="text-[8px] font-[700] text-[#9E6B00]">
                    {isEs ? 'Revisar fuente del contrato' : 'Verify contract source'}
                  </span>
                )}
              </>
            )
          })()}
          {draws.length > 0 && (
            <span className="text-[9px] font-[700] text-[#9E9890]">{draws.length} {isEs ? 'pagos' : 'draws'}</span>
          )}
          {expanded ? <ChevronUp size={14} className="ml-auto text-[#9E9890]" /> : <ChevronDown size={14} className="ml-auto text-[#9E9890]" />}
        </div>
      </div>

      {/* PR-F3A: anomalous contract amount warning — shown always (not gated on expand)
          when the amount is >= $1M (absolute threshold). Amount remains visible above;
          these lines explain the financial-total exclusion. */}
      {!isDemo && isAnomalousContractAmount(contract.totalAmount) && (
        <div
          data-testid="contract-anomalous-amount-warning"
          className="mx-4 mb-3 flex flex-col gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[10px] font-[700] text-red-700"
        >
          <span data-testid="contract-anomalous-excluded">{isEs ? PRF3A_COPY.excludedFromTotals.es : PRF3A_COPY.excludedFromTotals.en}</span>
          <span data-testid="contract-anomalous-high">{isEs ? PRF3A_COPY.unusuallyHigh.es : PRF3A_COPY.unusuallyHigh.en}</span>
          <span data-testid="contract-anomalous-verify">{isEs ? PRF3A_COPY.verifyBeforeUse.es : PRF3A_COPY.verifyBeforeUse.en}</span>
        </div>
      )}

      {expanded && (
        <div className="px-4 pb-4 border-t border-[#ECEAE4] pt-3">
          {draws.length > 0 && (
            <>
              <h4 className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-2">{t('Draw Schedule')}</h4>
              <div className="space-y-2">
                {draws.map((draw, i) => {
                  // PR-F4: safe draw amount — NaN-proof if totalAmount or pct is bad.
                  const drawAmt = Number.isFinite(contract.totalAmount) && Number.isFinite(draw.pct)
                    ? Math.round(contract.totalAmount * draw.pct / 100)
                    : null
                  return (
                    <div key={i} className="flex items-center gap-3 py-1.5 border-b border-[#F2F0EB] last:border-0">
                      <span className="text-[10px] font-[800] text-[#06539B] w-[40px]">{draw.pct}%</span>
                      <span className="text-[11px] font-[600] flex-1">{isEs && draw.conditionEs ? draw.conditionEs : draw.condition}</span>
                      <span className="text-[12px] font-[800] text-[#1C1A17]">
                        {formatMoneyValue(drawAmt, isEs)}
                      </span>
                    </div>
                  )
                })}
              </div>
              {/* PR-F4: draw schedule auto-creates Payment rows at contract creation — inform user */}
              {!isDemo && (
                <p data-testid="contract-draw-payment-note" className="text-[9px] font-[600] text-[#9E9890] mt-2">
                  {isEs
                    ? 'Los pagos pueden generarse desde este calendario al crear el contrato. Revisar antes de usarse.'
                    : 'Payment rows may be generated from this draw schedule when the contract is created. Review before use.'}
                </p>
              )}
            </>
          )}

          {/* Mixed-language coherence gate (card level): a contract whose clause
              bodies mix EN/ES must not be finalized — Preview, Download, and
              Send for Signature are all blocked until the builder makes the
              clauses one language. No translation, no body rewrite. */}
          {isMixedLanguage && (
            <div data-testid="contract-mixed-language-block" className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[11px] font-[700] text-red-700">
              {mixedLanguageBlockMessage(isEs)}
            </div>
          )}

          {/* Preview PDF — always available, regardless of status
              or pdfPath. The PDF is generated on the fly from the
              current DB row, so it always reflects the latest
              edits (including the contractor signature once the
              contract is signed). No email is sent and the
              contract status is not changed by this action. After
              the contractor signs we relabel the button "View
              signed PDF" / "Ver PDF firmado" so the builder can
              tell at a glance which variant they'll see. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handlePreview() }}
            disabled={previewing || isMixedLanguage}
            className="flex items-center justify-center gap-2 w-full mt-3 border border-[#06539B] text-[#06539B] rounded-full py-2 text-[11px] font-[800] hover:bg-[#EFF6FF] disabled:opacity-40 transition-colors"
          >
            <Eye size={14} />
            {previewing
              ? (isEs ? 'Cargando…' : 'Loading…')
              : isFullyExecuted
                ? (isEs ? 'Ver PDF final firmado' : 'View final signed PDF')
                : isSigned
                  ? (isEs ? 'Ver PDF firmado' : 'View signed PDF')
                  : (isEs ? 'Previsualizar PDF' : 'Preview PDF')}
          </button>

          {/* Download PDF — always available, same route as Preview.
              Forces a.download so the file saves to disk rather than
              opening in a browser tab. pdfPath is not used here:
              that field has no server-side writer for real contracts
              and would make this button invisible to all pilot users. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDownload() }}
            disabled={downloading || isMixedLanguage}
            className="flex items-center justify-center gap-2 w-full mt-2 border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#1C1A17] hover:bg-[#F2F0EB] disabled:opacity-40 transition-colors"
          >
            <Download size={14} />
            {downloading
              ? (isEs ? 'Descargando…' : 'Downloading…')
              : (isEs ? 'Descargar PDF' : 'Download PDF')}
          </button>

          {/* Source / Provenance (Contracts Provenance / Source-Bid Prep).
              READ-ONLY. Shows where a contract came from when provenance was
              recorded. This is display-only — there is NO bid→contract action
              and no NBA-4 button here; it only renders existing, persisted
              provenance. Manual contracts (sourceType === 'MANUAL') show a small
              neutral chip; contracts with no provenance at all render nothing. */}
          {!isDemo && (contract.sourceBidId || contract.sourceBid || contract.sourceType) && (
            // Only claim "Source: selected bid" when a REAL bid pointer exists
            // (sourceBidId or the included sourceBid summary). A bare sourceType
            // — e.g. CONTRACTOR_BID with no linked bid, or CONTRACT_REVIEW — must
            // not assert a selected-bid source. (Backend also enforces that a
            // CONTRACTOR_BID source carries a sourceBidId.)
            (contract.sourceBidId || contract.sourceBid) ? (
              <div className="mt-3" data-testid="contract-source-panel">
                <div className="text-[10px] font-[800] uppercase tracking-wide text-[#9E9890] mb-1">
                  {isEs ? 'Fuente' : 'Source'}
                </div>
                <div className="rounded-lg border border-[#ECEAE4] bg-[#FAF9F6] px-3 py-2 text-[12px] space-y-1">
                  <div className="flex items-center gap-1.5 text-[10px] font-[800] uppercase tracking-wide text-[#06539B]">
                    <FileText size={12} />
                    {isEs ? 'Fuente: propuesta seleccionada' : 'Source: selected bid'}
                  </div>
                  {(contract.sourceBid?.contractorName) && (
                    <div className="font-[800] text-[#1C1A17] truncate">
                      {contract.sourceBid.contractorName}
                      {contract.sourceBid.trade ? ` · ${contract.sourceBid.trade}` : ''}
                    </div>
                  )}
                  {(contract.sourceFilename || contract.sourceBid?.originalFilename) && (
                    <div className="text-[11px] font-[600] text-[#6B655C]">
                      <span className="font-[800] text-[#9E9890]">{isEs ? 'Archivo fuente' : 'Source file'}:</span>{' '}
                      <span className="truncate">{contract.sourceFilename || contract.sourceBid?.originalFilename}</span>
                    </div>
                  )}
                  {contract.sourceNotes && (
                    <div className="text-[11px] font-[600] text-[#6B655C]">
                      <span className="font-[800] text-[#9E9890]">{isEs ? 'Notas de fuente' : 'Source notes'}:</span>{' '}
                      {localizeSourceNotes(contract.sourceNotes, isEs)}
                    </div>
                  )}
                  <div className="text-[10px] font-[600] italic text-[#9E9890]">
                    {isEs
                      ? 'Este contrato está enlazado a una propuesta fuente.'
                      : 'This contract is linked to a source bid.'}
                  </div>
                </div>
              </div>
            ) : contract.sourceType === 'MANUAL' ? (
              <div className="mt-3" data-testid="contract-source-manual">
                <span className="inline-flex items-center gap-1 rounded-full border border-[#ECEAE4] bg-[#FAF9F6] px-2.5 py-1 text-[10px] font-[800] uppercase tracking-wide text-[#9E9890]">
                  {isEs ? 'Contrato manual' : 'Manual contract'}
                </span>
              </div>
            ) : null
          )}

          {/* Contractor Contact (Contacts Linking Foundation). Real contracts
              only — demo rows have no DB id to link against. Linking here lets
              Send for Signature prefill the contractor's email. */}
          {!isDemo && projectId && (
            <div className="mt-3">
              <div className="text-[10px] font-[800] uppercase tracking-wide text-[#9E9890] mb-1">
                {isEs ? 'Contacto del contratista' : 'Contractor contact'}
              </div>
              <ContactLinker
                projectId={projectId}
                entity="contract"
                entityId={contract.id}
                linkedContact={contract.contractorContact ?? null}
                roleFilter="CONTRACTOR"
                detected={{ name: contract.contractorName }}
                isEs={isEs}
                invalidateKeys={[['contracts', projectId]]}
              />
            </div>
          )}

          {/* NBA-5: Review before sending — READ-ONLY guidance/checklist on DRAFT
              contracts. No durable checklist state (no schema), no send/sign
              rework, no auto-translation, no downstream writes. Reuses signals
              already on the contract (provenance, linked contact, clause-language
              coherence) to point the builder at what to review before the manual
              Send for Signature. */}
          {!isDemo && contract.status === 'DRAFT' && (() => {
            const hasSource = !!(contract.sourceBidId || contract.sourceBid)
            const linkedContact = contract.contractorContact ?? null
            const review = deriveDraftReview({
              hasSource,
              hasLinkedContact: !!linkedContact,
              linkedEmail: linkedContact?.email,
              coherence: clauseCoherence,
              sourceNotes: contract.sourceNotes,
            })
            const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
            return (
              <div data-testid="contract-review-before-send" className="mt-3 rounded-xl border border-[#E3D9C6] bg-[#FBF7EE] px-3 py-3 space-y-2.5">
                <div className="flex items-center gap-1.5 text-[11px] font-[900] text-[#1C1A17]">
                  <Info size={13} className="text-[#9E6B00]" />
                  {L(NBA5_COPY.title)}
                </div>

                {/* Explanatory copy */}
                <div className="space-y-1 text-[10px] font-[600] text-[#6B655C]">
                  {hasSource && <p>{L(NBA5_COPY.createdFromSelectedBid)}</p>}
                  <p>{L(NBA5_COPY.reviewIntro)}</p>
                  <p>{L(NBA5_COPY.noDownstream)}</p>
                  <p className="font-[800] text-[#9E6B00]">{L(NBA5_COPY.sendManual)}</p>
                </div>

                {/* Checklist — informational/read-only (not persisted). Rendered
                    as a non-interactive list so it never implies durable state. */}
                <ul data-testid="contract-review-checklist" className="space-y-1">
                  {[
                    NBA5_COPY.checklist.sourceBid,
                    NBA5_COPY.checklist.contact,
                    NBA5_COPY.checklist.language,
                    NBA5_COPY.checklist.scope,
                    NBA5_COPY.checklist.pdf,
                    NBA5_COPY.checklist.ready,
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[10px] font-[700] text-[#1C1A17]">
                      <span aria-hidden className="mt-[3px] inline-block w-1.5 h-1.5 rounded-full border border-[#C9B68E] shrink-0" />
                      <span>{L(item)}</span>
                    </li>
                  ))}
                </ul>

                {/* Source bid summary — safe provenance fields only (never
                    rawAiResponse / storagePath / sensitive payload). */}
                {hasSource && (
                  <div data-testid="contract-review-source-bid" className="rounded-lg border border-[#ECEAE4] bg-white px-2.5 py-2 text-[10px] space-y-0.5">
                    <div className="font-[900] uppercase tracking-wide text-[#9E9890] text-[9px]">{L(NBA5_COPY.sourceBidHeading)}</div>
                    {contract.sourceBid?.contractorName && (
                      <div className="font-[800] text-[#1C1A17] truncate">
                        {contract.sourceBid.contractorName}
                        {contract.sourceBid.trade ? ` · ${contract.sourceBid.trade}` : ''}
                      </div>
                    )}
                    {Number.isFinite(contract.sourceBid?.totalAmount as number) && (
                      <div className="font-[700] text-[#6B655C]">{formatMoney(contract.sourceBid!.totalAmount as number, { isEs }).displayValue}</div>
                    )}
                    {(contract.sourceFilename || contract.sourceBid?.originalFilename) && (
                      <div className="text-[#6B655C] truncate">
                        <span className="font-[800] text-[#9E9890]">{isEs ? 'Archivo fuente' : 'Source file'}:</span>{' '}
                        {contract.sourceFilename || contract.sourceBid?.originalFilename}
                      </div>
                    )}
                    {contract.sourceNotes && (
                      <div className="text-[#6B655C]">
                        <span className="font-[800] text-[#9E9890]">{isEs ? 'Notas de fuente' : 'Source notes'}:</span>{' '}
                        {localizeSourceNotes(contract.sourceNotes, isEs)}
                      </div>
                    )}
                    {contract.sourceBid?.decisionStatus && (
                      <div className="text-[#6B655C]">
                        <span className="font-[800] text-[#9E9890]">{L(NBA5_COPY.selectedBidStatus)}:</span>{' '}
                        {localizeDecisionStatus(contract.sourceBid.decisionStatus, isEs)}
                      </div>
                    )}
                  </div>
                )}

                {/* Contact review */}
                {review.contactState === 'linked_ok' ? (
                  <div data-testid="contract-review-contact-linked" className="flex items-start gap-1.5 text-[10px] font-[700] text-green-700">
                    <CheckCircle size={12} className="mt-[1px] shrink-0" /> {L(NBA5_COPY.contactLinked)}
                  </div>
                ) : review.contactState === 'linked_no_email' ? (
                  <div data-testid="contract-review-contact-no-email" className="flex items-start gap-1.5 text-[10px] font-[700] text-amber-700">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" /> {L(NBA5_COPY.contactNoEmail)}
                  </div>
                ) : (
                  <div data-testid="contract-review-contact-not-linked" className="flex items-start gap-1.5 text-[10px] font-[700] text-amber-700">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" /> {L(NBA5_COPY.contactNotLinked)}
                  </div>
                )}

                {/* Language review */}
                {review.languageState === 'needs_review' ? (
                  <div data-testid="contract-review-language-needs" className="flex items-start gap-1.5 text-[10px] font-[700] text-amber-700">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" /> {L(NBA5_COPY.languageNeedsReview)}
                  </div>
                ) : (
                  <div data-testid="contract-review-language-ready" className="flex items-start gap-1.5 text-[10px] font-[700] text-[#6B655C]">
                    <CheckCircle size={12} className="mt-[1px] shrink-0 text-green-600" /> {L(NBA5_COPY.languageReady)}
                  </div>
                )}

                {/* Source / missing-info review */}
                <div data-testid="contract-review-source-info" className="flex items-start gap-1.5 text-[10px] font-[700] text-[#6B655C]">
                  <Info size={12} className="mt-[1px] shrink-0 text-[#9E6B00]" />
                  {review.sourceInfoNeedsReview ? L(NBA5_COPY.sourceMayNeedReview) : L(NBA5_COPY.sourceReviewNeutral)}
                </div>

                {/* PDF review reminder */}
                <div data-testid="contract-review-pdf" className="flex items-start gap-1.5 text-[10px] font-[700] text-[#6B655C]">
                  <Eye size={12} className="mt-[1px] shrink-0 text-[#06539B]" /> {L(NBA5_COPY.pdfReminder)}
                </div>

                {/* Send-for-Signature guard copy — does NOT disable the button;
                    Send for Signature remains manual and separate (below). */}
                <div data-testid="contract-review-send-guard" className="text-[10px] font-[700] text-[#9E6B00] border-t border-[#E3D9C6] pt-2">
                  {L(NBA5_COPY.sendGuard)}
                </div>
              </div>
            )
          })()}

          {/* Edit Draft */}
          {onEdit && contract.status === 'DRAFT' && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit() }}
              className="flex items-center justify-center gap-2 w-full mt-2 border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#1C1A17] hover:bg-[#F2F0EB] transition-colors"
            >
              <Pencil size={14} /> {t('Edit Draft')}
            </button>
          )}

          {/* Send for Signature
              PR #138B: demo contracts have no DB row → POST `/contracts/
              :id/sign` would fail. Hide the entry button so the sign
              modal cannot open. */}
          {!isDemo && contract.status === 'DRAFT' && !showSignForm && (
            <button
              onClick={() => {
                setShowSignForm(true)
                setSendStatus(null)
                // Prefill the signing email from the linked contractor contact
                // (if any). The builder can still override it — see the manual-
                // override warning below. Only prefill when the field is empty.
                if (!signEmail && contract.contractorContact?.email) setSignEmail(contract.contractorContact.email)
              }}
              disabled={isMixedLanguage}
              className="flex items-center justify-center gap-2 w-full mt-2 bg-[#F5A623] hover:bg-[#E6961A] text-white rounded-full py-2 text-[11px] font-[800] disabled:opacity-40 transition-colors"
            >
              <Send size={14} /> {t('Send for Signature')}
            </button>
          )}
          {/* NBA-6: Send for Signature Readiness Guard. This is the SAME
              showSignForm panel the builder already opened with one explicit
              click (the entry button above is the only place that flips
              showSignForm true). It never auto-opens and never auto-sends; the existing
              POST /send-for-signature still runs only from the final-confirm
              button below. We wrap it as a confirmation layer that states what
              the send does (signature only — no downstream writes) and what to
              review (recipient, linked vs. manual email, final language, PDF).
              Existing email prefill, manual-override + no-email warnings, and
              the mixed-language send block are all preserved unchanged. */}
          {showSignForm && (
            <div data-testid="send-readiness-guard" className="mt-2 space-y-2 rounded-xl border border-[#E3D9C6] bg-[#FBF7EE] px-3 py-3">
              <div data-testid="send-readiness-title" className="text-[12px] font-[900] text-[#1C1A17]">
                {isEs ? NBA6_COPY.title.es : NBA6_COPY.title.en}
              </div>
              {/* Mixed-language coherence gate: block sending an incoherent
                  EN/ES contract. No translation; the builder must make all
                  clauses one language first. */}
              {isMixedLanguage && (
                <div data-testid="contract-mixed-language-block" className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[11px] font-[700] text-red-700">
                  {mixedLanguageBlockMessage(isEs)}
                </div>
              )}
              <div data-testid="send-readiness-recipient-label" className="text-[10px] font-[800] uppercase tracking-wide text-[#9E9890]">
                {isEs ? NBA6_COPY.recipientEmail.es : NBA6_COPY.recipientEmail.en}
              </div>
              <input
                type="email"
                placeholder={t("Contractor's email")}
                value={signEmail}
                onChange={e => setSignEmail(e.target.value)}
                className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
              />
              {/* Contacts Linking Foundation — signing-email guidance. The send
                  mechanics (token, sign/countersign) are UNCHANGED; this only
                  helps the builder use/override the linked contact's email. */}
              {(() => {
                const linkedEmail = contract.contractorContact?.email || ''
                // No email anywhere → tell the builder to link/create/enter one.
                if (!signEmail.trim() && !linkedEmail) {
                  return (
                    <div data-testid="contract-no-email-warning" className="text-[10px] font-[700] text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                      {isEs
                        ? 'No se encontró correo del contratista. Selecciona o crea un contacto, o ingresa un correo antes de enviar.'
                        : 'No contractor email found. Select or create a contact, or enter an email before sending.'}
                    </div>
                  )
                }
                // Email present but not a valid address format → block with clear message.
                if (signEmail.trim() && !isValidRecipientEmail(signEmail.trim())) {
                  return (
                    <div data-testid="contract-invalid-email-warning" className="text-[10px] font-[700] text-red-700 bg-red-50 rounded-lg px-3 py-2">
                      {isEs ? NBA6_COPY.invalidEmail.es : NBA6_COPY.invalidEmail.en}
                    </div>
                  )
                }
                // A typed email that doesn't match the linked contact (or no
                // contact at all) → flag it as a manual, unlinked override.
                if (signEmail.trim() && signEmail.trim() !== linkedEmail) {
                  return (
                    <div className="space-y-1">
                      <div data-testid="contract-manual-email-warning" className="text-[10px] font-[700] text-[#6B655C] bg-[#FAF9F6] border border-[#ECEAE4] rounded-lg px-3 py-2">
                        {isEs ? 'Este correo no está enlazado a un contacto.' : 'This email is not linked to a contact.'}
                      </div>
                      {linkedEmail && (
                        <button
                          data-testid="contract-use-linked-email"
                          onClick={() => setSignEmail(linkedEmail)}
                          className="text-[10px] font-[800] text-[#06539B] underline"
                        >
                          {isEs ? 'Usar correo del contratista enlazado' : 'Use linked contractor email'}
                        </button>
                      )}
                    </div>
                  )
                }
                return null
              })()}
              {/* NBA-6 review rows. All read-only, warn-but-allow (none of these
                  block the send — the only send blocks are no-email + mixed
                  language, preserved on the final button). */}
              {(() => {
                const linkedEmail = contract.contractorContact?.email || ''
                const typed = signEmail.trim()
                const hasLinked = !!linkedEmail
                const isManual = !!typed && typed !== linkedEmail
                return (
                  <div data-testid="send-readiness-review" className="space-y-1.5 text-[10px] font-[700] text-[#6B655C]">
                    {/* Recipient source: linked contact vs. manual override vs. none. */}
                    {isManual ? (
                      <div data-testid="send-readiness-recipient-source" className="text-amber-700">
                        {(isEs ? NBA6_COPY.manualRecipientEmail.es : NBA6_COPY.manualRecipientEmail.en)}
                        {' — '}
                        {(isEs ? NBA6_COPY.notLinkedWarning.es : NBA6_COPY.notLinkedWarning.en)}
                      </div>
                    ) : hasLinked ? (
                      <div data-testid="send-readiness-recipient-source" className="text-green-700">
                        {isEs ? NBA6_COPY.linkedContact.es : NBA6_COPY.linkedContact.en}
                      </div>
                    ) : (
                      <div data-testid="send-readiness-recipient-source" className="text-amber-700">
                        {isEs ? NBA6_COPY.noLinkedContact.es : NBA6_COPY.noLinkedContact.en}
                      </div>
                    )}
                    {/* Final document language (detected from stored clauses). */}
                    <div data-testid="send-readiness-language">
                      {contractLang === 'es'
                        ? (isEs ? NBA6_COPY.langEs.es : NBA6_COPY.langEs.en)
                        : (isEs ? NBA6_COPY.langEn.es : NBA6_COPY.langEn.en)}
                    </div>
                    {/* PDF preview reminder (soft) — reminder when not previewed
                        this session; confirmed note once previewed/downloaded. */}
                    {!pdfPreviewedThisSession && (
                      <div data-testid="send-readiness-pdf-reminder">
                        {isEs ? NBA6_COPY.pdfReminder.es : NBA6_COPY.pdfReminder.en}
                      </div>
                    )}
                    {/* Source bid / review reminder (soft). */}
                    <div data-testid="send-readiness-source-reminder">
                      {isEs ? NBA6_COPY.sourceReminder.es : NBA6_COPY.sourceReminder.en}
                    </div>
                    {/* What the send does + explicit no-downstream statement. */}
                    <div data-testid="send-readiness-signature-only" className="text-[#1C1A17] font-[800]">
                      {isEs ? NBA6_COPY.signatureOnly.es : NBA6_COPY.signatureOnly.en}
                    </div>
                    <div data-testid="send-readiness-no-downstream">
                      {isEs ? NBA6_COPY.noDownstream.es : NBA6_COPY.noDownstream.en}
                    </div>
                  </div>
                )
              })()}
              <div className="flex gap-2">
                <button data-testid="send-readiness-cancel" onClick={() => setShowSignForm(false)} className="flex-1 border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#9E9890]">{isEs ? NBA6_COPY.cancel.es : NBA6_COPY.cancel.en}</button>
                <button
                  data-testid="send-readiness-confirm"
                  disabled={!signEmail || !isValidRecipientEmail(signEmail.trim()) || sending || isMixedLanguage}
                  onClick={async () => {
                    // Hard finalization gate (defense-in-depth): never send a
                    // mixed EN/ES contract for signature, even if the disabled
                    // attribute is bypassed. Surface the bilingual message.
                    if (isMixedLanguage) {
                      setSendStatus({ type: 'error', message: mixedLanguageBlockMessage(isEs) })
                      return
                    }
                    if (!isValidRecipientEmail(signEmail.trim())) {
                      setSendStatus({ type: 'error', message: isEs ? NBA6_COPY.invalidEmail.es : NBA6_COPY.invalidEmail.en })
                      return
                    }
                    setSending(true)
                    setSendStatus(null)
                    try {
                      const token = await getToken()
                      const res = await apiFetch(`/api/contracts/${contract.id}/send-for-signature`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                        // Signing link language = the contract's FINAL document
                        // language (detected from its stored clauses), not the
                        // builder's transient app UI language. So the contractor's
                        // signing page matches the language of the contract text.
                        body: JSON.stringify({ email: signEmail, language: contractLang }),
                      })
                      // PR3 (Codex R2): the server sends codes only — map them to a sentence.
                      const sendFailedText = (code?: string, status?: number) => {
                        if (code === 'EMAIL_DELIVERY_FAILED') return isEs ? 'No se pudo enviar el correo. El contrato sigue en borrador; inténtalo de nuevo.' : 'The email could not be sent. The contract is still a draft; please try again.'
                        if (status === 400) return isEs ? 'Revisa el correo del subcontratista.' : "Check the subcontractor's email."
                        if (status === 404) return isEs ? 'No se encontró el contrato.' : 'Contract not found.'
                        if (status === 409) return isEs ? 'Este contrato ya está firmado.' : 'This contract is already signed.'
                        return isEs ? 'Error al enviar el correo' : 'Failed to send email'
                      }
                      if (!res.ok) {
                        const err = await res.json().catch(() => ({}))
                        throw new Error(sendFailedText(err.code, res.status))
                      }
                      const result = await res.json()
                      if (!result.success) {
                        throw new Error(sendFailedText(result.code))
                      }
                      setShowSignForm(false)
                      setSignEmail('')
                      setSendStatus({ type: 'success', message: isEs ? 'Contrato enviado para firma' : 'Contract sent for signature' })
                      queryClient.invalidateQueries({ queryKey: ['contracts', projectId] })
                      setTimeout(() => setSendStatus(null), 4000)
                    } catch (err: any) {
                      setSendStatus({ type: 'error', message: err.message || (isEs ? 'Error al enviar' : 'Send failed') })
                    }
                    setSending(false)
                  }}
                  className="flex-1 bg-[#F5A623] hover:bg-[#E6961A] text-white rounded-full py-2 text-[11px] font-[800] disabled:opacity-40 transition-colors"
                >
                  {sending ? '...' : (isEs ? NBA6_COPY.finalSend.es : NBA6_COPY.finalSend.en)}
                </button>
              </div>
            </div>
          )}
          {sendStatus && (
            <div className={`mt-2 text-[10px] font-[700] rounded-lg px-3 py-2 ${sendStatus.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
              {sendStatus.message}
            </div>
          )}

          {/* In-app PDF preview modal. Desktop browsers render the PDF inline
              via <object> (with visible fallback children if they can't). iOS /
              in-app WebViews never render PDFs inline and ignore blob: downloads,
              so they get a Download-first panel instead of a blank frame. Either
              way the Download action routes through downloadPdfBlobSmart (data:
              URL on iOS), so the user can always open the PDF and inspect the
              header/footer — Preview is never a silent no-op or blank screen. */}
          {preview && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label={isEs ? 'Vista previa del contrato' : 'Contract preview'}
              className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
              onClick={(e) => { if (e.target === e.currentTarget) closePreview() }}
            >
              <div className="bg-white rounded-xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-[#ECEAE4]">
                  <span className="text-[12px] font-[800] text-[#1C1A17] truncate">
                    {isEs ? 'Vista previa del contrato' : 'Contract preview'} · {contract.contractorName}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    {!isIosLike && (
                      <button
                        type="button"
                        onClick={() => openPdfInTab(preview.objectUrl)}
                        className="border border-[#06539B] text-[#06539B] rounded-full px-3 py-1 text-[10px] font-[800] hover:bg-[#EFF6FF]"
                      >
                        {isEs ? 'Abrir en pestaña' : 'Open in tab'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => savePdfBlob(preview.blob)}
                      className="bg-[#06539B] text-white rounded-full px-3 py-1 text-[10px] font-[800] hover:bg-[#04437f]"
                    >
                      {isEs ? 'Descargar' : 'Download'}
                    </button>
                    <button
                      type="button"
                      onClick={closePreview}
                      aria-label={isEs ? 'Cerrar' : 'Close'}
                      className="border border-[#ECEAE4] text-[#9E9890] rounded-full w-7 h-7 text-[12px] font-[800] hover:bg-[#F2F0EB]"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                {isIosLike ? (
                  // iOS / in-app WebView: inline PDF rendering is unsupported, so
                  // show an actionable Download-first panel instead of a blank box.
                  <div
                    data-testid="contract-pdf-fallback"
                    className="flex-1 w-full bg-[#F2F0EB] flex flex-col items-center justify-center gap-3 px-6 text-center"
                  >
                    <FileText size={28} className="text-[#9E9890]" />
                    <p className="text-[11px] font-[700] text-[#6B6660] max-w-xs">
                      {isEs
                        ? 'La vista previa en línea no es compatible con este navegador. Descargue el PDF para abrirlo e inspeccionar el contrato.'
                        : 'Inline preview isn’t supported in this browser. Download the PDF to open and inspect the contract.'}
                    </p>
                    <button
                      type="button"
                      onClick={() => savePdfBlob(preview.blob)}
                      className="bg-[#06539B] text-white rounded-full px-4 py-1.5 text-[11px] font-[800] hover:bg-[#04437f]"
                    >
                      {isEs ? 'Descargar PDF' : 'Download PDF'}
                    </button>
                  </div>
                ) : (
                  <object
                    data={preview.objectUrl}
                    type="application/pdf"
                    aria-label={isEs ? 'Vista previa del PDF' : 'PDF preview'}
                    className="flex-1 w-full bg-[#F2F0EB]"
                  >
                    {/* Rendered only when the browser can't display the PDF inline. */}
                    <div
                      data-testid="contract-pdf-fallback"
                      className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center"
                    >
                      <FileText size={28} className="text-[#9E9890]" />
                      <p className="text-[11px] font-[700] text-[#6B6660] max-w-xs">
                        {isEs
                          ? 'No se pudo mostrar la vista previa aquí. Descargue el PDF o ábralo en una pestaña para inspeccionar el contrato.'
                          : 'The preview could not be shown here. Download the PDF or open it in a tab to inspect the contract.'}
                      </p>
                      <button
                        type="button"
                        onClick={() => savePdfBlob(preview.blob)}
                        className="bg-[#06539B] text-white rounded-full px-4 py-1.5 text-[11px] font-[800] hover:bg-[#04437f]"
                      >
                        {isEs ? 'Descargar PDF' : 'Download PDF'}
                      </button>
                    </div>
                  </object>
                )}
                <div className="px-4 py-1.5 text-[9px] font-[600] text-[#9E9890] border-t border-[#ECEAE4]">
                  {isEs
                    ? 'Si la vista previa no aparece, use Descargar.'
                    : 'If the preview does not appear, use Download.'}
                </div>
              </div>
            </div>
          )}
          {isSigned && (
            <>
              {contract.signerName && (
                <div className="mt-2 text-[10px] font-[700] text-green-600">
                  ✓ {isEs ? 'Contratista firmado' : 'Contractor signed'}: {contract.signerName}
                  {contract.signedAt
                    ? ` · ${new Date(contract.signedAt).toLocaleDateString(isEs ? 'es-MX' : 'en-US')}`
                    : ''}
                </div>
              )}
              {/* Phase 2 — builder/owner countersign. Visible only
                  while the contract is contractor-signed but not
                  yet fully executed. Once builderSignedAt is
                  written, the button disappears and the row below
                  surfaces the executed-state signature line.
                  PR #138B: hidden entirely in demo so the countersign
                  modal cannot open and `submitCountersign` cannot fire.
                  Guard nesting: `!isFullyExecuted` is the PRIMARY,
                  outer invariant — a fully-executed contract must
                  never re-open the countersign flow regardless of
                  any other state. `!isDemo` is nested as the
                  secondary guard for the demo-tenant carve-out.
                  Runtime is unchanged from the previous flat
                  `!isDemo && !isFullyExecuted` form (boolean AND
                  is commutative); the nesting makes the dominant
                  invariant structurally explicit and satisfies the
                  pin in contracts.smoke.ts #77. */}
              {!isFullyExecuted && (
                !isDemo && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowBuilderSignModal(true)
                      setCountersignError(null)
                    }}
                    className="flex items-center justify-center gap-2 w-full mt-2 bg-[#06539B] hover:bg-[#14457E] text-white rounded-full py-2 text-[11px] font-[800] transition-colors"
                  >
                    <Pencil size={14} />
                    {isEs ? 'Firmar como builder' : 'Sign as builder'}
                  </button>
                )
              )}
              {isFullyExecuted && contract.builderSignerName && (
                <div className="mt-2 text-[10px] font-[700] text-green-600">
                  ✓ {isEs ? 'Builder firmó' : 'Builder signed'}: {contract.builderSignerName}
                  {contract.builderSignedAt
                    ? ` · ${new Date(contract.builderSignedAt).toLocaleDateString(isEs ? 'es-MX' : 'en-US')}`
                    : ''}
                </div>
              )}
              {isFullyExecuted && (
                <div className="mt-1 text-[10px] font-[700] text-[#06539B]">
                  {isEs ? 'Contrato completado' : 'Fully executed'}
                </div>
              )}
            </>
          )}

          {/* NBA-8: Executed Contract Readiness / Post-Signature Summary.
              READ-ONLY. Shown ONLY on real, fully-executed contracts (both
              parties signed → builderSignedAt present). Renders existing row
              data (status, signatures, amount, final PDF access) and makes the
              downstream-controls boundary explicit. NO mutation, NO write
              fetch, NO schema, NO lifecycle/signature change. Indicators use
              "by this workflow" wording so they never falsely claim a
              downstream module is empty. */}
          {!isDemo && isFullyExecuted && (() => {
            const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
            const amt = formatMoney(contract.totalAmount, { isEs })
            const needsAmountReview = !amt.isValid || amt.isSuspicious
            const readiness = [
              NBA8_COPY.paymentsNot,
              NBA8_COPY.budgetNot,
              NBA8_COPY.calendarNot,
              NBA8_COPY.quickbooksNot,
              NBA8_COPY.pcsNot,
              NBA8_COPY.takeoffNot,
              NBA8_COPY.aiChatNot,
            ]
            return (
              <div data-testid="executed-contract-summary" className="mt-3 rounded-xl border border-[#CBD9E8] bg-[#F4F8FC] px-3 py-3 space-y-2.5">
                <div className="flex items-center gap-1.5 text-[11px] font-[900] text-[#1C1A17]">
                  <CheckCircle size={13} className="text-[#06539B]" />
                  {L(NBA8_COPY.title)}
                </div>
                <p className="text-[11px] font-[700] text-[#1C1A17]">{L(NBA8_COPY.coreCompleted)}</p>
                <p className="text-[11px] font-[600] text-[#6B655C]">{L(NBA8_COPY.coreReview)}</p>

                {/* Final state — sourced entirely from the existing contract row. */}
                <div className="rounded-lg border border-[#E2EAF3] bg-white px-3 py-2 text-[11px] space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-[800] text-[#9E9890]">{L(NBA8_COPY.statusLabel)}</span>
                    <span className="font-[800] text-[#06539B]">{isEs ? 'Contrato completado' : 'Fully executed'}</span>
                  </div>
                  {contract.signerName && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-[800] text-[#9E9890]">{L(NBA8_COPY.contractorLabel)}</span>
                      <span className="font-[700] text-[#1C1A17] truncate">
                        {contract.signerName}
                        {contract.signedAt ? ` · ${new Date(contract.signedAt).toLocaleDateString(isEs ? 'es-MX' : 'en-US')}` : ''}
                      </span>
                    </div>
                  )}
                  {contract.builderSignerName && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-[800] text-[#9E9890]">{L(NBA8_COPY.builderLabel)}</span>
                      <span className="font-[700] text-[#1C1A17] truncate">
                        {contract.builderSignerName}
                        {contract.builderSignedAt ? ` · ${new Date(contract.builderSignedAt).toLocaleDateString(isEs ? 'es-MX' : 'en-US')}` : ''}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-[800] text-[#9E9890]">{L(NBA8_COPY.amountLabel)}</span>
                    <span data-testid="executed-summary-amount" className={`font-[900] ${amt.isValid && !amt.isSuspicious ? 'text-[#1C1A17]' : 'text-[#9E6B00]'}`}>
                      {amt.displayValue}
                    </span>
                  </div>
                </div>

                {/* Downstream readiness — "by this workflow" wording only. */}
                <div data-testid="executed-summary-readiness">
                  <div className="text-[9px] font-[900] uppercase tracking-wide text-[#9E9890] mb-1">{L(NBA8_COPY.readinessHeading)}</div>
                  <ul className="space-y-1">
                    {readiness.map((c, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[10px] font-[600] text-[#6B655C]">
                        <Info size={11} className="mt-[1px] shrink-0 text-[#06539B]" />
                        {L(c)}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Always-on review reminder (dashboards/AIA may read totals). */}
                <div data-testid="executed-summary-dashboard-warning" className="flex items-start gap-1.5 rounded-lg bg-[#FBF7EE] border border-[#E3D9C6] px-2.5 py-2 text-[10px] font-[700] text-[#9E6B00]">
                  <AlertTriangle size={12} className="mt-[1px] shrink-0" />
                  {L(NBA8_COPY.dashboardReadsWarning)}
                </div>

                {/* Stronger amount/source warning — only when amount is
                    unavailable or suspicious (e.g. unusually large total). */}
                {needsAmountReview && (
                  <div data-testid="executed-summary-amount-warning" className="flex items-start gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2.5 py-2 text-[10px] font-[800] text-red-700">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" />
                    {L(NBA8_COPY.amountSourceWarning)}
                  </div>
                )}
              </div>
            )
          })()}

          {/* NBA-9: Payment Schedule Readiness — read-only readiness layer on a
              completed/executed contract. Informs that the contract is ready for
              payment-schedule review; never creates a payment, never writes any
              downstream module. Gated identically to NBA-8 (fully executed). */}
          {!isDemo && isFullyExecuted && (() => {
            const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
            const amt = formatMoney(contract.totalAmount, { isEs })
            const needsAmountReview = !amt.isValid || amt.isSuspicious
            // Free-text payment terms live as a string on the saved contract
            // (NBA-4 stores a plain-text hint). Coerce defensively so a stray
            // object never renders as "[object Object]".
            const rawTerms = (contract as any).paymentTerms
            const paymentTermsText = typeof rawTerms === 'string' ? rawTerms.trim() : ''
            const hasFreeTextTerms = paymentTermsText.length > 0
            const hasStructuredSchedule = Array.isArray(contract.drawSchedule) && contract.drawSchedule.length > 0
            return (
              <div data-testid="payment-schedule-readiness" className="mt-3 rounded-xl border border-[#CBD9E8] bg-[#F4F8FC] px-3 py-3 space-y-2.5">
                <div className="flex items-center gap-1.5 text-[11px] font-[900] text-[#1C1A17]">
                  <DollarSign size={13} className="text-[#06539B]" />
                  {L(NBA9_COPY.title)}
                </div>
                <p className="text-[11px] font-[700] text-[#1C1A17]">{L(NBA9_COPY.coreReady)}</p>
                <p className="text-[11px] font-[600] text-[#6B655C]">{L(NBA9_COPY.noPaymentsCreated)}</p>
                <p className="text-[11px] font-[600] text-[#6B655C]">{L(NBA9_COPY.reviewBeforeCreating)}</p>

                {/* Readiness fields — sourced entirely from the existing row. */}
                <div className="rounded-lg border border-[#E2EAF3] bg-white px-3 py-2 text-[11px] space-y-1">
                  {contract.contractorName && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-[800] text-[#9E9890]">{L(NBA9_COPY.contractorLabel)}</span>
                      <span className="font-[700] text-[#1C1A17] truncate">{contract.contractorName}</span>
                    </div>
                  )}
                  {contract.trade && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-[800] text-[#9E9890]">{L(NBA9_COPY.tradeLabel)}</span>
                      {/* tradeEs is demo-only; this panel is !isDemo-gated, so render trade plainly. */}
                      <span className="font-[700] text-[#1C1A17] truncate">{contract.trade}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-[800] text-[#9E9890]">{L(NBA9_COPY.amountLabel)}</span>
                    <span data-testid="payment-readiness-amount" className={`font-[900] ${amt.isValid && !amt.isSuspicious ? 'text-[#1C1A17]' : 'text-[#9E6B00]'}`}>
                      {amt.displayValue}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-[800] text-[#9E9890]">{L(NBA9_COPY.scheduleStatusLabel)}</span>
                    <span data-testid="payment-readiness-schedule-status" className="font-[800] text-[#9E6B00] text-right">
                      {hasStructuredSchedule ? L(NBA9_COPY.noScheduledPayments) : L(NBA9_COPY.structuredScheduleMissing)}
                    </span>
                  </div>
                  {hasFreeTextTerms && (
                    <div className="pt-1">
                      <div className="font-[800] text-[#9E9890] mb-0.5">{L(NBA9_COPY.paymentTermsLabel)}</div>
                      <div data-testid="payment-readiness-terms-text" className="font-[600] text-[#6B655C] whitespace-pre-wrap break-words">{paymentTermsText}</div>
                    </div>
                  )}
                </div>

                {/* Free-text terms warning — terms are text, not structured rows. */}
                {hasFreeTextTerms && (
                  <div data-testid="payment-readiness-freetext-warning" className="flex items-start gap-1.5 rounded-lg bg-[#FBF7EE] border border-[#E3D9C6] px-2.5 py-2 text-[10px] font-[700] text-[#9E6B00]">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" />
                    {L(NBA9_COPY.freeTextTermsWarning)}
                  </div>
                )}

                {/* Stronger amount/source warning — only when amount is
                    unavailable or suspicious (per-contract gate, same as NBA-8). */}
                {needsAmountReview && (
                  <div data-testid="payment-readiness-amount-warning" className="flex items-start gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2.5 py-2 text-[10px] font-[800] text-red-700">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" />
                    {L(NBA9_COPY.amountSourceWarning)}
                  </div>
                )}

                {/* Downstream readiness — "by this workflow" wording only. */}
                <div data-testid="payment-readiness-downstream" className="space-y-1">
                  <p className="text-[10px] font-[700] text-[#6B655C]">{L(NBA9_COPY.downstreamCombined)}</p>
                  <ul className="space-y-1">
                    {[NBA9_COPY.budgetNot, NBA9_COPY.calendarNot, NBA9_COPY.quickbooksNot].map((c, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[10px] font-[600] text-[#6B655C]">
                        <Info size={11} className="mt-[1px] shrink-0 text-[#06539B]" />
                        {L(c)}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-[10px] font-[700] text-[#06539B]">{L(NBA9_COPY.createOnlyAfterReview)}</p>
              </div>
            )
          })()}

          {/* NBA-10 — Proposed Payment Schedule Preview. Read-only; sibling under
              NBA-9. Conservatively parses free-text terms into proposed rows; never
              creates a payment, infers a date, or touches any downstream module. */}
          {!isDemo && canShowDraftSchedule && (() => {
            const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
            const amt = formatMoney(contract.totalAmount, { isEs })
            const rawTerms = (contract as any).paymentTerms
            const paymentTermsText = typeof rawTerms === 'string' ? rawTerms.trim() : ''
            const preview = deriveProposedSchedule(paymentTermsText, contract.totalAmount)

            // Warning gating — distinct, non-overlapping reasons.
            const showAmountInvalid = preview.amountInvalid
            const showSuspiciousSource = amt.isSuspicious // valid but large (e.g. $150M)
            const showFreeText = preview.hasFreeText
            // "Doesn't sum to 100" is the SPECIFIC reason when classifiable percents
            // exist but don't total 100; otherwise a generic "review manually".
            const showSumWarning =
              preview.hasClassifiedPercents && !preview.percentsSumTo100 && !preview.amountInvalid
            const showAmbiguous =
              preview.hasFreeText && !preview.showRows && !preview.amountInvalid && !showSumWarning
            // Dates are never inferred → when rows are shown, the due date is missing.
            const showDueDateMissing = preview.showRows

            const milestoneLabel = (key: 'deposit' | 'completion') =>
              key === 'deposit' ? L(NBA10_COPY.milestoneDeposit) : L(NBA10_COPY.milestoneCompletion)
            const triggerLabel = (t: 'deposit_start' | 'completion') =>
              t === 'deposit_start' ? L(NBA10_COPY.triggerDepositStart) : L(NBA10_COPY.triggerCompletion)

            return (
              <div data-testid="proposed-payment-schedule-preview" className="mt-3 rounded-xl border border-[#CBD9E8] bg-[#F4F8FC] px-3 py-3 space-y-2.5">
                <div className="flex items-center gap-1.5 text-[11px] font-[900] text-[#1C1A17]">
                  <DollarSign size={13} className="text-[#06539B]" />
                  {L(NBA10_COPY.title)}
                </div>
                <p className="text-[11px] font-[700] text-[#1C1A17]">{L(NBA10_COPY.previewOnly)}</p>
                <p className="text-[11px] font-[600] text-[#6B655C]">{L(NBA10_COPY.reviewBeforeRows)}</p>

                {/* Invalid amount blocks the preview math entirely. */}
                {showAmountInvalid && (
                  <div data-testid="payment-preview-amount-invalid" className="flex items-start gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2.5 py-2 text-[10px] font-[800] text-red-700">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" />
                    {L(NBA10_COPY.amountInvalidWarning)}
                  </div>
                )}

                {/* Proposed rows — shown ONLY when the parse is confident. */}
                {preview.showRows && (
                  <div data-testid="payment-preview-rows" className="rounded-lg border border-[#E2EAF3] bg-white px-3 py-2 text-[11px]">
                    <div className="grid grid-cols-[1.2fr_0.6fr_1fr_1.1fr] gap-1 pb-1 text-[9px] font-[900] uppercase tracking-wide text-[#9E9890]">
                      <span>{L(NBA10_COPY.milestoneLabel)}</span>
                      <span>{L(NBA10_COPY.percentLabel)}</span>
                      <span>{L(NBA10_COPY.amountLabel)}</span>
                      <span>{L(NBA10_COPY.dueTriggerLabel)}</span>
                    </div>
                    {preview.rows.map((row, i) => (
                      <div key={i} data-testid="payment-preview-row" className="grid grid-cols-[1.2fr_0.6fr_1fr_1.1fr] gap-1 py-1 border-t border-[#F0F4F9] items-center">
                        <span className="font-[800] text-[#1C1A17]">{milestoneLabel(row.key)}</span>
                        <span className="font-[700] text-[#1C1A17]">{row.pct}%</span>
                        <span className="font-[900] text-[#1C1A17]">{formatMoneyValue(row.amount, isEs)}</span>
                        <span className="font-[700] text-[#6B655C]">{triggerLabel(row.dueTrigger)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Due date missing — dates are never inferred. */}
                {showDueDateMissing && (
                  <div data-testid="payment-preview-due-missing" className="flex items-start gap-1.5 rounded-lg bg-[#FBF7EE] border border-[#E3D9C6] px-2.5 py-2 text-[10px] font-[800] text-[#9E6B00]">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" />
                    <span>{L(NBA10_COPY.dueDateMissing)} — {L(NBA10_COPY.dueDatesRequired)}</span>
                  </div>
                )}

                {/* Free-text terms always warrant review (even when rows are shown). */}
                {showFreeText && (
                  <div data-testid="payment-preview-freetext-warning" className="flex items-start gap-1.5 rounded-lg bg-[#FBF7EE] border border-[#E3D9C6] px-2.5 py-2 text-[10px] font-[700] text-[#9E6B00]">
                    <Info size={12} className="mt-[1px] shrink-0" />
                    {L(NBA10_COPY.freeTextWarning)}
                  </div>
                )}

                {/* Percentages don't sum to 100% — specific manual-review reason. */}
                {showSumWarning && (
                  <div data-testid="payment-preview-sum-warning" className="flex items-start gap-1.5 rounded-lg bg-[#FBF7EE] border border-[#E3D9C6] px-2.5 py-2 text-[10px] font-[800] text-[#9E6B00]">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" />
                    {L(NBA10_COPY.sumWarning)}
                  </div>
                )}

                {/* Generic ambiguous-terms manual-review (no safe rows produced). */}
                {showAmbiguous && (
                  <div data-testid="payment-preview-ambiguous-warning" className="flex items-start gap-1.5 rounded-lg bg-[#FBF7EE] border border-[#E3D9C6] px-2.5 py-2 text-[10px] font-[800] text-[#9E6B00]">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" />
                    {L(NBA10_COPY.ambiguousWarning)}
                  </div>
                )}

                {/* Suspicious-but-finite amount/source (e.g. $150M). */}
                {showSuspiciousSource && (
                  <div data-testid="payment-preview-amount-source-warning" className="flex items-start gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2.5 py-2 text-[10px] font-[800] text-red-700">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" />
                    {L(NBA10_COPY.amountSourceWarning)}
                  </div>
                )}

                {/* Downstream boundary + no-payments-created statement. */}
                <div data-testid="payment-preview-downstream" className="space-y-1">
                  <p className="text-[10px] font-[700] text-[#6B655C]">{L(NBA10_COPY.downstreamNot)}</p>
                </div>
                <p className="text-[10px] font-[700] text-[#06539B]">{L(NBA10_COPY.createOnlyAfterConfirm)}</p>
              </div>
            )
          })()}

          {/* ── NBA-11: Payment Row Creation Readiness / Explicit Create Payments
              Guard. Read-only. Shown below NBA-10 on completed/executed
              contracts. NEVER creates a payment: the only action control is a
              DISABLED button with no onClick/fetch/mutation/navigation. The
              panel is always blocked (no DRAFT/PROPOSED payment state exists),
              so creating a PENDING row — which would read as a real obligation
              — is intentionally impossible from here. ── */}
          {!isDemo && isFullyExecuted && (() => {
            const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
            const amt = formatMoney(contract.totalAmount, { isEs })
            const rawTerms = (contract as any).paymentTerms
            const paymentTermsText = typeof rawTerms === 'string' ? rawTerms.trim() : ''
            const preview = deriveProposedSchedule(paymentTermsText, contract.totalAmount)

            // Due dates are never inferred. They exist ONLY if a real structured
            // draw schedule carries a non-empty dueDate on EVERY row.
            const scheduleHasDueDates =
              Array.isArray(contract.drawSchedule) &&
              contract.drawSchedule.length > 0 &&
              contract.drawSchedule.every(
                (d) => typeof d.dueDate === 'string' && d.dueDate.trim().length > 0,
              )

            // Blockers — each derived read-only. blockNoProposedState is ALWAYS
            // true (architectural: the Payment model has no draft/proposed
            // state), which keeps the action permanently disabled.
            const blockDueDates = !scheduleHasDueDates
            const blockTermsReview = preview.hasFreeText || preview.hasAmbiguousClauses
            const blockSuspicious = amt.isSuspicious || !amt.isValid
            const blockNoProposedState = true

            const blockers: { testid: string; copy: { en: string; es: string } }[] = []
            if (blockDueDates)
              blockers.push({ testid: 'create-payments-block-due-dates', copy: NBA11_COPY.blockerDueDates })
            if (blockTermsReview)
              blockers.push({ testid: 'create-payments-block-terms', copy: NBA11_COPY.blockerTerms })
            if (blockSuspicious)
              blockers.push({ testid: 'create-payments-block-suspicious', copy: NBA11_COPY.blockerSuspicious })
            if (blockNoProposedState)
              blockers.push({ testid: 'create-payments-block-no-state', copy: NBA11_COPY.blockerNoState })

            return (
              <div data-testid="create-payments-readiness" className="mt-3 rounded-xl border border-[#CBD9E8] bg-[#F4F8FC] px-3 py-3 space-y-2.5">
                <div className="flex items-center gap-1.5 text-[11px] font-[900] text-[#1C1A17]">
                  <DollarSign size={13} className="text-[#06539B]" />
                  {L(NBA11_COPY.title)}
                </div>
                <p className="text-[11px] font-[700] text-[#1C1A17]">{L(NBA11_COPY.reviewConfirm)}</p>
                <p className="text-[11px] font-[600] text-[#6B655C]">{L(NBA11_COPY.onlyAfterConfirm)}</p>
                <p className="text-[11px] font-[600] text-[#6B655C]">{L(NBA11_COPY.dueDatesRequired)}</p>

                {/* Current blockers — always at least one (no draft/proposed state). */}
                <div data-testid="create-payments-blockers" className="space-y-1.5">
                  <p className="text-[9px] font-[900] uppercase tracking-wide text-[#9E9890]">
                    {L(NBA11_COPY.blockersHeading)}
                  </p>
                  {blockers.map((b) => (
                    <div
                      key={b.testid}
                      data-testid={b.testid}
                      className="flex items-start gap-1.5 rounded-lg bg-[#FBF7EE] border border-[#E3D9C6] px-2.5 py-2 text-[10px] font-[800] text-[#9E6B00]"
                    >
                      <AlertTriangle size={12} className="mt-[1px] shrink-0" />
                      {L(b.copy)}
                    </div>
                  ))}
                </div>

                {/* Downstream boundary statements. */}
                <div data-testid="create-payments-downstream" className="space-y-1">
                  <p className="text-[10px] font-[700] text-[#6B655C]">{L(NBA11_COPY.downstreamNot)}</p>
                  <p className="text-[10px] font-[700] text-[#6B655C]">{L(NBA11_COPY.notMarkPaid)}</p>
                  <p className="text-[10px] font-[700] text-[#6B655C]">{L(NBA11_COPY.notSyncQb)}</p>
                </div>

                {/* Disabled action — intentionally inert: no handler, no
                    request, no write, no nav, no record creation. */}
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  data-testid="create-payments-disabled-btn"
                  className="w-full rounded-lg border border-[#D6CFC4] bg-[#ECEAE4] px-3 py-2 text-[11px] font-[800] text-[#9E9890] cursor-not-allowed"
                >
                  {L(NBA11_COPY.buttonUnavailable)}
                </button>
              </div>
            )
          })()}

          {/* ── NBA-12A: Draft Payment Schedule ─────────────────────────────
              Shown after the NBA-11 guard on fully-executed contracts. This is
              the "draft/proposed payment state" NBA-11 was waiting for. Creating
              a draft writes ONLY the isolated PaymentScheduleDraft tables — it
              never creates a real Payment row, never marks anything due/paid,
              and affects NO downstream module (Budget / Calendar-Gantt / AIA /
              QuickBooks). Proposed rows are clearly separated from real scheduled
              payments and are never counted as due or paid. ── */}
          {!isDemo && canShowDraftSchedule && (() => {
            const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
            const amt = formatMoney(contract.totalAmount, { isEs })
            const rawTerms = (contract as any).paymentTerms
            const paymentTermsText = typeof rawTerms === 'string' ? rawTerms.trim() : ''
            const preview = deriveProposedSchedule(paymentTermsText, contract.totalAmount)

            // The single ACTIVE draft (if one was already created), read-only.
            const existingDraft =
              Array.isArray(contract.paymentScheduleDrafts) && contract.paymentScheduleDrafts.length > 0
                ? contract.paymentScheduleDrafts[0]
                : null

            // "scheduled" count comes from REAL Payment rows only — NEVER from the
            // draft tables. proposed count comes from the draft (or, before one is
            // created, from the conservative preview). The two are never conflated.
            const scheduledCount = Array.isArray(contract.payments) ? contract.payments.length : 0
            const proposedCount = existingDraft ? existingDraft.lines.length : preview.rows.length

            // Flags that require an explicit acknowledgement before creating.
            const amountFlagged = amt.isSuspicious || !amt.isValid
            const termsFlagged = preview.hasFreeText || preview.hasAmbiguousClauses
            // A draft can be proposed only when the parser produced a safe schedule.
            const canDerive = preview.showRows && preview.rows.length > 0
            const acksSatisfied =
              (!amountFlagged || draftAckSuspicious) && (!termsFlagged || draftAckTerms)

            const countLine = L(NBA12_COPY.countSummary)
              .replace('{p}', String(proposedCount))
              .replace('{s}', String(scheduledCount))

            const triggerLabel = (trigger?: string | null) =>
              trigger === 'deposit_start'
                ? L(NBA12_COPY.dueTriggerDepositStart)
                : trigger === 'completion'
                  ? L(NBA12_COPY.dueTriggerCompletion)
                  : ''
            const milestoneLabel = (trigger?: string | null) =>
              trigger === 'completion' ? L(NBA12_COPY.milestoneCompletion) : L(NBA12_COPY.milestoneDeposit)

            // Rows to render: persisted draft lines if present, else the preview rows.
            const renderRows: { key: string; label: string; pct: number | null; amount: number | null; trigger: string | null }[] =
              existingDraft
                ? existingDraft.lines.map((l) => ({
                    key: l.id,
                    label: milestoneLabel(l.dueTrigger),
                    pct: typeof l.percent === 'number' ? l.percent : null,
                    // PR-F4G: prefer exact amountMoney, fall back to legacy number.
                    amount: draftLineAmount(l),
                    trigger: l.dueTrigger ?? null,
                  }))
                : preview.rows.map((r, i) => ({
                    key: `prev-${i}`,
                    label: milestoneLabel(r.dueTrigger),
                    pct: r.pct,
                    amount: r.amount,
                    trigger: r.dueTrigger,
                  }))

            return (
              <div data-testid="draft-payment-schedule" className="mt-3 rounded-xl border border-[#CBD9E8] bg-[#F4F8FC] px-3 py-3 space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-[11px] font-[900] text-[#1C1A17]">
                    <DollarSign size={13} className="text-[#06539B]" />
                    {L(NBA12_COPY.title)}
                  </div>
                  {existingDraft && (
                    <span
                      data-testid="draft-schedule-badge"
                      className="rounded-full bg-[#E3ECF5] border border-[#CBD9E8] px-2 py-[2px] text-[9px] font-[900] uppercase tracking-wide text-[#06539B]"
                    >
                      {existingDraft.status === 'PROPOSED' ? L(NBA12_COPY.badgeProposed) : L(NBA12_COPY.badgeDraft)}
                    </span>
                  )}
                </div>

                {/* Proposed vs scheduled — distinct sources, never conflated. */}
                <p data-testid="draft-schedule-count" className="text-[11px] font-[800] text-[#1C1A17]">
                  {countLine}
                </p>

                {/* The hard no-effect statement + review/real reminders. */}
                <p data-testid="draft-schedule-no-effect" className="text-[10px] font-[700] text-[#6B655C]">
                  {L(NBA12_COPY.noEffect)}
                </p>
                <p className="text-[10px] font-[700] text-[#6B655C]">{L(NBA12_COPY.reviewConfirm)}</p>
                <p className="text-[10px] font-[700] text-[#6B655C]">{L(NBA12_COPY.notDueNotPaid)}</p>

                {canDerive || existingDraft ? (
                  <>
                    {/* Proposed rows — clearly labeled, never "scheduled payments". */}
                    <div data-testid="draft-schedule-rows" className="space-y-1.5">
                      <p className="text-[9px] font-[900] uppercase tracking-wide text-[#9E9890]">
                        {L(NBA12_COPY.proposedHeading)}
                      </p>
                      {renderRows.map((row) => (
                        <div
                          key={row.key}
                          className="flex items-center justify-between gap-2 rounded-lg bg-white border border-[#E6EDF5] px-2.5 py-1.5"
                        >
                          <div className="min-w-0">
                            <span className="text-[11px] font-[800] text-[#1C1A17]">{row.label}</span>
                            {triggerLabel(row.trigger) && (
                              <span className="ml-1.5 text-[9px] font-[700] text-[#9E9890]">
                                · {triggerLabel(row.trigger)}
                              </span>
                            )}
                            <span className="block text-[9px] font-[700] text-[#B07A00]">
                              {L(NBA12_COPY.dueDateMissing)}
                            </span>
                          </div>
                          <div className="text-right shrink-0">
                            {typeof row.pct === 'number' && (
                              <span className="block text-[9px] font-[700] text-[#9E9890]">{row.pct}%</span>
                            )}
                            <span className="text-[11px] font-[900] text-[#1C1A17]">
                              {row.amount === null ? '—' : formatMoney(row.amount, { isEs }).displayValue}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Due dates are never inferred — the user must add them. */}
                    <p className="text-[10px] font-[700] text-[#9E6B00]">{L(NBA12_COPY.addDueDates)}</p>

                    {/* Creation controls — only when no draft exists yet. */}
                    {!existingDraft && (
                      <div className="space-y-2">
                        {amountFlagged && (
                          <label
                            data-testid="draft-schedule-ack-suspicious"
                            className="flex items-start gap-2 text-[10px] font-[700] text-[#1C1A17] cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={draftAckSuspicious}
                              onChange={(e) => setDraftAckSuspicious(e.target.checked)}
                              className="mt-0.5"
                            />
                            <span>{L(NBA12_COPY.ackSuspicious)}</span>
                          </label>
                        )}
                        {termsFlagged && (
                          <label
                            data-testid="draft-schedule-ack-terms"
                            className="flex items-start gap-2 text-[10px] font-[700] text-[#1C1A17] cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={draftAckTerms}
                              onChange={(e) => setDraftAckTerms(e.target.checked)}
                              className="mt-0.5"
                            />
                            <span>{L(NBA12_COPY.ackTerms)}</span>
                          </label>
                        )}
                        {draftError && (
                          <div
                            data-testid="draft-schedule-error"
                            className="text-[10px] font-[700] text-red-600 bg-red-50 rounded-lg px-2.5 py-1.5"
                          >
                            {draftError}
                          </div>
                        )}
                        <button
                          type="button"
                          data-testid="draft-schedule-create-btn"
                          disabled={!acksSatisfied || creatingDraft}
                          onClick={() => createDraftSchedule(amountFlagged, termsFlagged)}
                          className="w-full rounded-lg border border-[#06539B] bg-[#06539B] px-3 py-2 text-[11px] font-[900] text-white disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {creatingDraft ? L(NBA12_COPY.creating) : L(NBA12_COPY.createButton)}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  // Terms do not parse into a safe schedule — no guessing, no empty
                  // draft. The user reviews the payment terms manually.
                  <p data-testid="draft-schedule-cannot-derive" className="text-[10px] font-[700] text-[#9E6B00]">
                    {L(NBA12_COPY.cannotDerive)}
                  </p>
                )}
              </div>
            )
          })()}

          {/* ── NBA-13A: Draft Schedule Review/Edit + Schedule Readiness Guard ──
              Shown after the NBA-12A draft panel, ONLY once an active draft
              exists. The user reviews/edits draft-only fields (milestone, due
              date, notes) and sees a readiness guard listing exactly what is
              still required before real scheduling can ever happen. Editing
              PATCHes ONLY the isolated draft line — it NEVER creates a real
              Payment, marks anything due/paid, or touches any downstream module.
              The "Schedule payments" button is permanently disabled. ── */}
          {!isDemo && canShowDraftSchedule && (() => {
            const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
            const existingDraft =
              Array.isArray(contract.paymentScheduleDrafts) && contract.paymentScheduleDrafts.length > 0
                ? contract.paymentScheduleDrafts[0]
                : null
            // Review/edit only exists once a draft has been created.
            if (!existingDraft) return null

            const lines = existingDraft.lines
            const amt = formatMoney(contract.totalAmount, { isEs })
            const rawTerms = (contract as any).paymentTerms
            const paymentTermsText = typeof rawTerms === 'string' ? rawTerms.trim() : ''
            const preview = deriveProposedSchedule(paymentTermsText, contract.totalAmount)

            // proposed = draft lines; scheduled = REAL Payment rows only. Never conflated.
            const proposedCount = lines.length
            const scheduledCount = Array.isArray(contract.payments) ? contract.payments.length : 0
            const countLine = L(NBA12_COPY.countSummary)
              .replace('{p}', String(proposedCount))
              .replace('{s}', String(scheduledCount))

            const amountFlagged = amt.isSuspicious || !amt.isValid
            const termsFlagged = preview.hasFreeText || preview.hasAmbiguousClauses

            // Readiness blockers — WHY real scheduling is not available yet. The
            // last three are always-on architectural blockers, so the guard can
            // NEVER be satisfied in NBA-13A and the Schedule button stays disabled.
            const blockers: { testid: string; copy: { en: string; es: string } }[] = []
            if (lines.some((l) => !l.dueDate))
              blockers.push({ testid: 'schedule-block-due-dates', copy: NBA13_COPY.blockMissingDueDate })
            if (lines.some((l) => !(typeof l.milestoneLabel === 'string' && l.milestoneLabel.trim().length > 0)))
              blockers.push({ testid: 'schedule-block-milestone', copy: NBA13_COPY.blockMilestone })
            // PR-F4G: blocker evaluates the normalized amount (amountMoney-aware).
            if (lines.some((l) => { const n = draftLineAmount(l); return !(n !== null && n > 0) }))
              blockers.push({ testid: 'schedule-block-amount', copy: NBA13_COPY.blockAmount })
            if (amountFlagged)
              blockers.push({ testid: 'schedule-block-suspicious', copy: NBA13_COPY.blockSuspiciousAck })
            if (termsFlagged)
              blockers.push({ testid: 'schedule-block-terms', copy: NBA13_COPY.blockAmbiguousAck })
            blockers.push({ testid: 'schedule-block-status', copy: NBA13_COPY.blockScheduledStatus })
            blockers.push({ testid: 'schedule-block-provenance', copy: NBA13_COPY.blockProvenance })
            blockers.push({ testid: 'schedule-block-endpoint', copy: NBA13_COPY.blockEndpoint })

            return (
              <div
                data-testid="draft-schedule-review"
                className="mt-3 rounded-xl border border-[#CBD9E8] bg-[#F4F8FC] px-3 py-3 space-y-2.5"
              >
                <div className="flex items-center gap-1.5 text-[11px] font-[900] text-[#1C1A17]">
                  <DollarSign size={13} className="text-[#06539B]" />
                  {L(NBA13_COPY.reviewTitle)}
                </div>

                {/* Proposed vs scheduled — distinct sources, never conflated. */}
                <p data-testid="review-schedule-count" className="text-[11px] font-[800] text-[#1C1A17]">
                  {countLine}
                </p>
                <p data-testid="review-still-draft" className="text-[10px] font-[700] text-[#6B655C]">
                  {L(NBA13_COPY.stillDraft)}
                </p>
                <p className="text-[10px] font-[700] text-[#6B655C]">{L(NBA13_COPY.reviewMilestones)}</p>

                {/* Editable draft rows — milestone / due date / notes (draft only). */}
                <div data-testid="review-schedule-rows" className="space-y-2">
                  {lines.map((line) => {
                    const edit = lineEdits[line.id] ?? {
                      milestoneLabel: line.milestoneLabel ?? '',
                      // NBA-13F: timezone-safe YYYY-MM-DD seed from the persisted
                      // (ISO-datetime) dueDate the server returns — never shifts a day.
                      dueDate: toDateInputValue(line.dueDate),
                      notes: line.notes ?? '',
                    }
                    // Merge onto the FRESHEST stored state (prev[line.id]), not the
                    // render-snapshot `edit`, so interleaved field edits never write
                    // back a stale buffer.
                    const update = (patch: Partial<typeof edit>) =>
                      setLineEdits((prev) => ({
                        ...prev,
                        [line.id]: { ...(prev[line.id] ?? edit), ...patch },
                      }))
                    // PR-F4G: amountMoney-aware; invalid renders "—", never $0.
                    const reviewLineAmount = draftLineAmount(line)
                    const amountText =
                      reviewLineAmount !== null
                        ? formatMoney(reviewLineAmount, { isEs }).displayValue
                        : '—'
                    return (
                      <div
                        key={line.id}
                        data-testid={`review-line-${line.id}`}
                        className="rounded-lg bg-white border border-[#E6EDF5] px-2.5 py-2 space-y-1.5"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          <label className="block">
                            <span className="block text-[9px] font-[800] uppercase tracking-wide text-[#9E9890]">
                              {L(NBA13_COPY.milestoneLabelLabel)}
                            </span>
                            <input
                              type="text"
                              data-testid={`review-line-milestone-${line.id}`}
                              value={edit.milestoneLabel}
                              onChange={(e) => update({ milestoneLabel: e.target.value })}
                              className="w-full rounded-md border border-[#D6CFC4] px-2 py-1 text-[11px] font-[700] text-[#1C1A17]"
                            />
                          </label>
                          <label className="block">
                            <span className="block text-[9px] font-[800] uppercase tracking-wide text-[#9E9890]">
                              {L(NBA13_COPY.dueDateLabel)}
                            </span>
                            <input
                              type="date"
                              data-testid={`review-line-duedate-${line.id}`}
                              ref={(el) => {
                                dueDateInputRefs.current[line.id] = el
                              }}
                              value={edit.dueDate}
                              onChange={(e) => update({ dueDate: e.target.value })}
                              // Mirror the native value into state on blur too, so a
                              // typed date that never fired `change` is still captured
                              // before the Save click moves focus away.
                              onBlur={(e) => update({ dueDate: e.target.value })}
                              className="w-full rounded-md border border-[#D6CFC4] px-2 py-1 text-[11px] font-[700] text-[#1C1A17]"
                            />
                          </label>
                        </div>
                        <label className="block">
                          <span className="block text-[9px] font-[800] uppercase tracking-wide text-[#9E9890]">
                            {L(NBA13_COPY.notesLabel)}
                          </span>
                          <input
                            type="text"
                            data-testid={`review-line-notes-${line.id}`}
                            value={edit.notes}
                            placeholder={L(NBA13_COPY.notesPlaceholder)}
                            onChange={(e) => update({ notes: e.target.value })}
                            className="w-full rounded-md border border-[#D6CFC4] px-2 py-1 text-[11px] font-[700] text-[#1C1A17]"
                          />
                        </label>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-[900] text-[#1C1A17]">{amountText}</span>
                          <button
                            type="button"
                            data-testid={`review-line-save-${line.id}`}
                            disabled={savingLineId === line.id}
                            onClick={() => {
                              // Read the date from the live DOM input — the user's
                              // real entry — rather than `edit.dueDate`, which can lag
                              // when the native date input's change event hasn't
                              // committed yet. Falls back to the controlled state.
                              const domDue = dueDateInputRefs.current[line.id]?.value
                              const dueDate =
                                typeof domDue === 'string' && domDue.length > 0
                                  ? domDue
                                  : edit.dueDate
                                    ? edit.dueDate
                                    : null
                              saveDraftLine(existingDraft.id, line.id, {
                                milestoneLabel: edit.milestoneLabel.trim(),
                                dueDate,
                                notes: edit.notes.trim() ? edit.notes.trim() : null,
                              })
                            }}
                            className="rounded-md border border-[#06539B] bg-[#06539B] px-3 py-1 text-[10px] font-[900] text-white disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {savingLineId === line.id ? L(NBA13_COPY.savingLine) : L(NBA13_COPY.saveLine)}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {lineEditError && (
                  <div
                    data-testid="review-line-error"
                    className="text-[10px] font-[700] text-red-600 bg-red-50 rounded-lg px-2.5 py-1.5"
                  >
                    {lineEditError}
                  </div>
                )}

                {/* ── Schedule payments readiness guard ── */}
                <div
                  data-testid="schedule-payments-readiness"
                  className="mt-1 rounded-lg border border-[#E3D9C4] bg-[#FBF7EE] px-2.5 py-2 space-y-1.5"
                >
                  <p className="text-[11px] font-[900] text-[#1C1A17]">{L(NBA13_COPY.readinessTitle)}</p>
                  <p className="text-[10px] font-[800] text-[#9E6B00]">
                    {lines.some((l) => !l.dueDate)
                      ? L(NBA13_COPY.addDueDatesMissing)
                      : L(NBA13_COPY.preflightAvailable)}
                  </p>
                  <p className="text-[10px] font-[800] text-[#9E6B00]">{L(NBA13_COPY.schedulingNotEnabled)}</p>

                  <div className="space-y-1">
                    <p className="text-[9px] font-[900] uppercase tracking-wide text-[#9E9890]">
                      {L(NBA13_COPY.blockersHeading)}
                    </p>
                    {blockers.map((b) => (
                      <p
                        key={b.testid}
                        data-testid={b.testid}
                        className="text-[10px] font-[700] text-[#6B655C]"
                      >
                        • {L(b.copy)}
                      </p>
                    ))}
                  </div>

                  {/* Hard no-effect statement for the readiness guard. */}
                  <p data-testid="schedule-no-effect" className="text-[10px] font-[700] text-[#6B655C]">
                    {L(NBA13_COPY.noEffect)}
                  </p>

                  {/* ── NBA-13E: Preflight action (enabled, READ-ONLY) ──
                      Runs a server-validated preflight that creates NOTHING and
                      returns a safe confirmation summary. Replaces the permanent
                      "not available yet" dead-end with a reviewable step. */}
                  <button
                    type="button"
                    data-testid="schedule-payments-preflight-btn"
                    disabled={preflightLoading}
                    onClick={() => runSchedulePreflight(existingDraft.id)}
                    className="w-full rounded-lg border border-[#06539B] bg-[#06539B] px-3 py-2 text-[11px] font-[900] text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {preflightLoading ? L(NBA13E_COPY.reviewingBtn) : L(NBA13E_COPY.reviewBeforeBtn)}
                  </button>

                  {preflightError && (
                    <div
                      data-testid="schedule-preflight-error"
                      className="text-[10px] font-[700] text-red-600 bg-red-50 rounded-lg px-2.5 py-1.5"
                    >
                      {preflightError}
                    </div>
                  )}

                  {/* Preflight confirmation summary — shown only after the user runs
                      the review. Renders the PR #370 banner, the no-write
                      reassurance, a safe summary + line previews, and any blockers
                      / warnings. The final scheduling action below stays disabled. */}
                  {preflightResult && (
                    <div
                      data-testid="schedule-preflight-summary"
                      className="mt-1 rounded-lg border border-[#CBD9E8] bg-white px-2.5 py-2 space-y-2"
                    >
                      <p className="text-[11px] font-[900] text-[#1C1A17]">
                        {L(NBA13E_COPY.confirmTitle)}
                      </p>

                      {/* PR #370 accepted banner (verbatim EN/ES). */}
                      <p
                        data-testid="schedule-preflight-banner"
                        className="text-[10px] font-[700] text-[#06539B] bg-[#EEF5FC] rounded-md px-2 py-1.5"
                      >
                        {L(NBA13E_COPY.banner)}
                      </p>

                      {/* No-write reassurance. */}
                      <p
                        data-testid="schedule-preflight-no-write"
                        className="text-[10px] font-[800] text-[#6B655C]"
                      >
                        {L(NBA13E_COPY.noWrite)}
                      </p>

                      {/* Pass / blocked heading. */}
                      <p
                        data-testid="schedule-preflight-status"
                        className={
                          preflightResult.ready
                            ? 'text-[10px] font-[900] text-[#1F7A3D]'
                            : 'text-[10px] font-[900] text-[#9E6B00]'
                        }
                      >
                        {preflightResult.ready
                          ? L(NBA13E_COPY.readyHeading)
                          : L(NBA13E_COPY.blockedHeading)}
                      </p>

                      {/* Safe summary + line previews. */}
                      <div data-testid="schedule-preflight-lines" className="space-y-1">
                        <p className="text-[9px] font-[900] uppercase tracking-wide text-[#9E9890]">
                          {L(NBA13E_COPY.summaryHeading)}
                        </p>
                        {preflightResult.lines.map((pl) => {
                          // PR-F4G: amountMoney-aware; invalid renders "—", never $0.
                          const preflightLineAmount = draftLineAmount(pl)
                          const amountText =
                            preflightLineAmount !== null
                              ? formatMoney(preflightLineAmount, { isEs }).displayValue
                              : '—'
                          const dueText = pl.dueDate ? String(pl.dueDate).slice(0, 10) : '—'
                          return (
                            <div
                              key={pl.id}
                              data-testid={`schedule-preflight-line-${pl.id}`}
                              className="flex items-center justify-between gap-2 text-[10px] font-[700] text-[#1C1A17]"
                            >
                              <span className="truncate">{pl.milestoneLabel || '—'}</span>
                              <span className="shrink-0 text-[#6B655C]">{dueText}</span>
                              <span className="shrink-0 font-[900]">{amountText}</span>
                            </div>
                          )
                        })}
                      </div>

                      {/* Blockers (if any) — all returned together by the server. */}
                      {preflightResult.blockers.length > 0 && (
                        <div data-testid="schedule-preflight-blockers" className="space-y-0.5">
                          <p className="text-[9px] font-[900] uppercase tracking-wide text-[#9E6B00]">
                            {L(NBA13E_COPY.blockersHeading)}
                          </p>
                          {preflightResult.blockers.map((b, i) => (
                            <p
                              key={`${b.code}-${b.lineId ?? i}`}
                              data-testid={`schedule-preflight-blocker-${b.code}`}
                              className="text-[10px] font-[700] text-[#6B655C]"
                            >
                              • {localizePreflightCode(b.code, isEs)}
                            </p>
                          ))}
                        </div>
                      )}

                      {/* Warnings — always present (preflight-only / no real endpoint). */}
                      {preflightResult.warnings.length > 0 && (
                        <div data-testid="schedule-preflight-warnings" className="space-y-0.5">
                          <p className="text-[9px] font-[900] uppercase tracking-wide text-[#9E9890]">
                            {L(NBA13E_COPY.warningsHeading)}
                          </p>
                          {preflightResult.warnings.map((w) => (
                            <p
                              key={w.code}
                              data-testid={`schedule-preflight-warning-${w.code}`}
                              className="text-[10px] font-[700] text-[#6B655C]"
                            >
                              • {localizePreflightCode(w.code, isEs)}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* CREATE-FLOW-4: the REAL "Schedule payments" action is offered
                      ONLY when the backend readiness probe allows THIS user+project
                      (global flag ON *and* scoped-allowlisted) AND the read-only
                      preflight has passed. In every other case — flag OFF, project
                      not on the allowlist, or preflight not run/failed — the disabled
                      "coming next" button stays. Production and any non-allowlisted
                      project can never create real payments from here. The create
                      fetch itself lives in `runScheduleCreate` (outside this panel);
                      this block only renders the gated button / confirmation. */}
                  {scheduleReadiness?.canCreate ? (
                    <div data-testid="schedule-payments-create" className="space-y-2">
                      {createResult?.type === 'success' ? (
                        <div
                          data-testid="schedule-payments-create-success"
                          className="rounded-lg border border-[#B8E0C2] bg-[#F0FAF3] px-3 py-2 text-[10px] font-[800] text-[#1F7A3D]"
                        >
                          {L(CF4_CREATE_COPY.successTitle)}
                          {' — '}
                          {createResult.reused
                            ? L(CF4_CREATE_COPY.reused)
                            : isEs
                              ? `${createResult.count} pago(s) programado(s).`
                              : `${createResult.count} payment(s) scheduled.`}
                        </div>
                      ) : createConfirmOpen ? (
                        <div
                          data-testid="schedule-payments-confirm"
                          className="rounded-lg border border-[#CBD9E8] bg-white px-3 py-2 space-y-2"
                        >
                          <p className="text-[11px] font-[900] text-[#1C1A17]">
                            {L(CF4_CREATE_COPY.confirmTitle)}
                          </p>
                          <p className="text-[10px] font-[700] text-[#6B655C] leading-relaxed">
                            {L(CF4_CREATE_COPY.confirmBody)}
                          </p>
                          {createResult?.type === 'error' && (
                            <p
                              data-testid="schedule-payments-create-error"
                              className="text-[10px] font-[800] text-[#B4232A]"
                            >
                              {createResult.message}
                            </p>
                          )}
                          <div className="flex flex-col sm:flex-row gap-2">
                            <button
                              type="button"
                              disabled={createSubmitting}
                              data-testid="schedule-payments-confirm-btn"
                              onClick={() => runScheduleCreate(existingDraft.id)}
                              className="w-full rounded-lg border border-[#06539B] bg-[#06539B] px-3 py-2 text-[11px] font-[800] text-white disabled:opacity-60"
                            >
                              {createSubmitting ? L(CF4_CREATE_COPY.schedulingBtn) : L(CF4_CREATE_COPY.confirmCta)}
                            </button>
                            <button
                              type="button"
                              disabled={createSubmitting}
                              data-testid="schedule-payments-cancel-btn"
                              onClick={() => {
                                setCreateConfirmOpen(false)
                                setCreateResult(null)
                              }}
                              className="w-full rounded-lg border border-[#D6CFC4] bg-white px-3 py-2 text-[11px] font-[800] text-[#6B655C]"
                            >
                              {L(CF4_CREATE_COPY.cancelCta)}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            disabled={preflightResult?.ready !== true}
                            data-testid="schedule-payments-create-btn"
                            onClick={openScheduleConfirm}
                            className="w-full rounded-lg border border-[#06539B] bg-[#06539B] px-3 py-2 text-[11px] font-[800] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {L(CF4_CREATE_COPY.scheduleBtn)}
                          </button>
                          {preflightResult?.ready !== true && (
                            <p className="text-[10px] font-[700] text-[#9E6B00]">
                              {L(CF4_CREATE_COPY.runReviewFirst)}
                            </p>
                          )}
                          {createResult?.type === 'error' && (
                            <p
                              data-testid="schedule-payments-create-error"
                              className="text-[10px] font-[800] text-[#B4232A]"
                            >
                              {createResult.message}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Scoped-out note: the flow exists (global flag ON) but this
                          project/user is not on the CF-4 allowlist yet. */}
                      {scheduleReadiness?.enabled === true && scheduleReadiness?.allowedForScope === false && (
                        <p
                          data-testid="schedule-payments-scope-note"
                          className="text-[10px] font-[700] text-[#9E6B00]"
                        >
                          {L(CF4_CREATE_COPY.notAllowedForScope)}
                        </p>
                      )}
                      {/* Final scheduling action — DISABLED. No onClick handler, so
                          no real payment can ever be created here. Real scheduling is
                          "coming next" until this project is scoped-enabled. */}
                      <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        data-testid="schedule-payments-disabled-btn"
                        className="w-full rounded-lg border border-[#D6CFC4] bg-[#ECEAE4] px-3 py-2 text-[11px] font-[800] text-[#9E9890] cursor-not-allowed"
                      >
                        {L(NBA13E_COPY.finalComingNext)}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* Builder / owner countersign modal. Mounted at the card
          level so click events don't toggle the card expand/collapse
          handler on the outer wrapper. */}
      {showBuilderSignModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={isEs ? 'Firmar como builder' : 'Sign as builder'}
          onClick={() => !countersigning && setShowBuilderSignModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-xl border border-[#ECEAE4] w-full max-w-md p-5 space-y-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-[15px] font-[900] text-[#1C1A17]">
                  {isEs ? 'Firmar como builder' : 'Sign as builder'}
                </h3>
                <p className="mt-1 text-[11px] font-[600] text-[#9E9890]">
                  {contract.contractorName} · {isEs && contract.tradeEs ? contract.tradeEs : contract.trade}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowBuilderSignModal(false)}
                disabled={countersigning}
                className="text-[#9E9890] hover:text-[#1C1A17] disabled:opacity-40"
                aria-label={isEs ? 'Cerrar' : 'Close'}
              >
                <X size={18} />
              </button>
            </div>

            <div>
              <label className="text-[11px] font-[800] text-[#9E9890] block mb-1 uppercase tracking-[0.4px]">
                {isEs ? 'Nombre legal del builder' : 'Legal name of builder'}
              </label>
              <input
                value={builderSignerName}
                onChange={(e) => setBuilderSignerName(e.target.value)}
                className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
              />
            </div>

            <div>
              <label className="text-[11px] font-[800] text-[#9E9890] block mb-1 uppercase tracking-[0.4px]">
                {isEs ? 'Firma (dibuje abajo)' : 'Signature (draw below)'}
              </label>
              <SignaturePad onSignature={setBuilderSignatureData} />
            </div>

            <label className="flex items-start gap-2 text-[11px] font-[600] text-[#1C1A17] cursor-pointer">
              <input
                type="checkbox"
                checked={builderConfirm}
                onChange={(e) => setBuilderConfirm(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                {isEs
                  ? 'He revisado este contrato y autorizo mi firma.'
                  : 'I have reviewed this contract and authorize my signature.'}
              </span>
            </label>

            {countersignError && (
              <div className="text-[10px] font-[700] text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {countersignError}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowBuilderSignModal(false)}
                disabled={countersigning}
                className="flex-1 border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#9E9890] hover:bg-[#FAFAF8] disabled:opacity-40"
              >
                {isEs ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={submitCountersign}
                disabled={
                  !builderSignerName.trim() ||
                  !builderSignatureData ||
                  !builderConfirm ||
                  countersigning
                }
                className="flex-1 flex items-center justify-center gap-2 bg-[#06539B] hover:bg-[#14457E] text-white rounded-full py-2 text-[11px] font-[800] disabled:opacity-40"
              >
                {countersigning ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <CheckCircle size={14} />
                )}
                {countersigning
                  ? (isEs ? 'Firmando…' : 'Signing…')
                  : (isEs ? 'Completar contrato' : 'Complete contract')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── NEW CONTRACT FORM ─────────────────────────────

// ── CONTRACT REVIEW FLOW ─────────────────────────

type TermChoice = 'theirs' | 'yours' | 'custom'

interface ReviewedTerms {
  contractorName?: string
  trade?: string
  totalAmount?: number
  scopeOfWork?: string
  paymentTerms?: { amount?: number; schedule?: string; retainage?: string; deposit?: string }
  timeline?: { startDate?: string; duration?: string; completionDate?: string }
  penalties?: { lateCompletion?: string; defects?: string }
  insurance?: { required?: boolean; types?: string }
  warranty?: { duration?: string; coverage?: string }
  lienWaiver?: { type?: string; timing?: string }
  specialConditions?: string[]
  redFlags?: string[]
  confidence?: number
}

// Labels live in `contractsModule.terms.{key}` i18n namespace — keep this
// array as a stable key list so the order is fixed and the UI just maps
// keys. The legacy comment pointed to `contracts.terms.*` but that path
// does not exist in either locale (top-level `contracts` is a flat string,
// not a nested dict); the actual labels are nested under `contractsModule`
// in both en.json and es.json. Fixed alongside the call site to keep the
// pin in i18nCoverage.smoke.ts trivially happy.
const TERM_SECTIONS = [
  { key: 'scopeOfWork' },
  { key: 'paymentTerms' },
  { key: 'timeline' },
  { key: 'penalties' },
  { key: 'insurance' },
  { key: 'warranty' },
  { key: 'lienWaiver' },
] as const

function formatTermValue(val: any): string {
  if (!val) return '—'
  if (typeof val === 'string') return val
  if (typeof val === 'object') return Object.entries(val).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join('\n')
  return String(val)
}

// Phase 1 (no Prisma persistence): infer a contract's document language from its
// stored clause text so send-for-signature and the PDF reflect the FINAL contract
// language rather than the builder's transient app UI language. Spanish markers
// (accents, ñ, ¿¡, high-frequency ES legal words) → 'es'; otherwise 'en'. This is
// detection only — it never translates or rewrites clause text.
function detectClauseLanguage(...texts: Array<string | null | undefined>): 'en' | 'es' {
  const text = texts.filter(Boolean).join(' ').toLowerCase()
  if (!text.trim()) return 'en'
  if (/[áéíóúñ¿¡]|\b(el|la|los|las|del|para|con|según|días|garantía|materiales|propietario|contratista|trabajo|cláusula|pago)\b/.test(text)) return 'es'
  return 'en'
}

// Per-clause coherence: classify EACH non-empty clause body and report whether
// the contract is a single coherent language or mixes EN/ES. Used to BLOCK
// sending a mixed-language contract for signature. Detection only — it never
// translates or rewrites clause text. 'empty' = no clause bodies to judge.
function contractClauseCoherence(...texts: Array<string | null | undefined>): 'en' | 'es' | 'mixed' | 'empty' {
  const langs = texts
    .filter((t): t is string => !!t && t.trim() !== '')
    .map(t => detectClauseLanguage(t))
  if (langs.length === 0) return 'empty'
  const hasEs = langs.includes('es')
  const hasEn = langs.includes('en')
  if (hasEs && hasEn) return 'mixed'
  return hasEs ? 'es' : 'en'
}

// Shared bilingual message shown when a mixed-language contract is blocked.
// The copy guides the builder toward the (future) reviewed final-language
// workflow — it must NOT imply the builder has to personally rewrite the
// clauses in a language they may not speak. The "prepare a reviewed
// final-language version" path is a planned feature; until it ships the gate
// keeps a mixed contract from being created or sent. No silent translation.
function mixedLanguageBlockMessage(isEs: boolean): string {
  return isEs
    ? 'Este contrato mezcla cláusulas en inglés y español. Use un solo idioma antes de crear o enviar el contrato, o prepare una versión final revisada en el idioma seleccionado.'
    : 'This contract mixes English and Spanish clauses. Use one language before creating or sending the contract, or prepare a reviewed final-language version.'
}

function ContractReviewFlow({ projectId, onClose, onCreated }: {
  projectId: string; onClose: () => void; onCreated: () => void
}) {
  const { getToken } = useAuth()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const fileRef = useRef<HTMLInputElement>(null)

  const [uploading, setUploading] = useState(false)
  const [extracted, setExtracted] = useState<ReviewedTerms | null>(null)
  const [contractorPdfUrl, setContractorPdfUrl] = useState<string | null>(null)
  const [choices, setChoices] = useState<Record<string, TermChoice>>({})
  const [customValues, setCustomValues] = useState<Record<string, string>>({})
  // Bilingual TX-template defaults for the "Your Terms (TX Template)"
  // column. The previous implementation baked the language into a
  // useState lazy initializer that only ran once at mount, so a user
  // who switched to Spanish (or English) after the modal opened would
  // continue to see the original-language boilerplate. We now compute
  // the defaults via useMemo keyed on `isEs`, and keep user edits in
  // a separate `yourValueOverrides` map so a language switch refreshes
  // only the untouched fields. The user's overrides survive language
  // toggles untouched — exactly the same intent as the legacy code,
  // minus the staleness bug.
  const defaultYourValues = useMemo<Record<string, string>>(() => (isEs ? {
    scopeOfWork: TX_SCOPE_TEMPLATE_SHORT_ES,
    paymentTerms: TX_PAYMENT_TEMPLATE_SHORT_ES,
    timeline: 'Iniciar dentro de 5 días hábiles del contrato firmado. Duración: 30-45 días según el oficio. Conforme a Texas Property Code.',
    penalties: TX_PENALTIES_TEMPLATE_SHORT_ES,
    insurance: 'GL $1M por ocurrencia, $2M agregado. WC según ley de TX. Auto $1M. Builder como asegurado adicional. COI requerido antes de iniciar el trabajo.',
    warranty: 'Garantía de 1 año sobre mano de obra desde la terminación sustancial. Materiales según garantía del fabricante. Defectos reportados dentro de 30 días deben corregirse.',
    lienWaiver: 'Renuncia de gravamen condicional con cada solicitud de pago. Renuncia final incondicional con el pago final. Conforme a Texas Property Code Capítulo 53.',
  } : {
    scopeOfWork: TX_SCOPE_TEMPLATE_SHORT,
    paymentTerms: TX_PAYMENT_TEMPLATE_SHORT,
    timeline: 'Start within 5 business days of signed contract. Duration: 30-45 days depending on trade. Per Texas Property Code.',
    penalties: TX_PENALTIES_TEMPLATE_SHORT,
    insurance: 'GL $1M per occurrence, $2M aggregate. WC as required by TX law. Auto $1M. Builder as additional insured. COI required before work begins.',
    warranty: '1 year labor warranty from substantial completion. Materials per manufacturer warranty. Defects reported within 30 days must be corrected.',
    lienWaiver: 'Conditional lien waiver with each draw request. Unconditional final waiver with final payment. Per Texas Property Code Chapter 53.',
  }), [isEs])
  const [yourValueOverrides, setYourValueOverrides] = useState<Record<string, string>>({})
  const yourValues = useMemo<Record<string, string>>(() => {
    // Effective values: user override wins per-key; otherwise the
    // current-language default. Edits set the override; switching
    // language updates only keys the user has not edited.
    const merged: Record<string, string> = { ...defaultYourValues }
    for (const k of Object.keys(yourValueOverrides)) {
      merged[k] = yourValueOverrides[k]
    }
    return merged
  }, [defaultYourValues, yourValueOverrides])
  const setYourValues = (updater: (prev: Record<string, string>) => Record<string, string>) => {
    // Adapter so the textarea onChange continues to read like a
    // setState updater. We translate the next state into a diff and
    // record only the changed keys into the overrides map.
    setYourValueOverrides((prevOverrides) => {
      const prevEffective: Record<string, string> = { ...defaultYourValues, ...prevOverrides }
      const nextEffective = updater(prevEffective)
      const nextOverrides: Record<string, string> = { ...prevOverrides }
      for (const k of Object.keys(nextEffective)) {
        if (nextEffective[k] !== defaultYourValues[k]) {
          nextOverrides[k] = nextEffective[k]
        }
      }
      return nextOverrides
    })
  }
  const [creating, setCreating] = useState(false)
  // PR3 (C6): when the AI review finds no total, the save used to send 0 →
  // 400, swallowed (console only). Ask for the amount and show errors.
  const [amountOverride, setAmountOverride] = useState('')
  const [generateError, setGenerateError] = useState<string | null>(null)
  const [sendEmail, setSendEmail] = useState('')
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [createdContractId, setCreatedContractId] = useState<string | null>(null)
  // Live coherence of the currently-selected merged terms (mirrors the merge in
  // handleGenerate for the body clauses) — gates "Generate Final Contract" so a
  // mixed EN/ES final contract can't be produced. Detection only; no translation.
  const reviewMergedCoherence = useMemo<'en' | 'es' | 'mixed' | 'empty'>(() => {
    if (!extracted) return 'empty'
    const mergedVal = (key: string) => {
      const choice = choices[key] || 'yours'
      if (choice === 'theirs') return formatTermValue((extracted as any)[key])
      if (choice === 'custom') return customValues[key] || ''
      return yourValues[key] || ''
    }
    return contractClauseCoherence(mergedVal('scopeOfWork'), mergedVal('paymentTerms'), mergedVal('penalties'))
  }, [extracted, choices, customValues, yourValues])
  // Final document language of the contract just created from this review,
  // detected from the merged clause text — drives the signing-link language.
  const [sendLang, setSendLang] = useState<'en' | 'es'>('en')
  // Mixed EN/ES merged terms must not be sent for signature (coherence gate).
  const [sendCoherence, setSendCoherence] = useState<'en' | 'es' | 'mixed' | 'empty'>('en')

  // Codex PR3 [P1]: la revisión IA ya no falla en silencio.
  const [uploadError, setUploadError] = useState<string | null>(null)
  const reviewUploadErrorText = (status: number | null): string => {
    if (status === 415) return isEs ? 'Ese tipo de archivo no se puede revisar. Sube un PDF, JPG o PNG.' : 'That file type can’t be reviewed. Upload a PDF, JPG or PNG.'
    if (status === 413) return isEs ? 'El archivo es demasiado grande. Prueba con un PDF más ligero.' : 'The file is too large. Try a smaller PDF.'
    if (status === 402) return isEs ? 'La revisión con IA no está incluida en tu plan actual.' : 'AI review isn’t included in your current plan.'
    if (status === 429) return isEs ? 'Demasiadas revisiones seguidas. Espera un momento e intenta de nuevo.' : 'Too many reviews in a row. Wait a moment and try again.'
    if (status === null) return isEs ? 'No hay conexión. Revisa tu internet e intenta de nuevo.' : 'No connection. Check your internet and try again.'
    return isEs ? 'No se pudo revisar el contrato. Intenta de nuevo.' : 'The contract couldn’t be reviewed. Try again.'
  }

  const handleUpload = async (file: File) => {
    setUploading(true)
    setUploadError(null)
    let failedStatus: number | null | undefined
    try {
      const token = await getToken()
      const formData = new FormData()
      formData.append('file', file)
      // Tell the analyzer which language to write the platform-generated
      // redFlags analysis in. Source-document terms stay in their original
      // language (the backend keeps every other field verbatim).
      formData.append('language', i18n.language === 'es' ? 'es' : 'en')
      const res = await apiFetch(`/api/projects/${projectId}/contracts/review`, {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: formData,
      })
      if (!res.ok) { failedStatus = res.status; throw new Error('Review failed') }
      const data = await res.json()
      if (!data?.extractedTerms) { failedStatus = res.status; throw new Error('Review returned no terms') }
      setExtracted(data.extractedTerms)
      setContractorPdfUrl(data.contractorPdfUrl)
      // Default all to "yours"
      const defaults: Record<string, TermChoice> = {}
      TERM_SECTIONS.forEach(s => { defaults[s.key] = 'yours' })
      setChoices(defaults)
    } catch (err) {
      console.error('Review error:', err)
      setUploadError(reviewUploadErrorText(failedStatus === undefined ? null : failedStatus))
    }
    setUploading(false)
  }

  // PR3 (C6): no total found by the AI and none typed → ask, don't send 0.
  const stopIfNoReviewAmount = (): boolean => {
    setGenerateError(null)
    if (Number(extracted?.totalAmount) > 0 || Number(amountOverride) > 0) return false
    setGenerateError(isEs ? 'Escribe el monto total del contrato antes de generarlo.' : 'Enter the contract total before generating it.')
    setCreating(false)
    return true
  }

  const handleGenerate = async () => {
    if (!extracted) return
    // Hard finalization gate (defense-in-depth): never generate a final contract
    // from mixed EN/ES merged terms, even if the Generate button's disabled
    // attribute is bypassed. The bilingual block notice beside the button stays
    // visible while this holds. No translation, no body rewrite.
    if (reviewMergedCoherence === 'mixed') return
    setCreating(true)
    if (stopIfNoReviewAmount()) return
    try {
      const merged: Record<string, string> = {}
      TERM_SECTIONS.forEach(({ key }) => {
        const choice = choices[key] || 'yours'
        if (choice === 'theirs') merged[key] = formatTermValue((extracted as any)[key])
        else if (choice === 'custom') merged[key] = customValues[key] || ''
        else merged[key] = yourValues[key] || ''
      })

      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/contracts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          contractorName: extracted.contractorName || 'Unknown Contractor',
          trade: extracted.trade || 'General',
          totalAmount: Number(extracted.totalAmount) > 0 ? Number(extracted.totalAmount) : Number(amountOverride),
          scopeOfWork: merged.scopeOfWork,
          paymentTerms: merged.paymentTerms,
          penalties: merged.penalties,
          reviewedTerms: extracted,
          contractorPdfUrl,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || err.message || `Request failed (${res.status})`)
      }
      const contract = await res.json()
      setCreatedContractId(contract.id)
      setSendLang(detectClauseLanguage(merged.scopeOfWork, merged.paymentTerms, merged.penalties))
      setSendCoherence(contractClauseCoherence(merged.scopeOfWork, merged.paymentTerms, merged.penalties))
      setShowEmailForm(true)
    } catch (err: any) {
      console.error('Contract generate failed:', err?.message)
      setGenerateError(isEs ? 'No se pudo crear el contrato. Revisa el monto y los datos e intenta de nuevo.' : 'Could not create the contract. Check the amount and fields and try again.')
    }
    setCreating(false)
  }
  const reviewNeedsAmount = !!extracted && !(Number(extracted.totalAmount) > 0)

  const handleSendForSignature = async () => {
    if (!createdContractId || !sendEmail) return
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/contracts/${createdContractId}/send-for-signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        // Signing link language = the created contract's FINAL document language
        // (detected from the merged clauses at generate time), not the app UI.
        body: JSON.stringify({ email: sendEmail, language: sendLang }),
      })
      // PR3 (C6): a failed send used to close the dialog as if it was sent.
      if (!res.ok) throw new Error(`send_for_signature_failed_${res.status}`)
      onCreated()
    } catch {
      setGenerateError(isEs
        ? 'El contrato se guardó, pero NO se pudo enviar a firma. Envíalo desde la lista de contratos.'
        : 'The contract was saved, but it could NOT be sent for signature. Send it from the contracts list.')
    }
  }

  const inputClass = "w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
  const labelClass = "text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-1"

  // Upload state
  if (!extracted) {
    return (
      <div className="bg-white border border-[#ECEAE4] rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-[14px] font-[900] text-[#1C1A17]">{t("Review Contractor's Contract")}</h3>
          <button onClick={onClose} className="text-[11px] font-[800] text-[#9E9890]">{t('Cancel')}</button>
        </div>

        {uploading ? (
          <div className="text-center py-12">
            <Loader2 size={28} className="mx-auto text-[#06539B] animate-spin mb-3" />
            <p className="text-[12px] font-[700] text-[#9E9890]">{t('contractsModule.analyzing')}</p>
          </div>
        ) : (
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-[#ECEAE4] rounded-xl p-10 text-center cursor-pointer hover:border-[#06539B] hover:bg-[#F8F7FF] transition-all"
          >
            <Upload size={28} className="mx-auto text-[#9E9890] mb-3" />
            <p className="text-[13px] font-[800] text-[#1C1A17]">{t('contractsModule.uploadContractorContract')}</p>
            <p className="text-[10px] font-[600] text-[#9E9890] mt-1">PDF, JPG, PNG</p>
          </div>
        )}

        {uploadError && !uploading && (
          <p role="alert" data-testid="contract-review-upload-error" className="text-[12px] font-[700] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{uploadError}</p>
        )}

        <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = '' }} />
      </div>
    )
  }

  // Email send after creation
  if (showEmailForm && createdContractId) {
    return (
      <div className="bg-white border border-[#ECEAE4] rounded-xl p-5 space-y-4">
        <div className="text-center py-4">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
            <FileText size={20} className="text-green-600" />
          </div>
          <h3 className="text-[14px] font-[900] text-[#1C1A17]">{t('Contract Created')}</h3>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-1">{t('Send it for signature?')}</p>
        </div>
        {/* Coherence gate: the merged terms mix EN/ES — block send until the
            contract is one language (no translation). */}
        {sendCoherence === 'mixed' && (
          <div data-testid="contract-mixed-language-block" className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[11px] font-[700] text-red-700">
            {mixedLanguageBlockMessage(isEs)}
          </div>
        )}
        <input type="email" placeholder={t("Contractor's email")} value={sendEmail} onChange={e => setSendEmail(e.target.value)} className={inputClass} />
        <div className="flex gap-2">
          <button onClick={onCreated} className="flex-1 border border-[#ECEAE4] rounded-full py-2.5 text-[11px] font-[800] text-[#9E9890]">{t('Skip')}</button>
          <button onClick={handleSendForSignature} disabled={!sendEmail || sendCoherence === 'mixed'}
            className="flex-1 bg-[#F5A623] hover:bg-[#E6961A] text-white rounded-full py-2.5 text-[11px] font-[800] disabled:opacity-40 transition-colors">
            <Send size={12} className="inline mr-1" /> {t('Send for Signature')}
          </button>
        </div>
      </div>
    )
  }

  // Side-by-side comparison
  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[#ECEAE4] flex items-center justify-between">
        <div>
          <h3 className="text-[14px] font-[900] text-[#1C1A17]">{t('Contract Review')}</h3>
          <p className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
            {extracted.contractorName} — {extracted.trade} — {formatMoneyValue(extracted.totalAmount ?? null, isEs)}
          </p>
        </div>
        <button onClick={onClose} className="text-[11px] font-[800] text-[#9E9890]">{t('Cancel')}</button>
      </div>

      {/* Red flags */}
      {extracted.redFlags && extracted.redFlags.length > 0 && (
        <div className="px-5 py-3 bg-red-50 border-b border-red-100">
          <div className="flex items-center gap-1.5 text-[10px] font-[800] text-red-600 mb-1.5">
            <AlertTriangle size={12} /> {t('Red Flags')}
          </div>
          {extracted.redFlags.map((flag, i) => (
            <p key={i} className="text-[11px] font-[600] text-red-700 ml-5">• {flag}</p>
          ))}
        </div>
      )}

      {/* Term sections */}
      <div className="divide-y divide-[#ECEAE4] max-h-[60vh] overflow-y-auto">
        {TERM_SECTIONS.map(({ key }) => {
          const choice = choices[key] || 'yours'
          const theirValue = formatTermValue((extracted as any)[key])
          return (
            <div key={key} className="px-5 py-4">
              <div className="text-[11px] font-[800] text-[#1C1A17] mb-3">{t(`contractsModule.terms.${key}`)}</div>

              {/* Toggle */}
              <div className="flex gap-1 mb-3">
                {(['theirs', 'yours', 'custom'] as TermChoice[]).map(opt => (
                  <button key={opt} onClick={() => setChoices(p => ({ ...p, [key]: opt }))}
                    className={`px-3 py-1 rounded-full text-[9px] font-[800] transition-colors ${
                      choice === opt
                        ? opt === 'theirs' ? 'bg-amber-100 text-amber-700' : opt === 'yours' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        : 'bg-[#F2F0EB] text-[#9E9890]'
                    }`}>
                    {opt === 'theirs' ? t('Accept Theirs') : opt === 'yours' ? t('Use Yours') : t('Custom')}
                  </button>
                ))}
              </div>

              {/* Two columns */}
              <div className="grid grid-cols-2 gap-3">
                <div className={`rounded-lg p-3 text-[11px] font-[600] whitespace-pre-wrap ${choice === 'theirs' ? 'bg-amber-50 border border-amber-200' : 'bg-[#FAFAF8] border border-[#ECEAE4]'}`}>
                  <div className={labelClass}>{t("Contractor's Terms")}</div>
                  <p className="text-[#1C1A17]">{theirValue}</p>
                </div>
                <div className={`rounded-lg p-3 text-[11px] font-[600] ${choice === 'yours' ? 'bg-blue-50 border border-blue-200' : choice === 'custom' ? 'bg-purple-50 border border-purple-200' : 'bg-[#FAFAF8] border border-[#ECEAE4]'}`}>
                  <div className={labelClass}>{choice === 'custom' ? t('Custom Terms') : t('Your Terms (TX Template)')}</div>
                  {choice === 'custom' ? (
                    <textarea rows={4} value={customValues[key] || ''} onChange={e => setCustomValues(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full bg-transparent text-[11px] font-[600] text-[#1C1A17] resize-y focus:outline-none" placeholder={t('Enter custom terms...')} />
                  ) : (
                    <textarea rows={4} value={yourValues[key] || ''} onChange={e => setYourValues(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full bg-transparent text-[11px] font-[600] text-[#1C1A17] resize-y focus:outline-none" />
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Action bar — a clear Close action sits beside Generate so the user can
          exit the review from the bottom (where the primary action is) without
          scrolling back to the header link. Close only discards the temporary
          review state (onClose); it never generates a contract or deletes data. */}
      {/* Coherence gate: the selected merged terms mix EN/ES — block generating
          a mixed-language final contract until one language is used. No
          translation; the builder switches term choices or edits custom text. */}
      {reviewMergedCoherence === 'mixed' && (
        <div data-testid="contract-mixed-language-block" className="mx-5 mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[11px] font-[700] text-red-700">
          {mixedLanguageBlockMessage(isEs)}
        </div>
      )}
      <div className="px-5 py-4 border-t border-[#ECEAE4] bg-[#FAFAF8] flex gap-2">
        <button onClick={onClose} disabled={creating}
          className="flex-1 border border-[#ECEAE4] bg-white rounded-full py-3 text-[12px] font-[800] text-[#9E9890] disabled:opacity-40 transition-colors">
          {isEs ? 'Cerrar revisión' : 'Close review'}
        </button>
        <button onClick={handleGenerate} disabled={creating || reviewMergedCoherence === 'mixed'}
          className="flex-[2] bg-[#06539B] hover:bg-[#14457E] text-white rounded-full py-3 text-[12px] font-[800] disabled:opacity-40 transition-colors">
          {creating ? '...' : t('Generate Final Contract')}
        </button>
      </div>
      {reviewNeedsAmount && (
        <div className="px-5 pb-3 bg-[#FAFAF8]" data-testid="contract-review-amount">
          <label className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
            {isEs ? 'La IA no encontró el monto total — escríbelo' : 'The AI did not find the total — enter it'}
          </label>
          <input type="number" step="0.01" min="0" value={amountOverride} onChange={(e) => setAmountOverride(e.target.value)}
            className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B] mt-1" />
        </div>
      )}
      {generateError && (
        <p role="alert" data-testid="contract-review-error" className="px-5 pb-3 text-[11px] font-[700] text-red-700 bg-[#FAFAF8]">{generateError}</p>
      )}
    </div>
  )
}

// ── SHORTER TX TEMPLATES FOR REVIEW ──────────────

const TX_SCOPE_TEMPLATE_SHORT = `All labor, materials, and equipment per approved plans. Comply with TX building codes and IRC 2021. Contractor obtains all inspections. Materials new, approved by Owner. Clean jobsite daily. OSHA safety standards.`

const TX_SCOPE_TEMPLATE_SHORT_ES = `Toda la mano de obra, materiales y equipo conforme a planos aprobados. Cumplir códigos de construcción de TX e IRC 2021. El contratista obtiene todas las inspecciones. Materiales nuevos, aprobados por el Propietario. Mantener el sitio limpio a diario. Estándares de seguridad OSHA.`

const TX_PAYMENT_TEMPLATE_SHORT = `Net 7 days via check/ACH. 10% retainage until final inspection + punch list. No payment for uninspected work. Final payment within 35 days per TX Property Code §28.002.`

const TX_PAYMENT_TEMPLATE_SHORT_ES = `Pago a 7 días vía cheque/ACH. Retención 10% hasta inspección final + punch list. No se paga trabajo no inspeccionado. Pago final dentro de 35 días según TX Property Code §28.002.`

const TX_PENALTIES_TEMPLATE_SHORT = `$150/day liquidated damages for late completion. Defects corrected within 5 business days of notice. Insurance lapse voids contract per TX Business & Commerce Code.`

const TX_PENALTIES_TEMPLATE_SHORT_ES = `Daños liquidados de $150/día por retraso en la terminación. Defectos corregidos dentro de 5 días hábiles tras aviso. La falta de seguro anula el contrato según TX Business & Commerce Code.`

// ── NEW CONTRACT FORM ─────────────────────────────

const TX_SCOPE_TEMPLATE = `1. Furnish all labor, materials, and equipment for [TRADE] work per approved plans and specifications.
2. Work shall comply with all applicable Texas building codes, City of [CITY] amendments, and IRC 2021.
3. Contractor responsible for obtaining all required inspections and passing same.
4. All materials shall be new, of good quality, and approved by Owner before installation.
5. Work area to be left clean and broom-swept at end of each workday.
6. Contractor shall maintain jobsite safety per OSHA standards.`

const TX_SCOPE_TEMPLATE_ES = `1. Suministrar toda la mano de obra, materiales y equipo para el trabajo de [OFICIO] según planos y especificaciones aprobadas.
2. El trabajo debe cumplir con todos los códigos de construcción aplicables de Texas, enmiendas de la Ciudad de [CIUDAD] e IRC 2021.
3. El contratista es responsable de obtener y aprobar todas las inspecciones requeridas.
4. Todos los materiales serán nuevos, de buena calidad y aprobados por el Propietario antes de su instalación.
5. El área de trabajo debe quedar limpia y barrida al final de cada jornada.
6. El contratista mantendrá la seguridad en obra según los estándares OSHA.`

const TX_PAYMENT_TEMPLATE = `Payment due within 7 days of invoice via check or ACH transfer.
Retainage: 10% held until final inspection and punch list completion.
No payment shall be made for work not inspected and approved.
Per Texas Property Code §28.002, final payment due within 35 days of completion.`

const TX_PAYMENT_TEMPLATE_ES = `Pago dentro de 7 días de la factura vía cheque o transferencia ACH.
Retención: 10% hasta la inspección final y terminación del punch list.
No se pagará ningún trabajo que no haya sido inspeccionado y aprobado.
Según Texas Property Code §28.002, el pago final vence dentro de 35 días tras la terminación.`

const TX_PENALTIES_TEMPLATE = `Liquidated damages: $150/day for delays beyond agreed completion date.
Defective work must be corrected within 5 business days of written notice.
Failure to maintain insurance voids this contract per Texas Business & Commerce Code.
Warranty: 1 year on labor from date of substantial completion.`

const TX_PENALTIES_TEMPLATE_ES = `Daños liquidados: $150/día por retrasos más allá de la fecha de terminación acordada.
El trabajo defectuoso debe corregirse dentro de 5 días hábiles tras aviso por escrito.
La falta de mantenimiento del seguro anula este contrato según Texas Business & Commerce Code.
Garantía: 1 año sobre mano de obra desde la fecha de terminación sustancial.`

function NewContractForm({ projectId, editingContract, onClose, onCreated }: {
  projectId: string; editingContract?: Contract | null; onClose: () => void; onCreated: () => void
}) {
  const { getToken } = useAuth()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const isEditing = !!editingContract
  // Final contract (document) language — SEPARATE from the app UI language.
  // Final contract (document) language — kept SEPARATE from the app UI language.
  //   • New draft  → seed from the app language as a convenient starting point;
  //                  the user can override it with the selector below.
  //   • Edit/reopen → HYDRATE from the SAVED contract's clause text, never from
  //                  the app UI language. There is no Contract.language column,
  //                  so the saved clauses ARE the source of truth (the same
  //                  detection the PDF/email/sign already use). Create is blocked
  //                  whenever the clause language and the selected final language
  //                  disagree, so at save time clause-language === selected final
  //                  language — detecting it here recovers the exact selection.
  //                  This fixes the bug where reopening in a Spanish UI flipped
  //                  an English contract's final language back to Spanish.
  const [finalLang, setFinalLang] = useState<'en' | 'es'>(
    editingContract
      ? detectClauseLanguage(
          (editingContract as any).scopeOfWork,
          (editingContract as any).paymentTerms,
          (editingContract as any).penalties,
        )
      : (isEs ? 'es' : 'en'),
  )
  // Non-clause fields only. The long default clause text lives in `clauses`
  // below so a language toggle can refresh untouched defaults.
  const [form, setForm] = useState(() => editingContract ? {
    contractorName: editingContract.contractorName,
    trade: editingContract.trade,
    totalAmount: String(editingContract.totalAmount),
    timelineDays: (editingContract as any).timelineDays ? String((editingContract as any).timelineDays) : '',
    startDate: (editingContract as any).startDate ? (editingContract as any).startDate.slice(0, 10) : '',
  } : {
    contractorName: '', trade: '', totalAmount: '', timelineDays: '', startDate: '',
  })

  // Default clause text. For a NEW draft this follows the current app language;
  // for an existing draft it is the saved text (never language-refreshed). The
  // previous code baked the language into the initial useState, so switching
  // app language updated the t() labels but left the long clause textareas in
  // the original language. We now compute defaults via useMemo keyed on isEs and
  // keep user edits in a separate overrides map — switching language refreshes
  // ONLY clauses the user has not edited; user-edited text is never overwritten.
  type ClauseKey = 'scopeOfWork' | 'paymentTerms' | 'penalties'
  const defaultClauses = useMemo<Record<ClauseKey, string>>(() => editingContract ? {
    scopeOfWork: (editingContract as any).scopeOfWork || '',
    paymentTerms: (editingContract as any).paymentTerms || '',
    penalties: (editingContract as any).penalties || '',
  } : (finalLang === 'es' ? {
    scopeOfWork: TX_SCOPE_TEMPLATE_ES,
    paymentTerms: TX_PAYMENT_TEMPLATE_ES,
    penalties: TX_PENALTIES_TEMPLATE_ES,
  } : {
    scopeOfWork: TX_SCOPE_TEMPLATE,
    paymentTerms: TX_PAYMENT_TEMPLATE,
    penalties: TX_PENALTIES_TEMPLATE,
  }), [editingContract, finalLang])
  const [clauseOverrides, setClauseOverrides] = useState<Partial<Record<ClauseKey, string>>>({})
  const clauses = useMemo<Record<ClauseKey, string>>(
    () => ({ ...defaultClauses, ...clauseOverrides }),
    [defaultClauses, clauseOverrides],
  )
  const setClause = (key: ClauseKey, value: string) => {
    // Record an override only while the text differs from the current-language
    // default. If the user clears it back to the default, drop the override so
    // language refresh resumes — same intent as the review flow's overrides.
    setClauseOverrides(prev => {
      const next = { ...prev }
      if (value !== defaultClauses[key]) next[key] = value
      else delete next[key]
      return next
    })
  }

  // A user-edited clause whose detected language differs from the chosen final
  // contract language → surface a non-blocking warning (never auto-translate /
  // never silently produce a mixed/incoherent final contract). Block CREATE when
  // the effective clause bodies are mixed EN/ES, OR when their single language
  // does not match the selected final language. 'empty' (no clause text yet) is
  // not blocked. The block is enforced on the submit button below; user edits
  // are preserved — the builder resolves it by editing clauses or switching the
  // final language. No auto-translation.
  const formClauseCoherence = contractClauseCoherence(clauses.scopeOfWork, clauses.paymentTerms, clauses.penalties)
  const finalizationBlocked = formClauseCoherence === 'mixed'
    || (formClauseCoherence !== 'empty' && formClauseCoherence !== finalLang)

  const inputClass = "w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
  const labelClass = "text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]"

  const mutation = useMutation({
    mutationFn: async () => {
      // Hard finalization gate (defense-in-depth): never POST a mixed/incoherent
      // contract, even if the Create button's disabled attribute is bypassed.
      // The bilingual block notice above the form stays visible while this holds.
      if (finalizationBlocked) throw new Error('mixed-language contract blocked from creation')
      const token = await getToken()
      const url = isEditing
        ? `/api/projects/${projectId}/contracts/${editingContract!.id}`
        : `/api/projects/${projectId}/contracts`
      const res = await apiFetch(url, {
        method: isEditing ? 'PATCH' : 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contractorName: form.contractorName, trade: form.trade,
          totalAmount: Number(form.totalAmount),
          scopeOfWork: clauses.scopeOfWork || undefined,
          paymentTerms: clauses.paymentTerms || undefined,
          timelineDays: form.timelineDays ? Number(form.timelineDays) : undefined,
          startDate: form.startDate || undefined,
          penalties: clauses.penalties || undefined,
        })
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || err.message || `Request failed (${res.status})`)
      }
      return res.json()
    },
    onSuccess: onCreated,
    onError: (error: Error) => { console.error('Contract save failed:', error.message) }
  })

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3 max-h-[80vh] overflow-y-auto">
      <h3 className="text-[13px] font-[800]">{isEditing ? t('Edit Draft') : t('New Contract')}</h3>

      {/* Final contract (document) language — separate from the app UI language.
          Drives the default-clause language and the mixed-language warning. */}
      <div>
        <label className={labelClass}>{isEs ? 'Idioma final del contrato' : 'Final contract language'}</label>
        <div className="flex gap-1.5 mt-1">
          {(['en', 'es'] as const).map(lng => (
            <button key={lng} type="button" onClick={() => setFinalLang(lng)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-[800] transition-colors ${
                finalLang === lng ? 'bg-[#06539B] text-white' : 'bg-[#F2F0EB] text-[#9E9890] hover:bg-[#ECEAE4]'
              }`}>
              {lng === 'en' ? 'English' : 'Español'}
            </button>
          ))}
        </div>
        <p className="text-[9px] font-[600] text-[#9E9890] mt-1">
          {isEs
            ? 'Puedes usar la app en español y enviar el contrato final en inglés (o viceversa).'
            : 'You can use the app in Spanish and still send the final contract in English (or vice-versa).'}
        </p>
        {/* Visible confirmation of WHICH language the final contract will be
            generated in — driven by finalLang, rendered in the app UI language,
            so the builder always sees the document language explicitly. */}
        <p data-testid="contract-final-language-state" className="text-[10px] font-[800] text-[#06539B] mt-1">
          {finalLang === 'en'
            ? (isEs ? 'El contrato final se generará en inglés.' : 'Final contract will be generated in English.')
            : (isEs ? 'El contrato final se generará en español.' : 'Final contract will be generated in Spanish.')}
        </p>
      </div>

      {/* BLOCKING mixed/incoherent-language notice: the clause bodies are mixed,
          or their single language doesn't match the selected final language.
          Create is disabled until resolved. No auto-translation; the builder
          edits the clauses or switches the final language. */}
      {finalizationBlocked && (
        <div data-testid="contract-mixed-language-block" className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[11px] font-[700] text-red-700">
          {formClauseCoherence === 'mixed'
            ? mixedLanguageBlockMessage(isEs)
            : finalLang === 'en'
              ? (isEs
                  ? 'El idioma final del contrato es inglés, pero algunas cláusulas parecen estar en español. Traduce o revisa las cláusulas antes de crear el contrato.'
                  : 'The final contract language is English, but some clauses appear to be Spanish. Please translate or review the clauses before creating the contract.')
              : (isEs
                  ? 'El idioma final del contrato es español, pero algunas cláusulas parecen estar en inglés. Traduce o revisa las cláusulas antes de crear el contrato.'
                  : 'The final contract language is Spanish, but some clauses appear to be English. Please translate or review the clauses before creating the contract.')}
        </div>
      )}

      <input placeholder={t('Contractor name')} value={form.contractorName} onChange={e => setForm({ ...form, contractorName: e.target.value })}
        className={inputClass} />
      <div className="grid grid-cols-2 gap-2">
        <input placeholder={t('Trade')} value={form.trade} onChange={e => setForm({ ...form, trade: e.target.value })}
          className={inputClass} />
        <input placeholder={t('Total amount')} type="number" value={form.totalAmount} onChange={e => setForm({ ...form, totalAmount: e.target.value })}
          className={inputClass} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelClass}>{t('Timeline (days)')}</label>
          <input placeholder="45" type="number" value={form.timelineDays} onChange={e => setForm({ ...form, timelineDays: e.target.value })}
            className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>{t('Start date')}</label>
          <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })}
            className={inputClass} />
        </div>
      </div>
      <div>
        <label className={labelClass}>{t('Scope of Work')}</label>
        <textarea rows={5} value={clauses.scopeOfWork} onChange={e => setClause('scopeOfWork', e.target.value)}
          className={`${inputClass} resize-y`} />
      </div>
      <div>
        <label className={labelClass}>{t('Payment Terms')}</label>
        <textarea rows={3} value={clauses.paymentTerms} onChange={e => setClause('paymentTerms', e.target.value)}
          className={`${inputClass} resize-y`} />
      </div>
      <div>
        <label className={labelClass}>{t('Penalties & Warranty')}</label>
        <textarea rows={3} value={clauses.penalties} onChange={e => setClause('penalties', e.target.value)}
          className={`${inputClass} resize-y`} />
      </div>
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#9E9890]">{t('Cancel')}</button>
        <button onClick={() => { if (finalizationBlocked) return; mutation.mutate() }} disabled={!form.contractorName || !form.totalAmount || mutation.isPending || finalizationBlocked}
          className="flex-1 bg-[#06539B] text-white rounded-full py-2 text-[11px] font-[800] disabled:opacity-40">
          {mutation.isPending ? '...' : isEditing ? t('Save') : t('Create')}
        </button>
      </div>
      {/* PR3 (C6): the server requires contractor, trade and an amount > 0.
          Say so up front, and never fail silently (it only logged to console). */}
      {(!form.trade.trim() || !(Number(form.totalAmount) > 0)) && (
        <p className="text-[10px] font-[600] text-[#9E9890]" data-testid="contract-required-hint">
          {isEs ? 'Contratista, oficio y un monto mayor a 0 son obligatorios.' : 'Contractor, trade and an amount above 0 are required.'}
        </p>
      )}
      {mutation.isError && (
        <p role="alert" data-testid="contract-save-error" className="text-[11px] font-[700] text-red-700">
          {isEs ? 'No se pudo guardar el contrato. Revisa los datos e intenta de nuevo.' : 'Could not save the contract. Check the fields and try again.'}
        </p>
      )}
    </div>
  )
}
